import axios from 'axios';

const productIdRegex = /^[0-9]*$/;
const apiBaseUrl = 'https://api.okendo.io/v1';
let subscriberId = undefined;
const aggregateCache = {};
var OkendoAPI = {
  get storeBaseUrl() {
    if (subscriberId) {
      return `${apiBaseUrl}/stores/${subscriberId}`;
    } else {
      console.error("Okendo subscriberId not defined: Please set your Okendo subscriberId by calling configureOkendo({ subscriberId: 'your-subscriber-id-here' })");
    }
  },

  transformProductId(productId) {
    if (!productId) {
      return;
    }

    if (productId.startsWith('shopify-')) {
      return productId;
    } else {
      // Supports Shopify's GraphQL Id syntax e.g. gid://shopify/Product/10079785100
      return `shopify-${productIdRegex.test(productId) ? productId : atob(productId).split('/').slice(-1)[0]}`;
    }
  },

  filtersToQueryString(filters) {
    if (filters) {
      return filters.map(filter => {
        const prefix = filter.target === 'shopper' ? 'shopperAttributes' : 'generalAttributes';
        return `${prefix}=${filter.title},${filter.options.join(',')}`;
      }).join('&');
    }
  },

  setSubscriberId(newSubscriberId) {
    subscriberId = newSubscriberId;
  },

  apiBaseUrl() {
    return apiBaseUrl;
  },

  subscriberId() {
    return subscriberId;
  },

  writeReviewUrl(productId, enhancedRecorder) {
    return enhancedRecorder ? `https://okendo.reviews/?subscriberId=${subscriberId}&productId=${productId}` : `https://reviews.okendo.io/stores/${subscriberId}/review?productId=${productId}`;
  },

  askQuestionUrl(productId) {
    return `https://reviews.okendo.io/stores/${subscriberId}/question?productId=${productId}`;
  },

  getMore(nextUrl) {
    return axios.get(`${apiBaseUrl}${nextUrl}`);
  },

  postHelpfulVoteReview(reviewId, voteType) {
    return axios.post(`${this.storeBaseUrl}/reviews/${reviewId}/vote`, {
      voteType
    });
  },

  getFiltersProduct(productId) {
    return axios.get(`${this.storeBaseUrl}/products/${productId}/attributes`);
  },

  getReview(reviewId) {
    return axios.get(`${this.storeBaseUrl}/reviews/${reviewId}`);
  },

  getReviewsProduct(productId, limit = 5, orderBy = 'date desc', filters) {
    return axios.get(`${this.storeBaseUrl}/products/${productId}/reviews?limit=${limit}&orderBy=${orderBy}${filters ? '&' + this.filtersToQueryString(filters) : ''}`);
  },

  getReviewsAll(limit = 5, orderBy = 'date desc') {
    return axios.get(`${this.storeBaseUrl}/reviews?limit=${limit}&orderBy=${orderBy}`);
  },

  getReviewAggregateProduct(productId) {
    return aggregateCache[productId] || (aggregateCache[productId] = axios.get(`${this.storeBaseUrl}/products/${productId}/review_aggregate`));
  },

  getReviewAggregateAll() {
    return axios.get(`${this.storeBaseUrl}/review_aggregate`);
  },

  getMediaProduct(productId, limit = 6, orderBy = 'date desc') {
    return axios.get(`${this.storeBaseUrl}/products/${productId}/review_media?limit=${limit}&orderBy=${orderBy}`);
  },

  getMediaAll(limit = 6, orderBy = 'date desc') {
    return axios.get(`${this.storeBaseUrl}/review_media?limit=${limit}&orderBy=${orderBy}`);
  },

  getQuestionsProduct(productId, limit = 5, orderBy = 'date desc') {
    return axios.get(`${this.storeBaseUrl}/products/${productId}/questions?limit=${limit}&orderBy=${orderBy}`);
  },

  getQuestionAggregateProduct(productId) {
    return axios.get(`${this.storeBaseUrl}/products/${productId}/question_aggregate`);
  },

  postHelpfulVoteQuestion(questionId, voteType) {
    return axios.post(`${this.storeBaseUrl}/questions/${questionId}/vote`, {
      voteType
    });
  },

  postHelpfulVoteAnswer(questionId, answerId, voteType) {
    return axios.post(`${this.storeBaseUrl}/questions/${questionId}/answers/${answerId}/vote`, {
      voteType
    });
  }

};

//
//
//
//
//
//
//
//
//
//
var script = {
  props: {
    rating: {
      required: true
    },
    accessibleText: {
      type: Boolean,
      default: true,
      required: false
    },
    size: {
      type: String,
      default: 'standard',
      required: false
    }
  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

/* script */
const __vue_script__ = script;

/* template */
var __vue_render__ = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('span',{staticClass:"okeReviews-starRating",class:{ 'okeReviews-starRating--small': _vm.size === 'small' }},[(_vm.accessibleText)?_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("Rated "+_vm._s(_vm.rating)+" out of 5")]):_vm._e(),_vm._v(" "),_c('span',{staticClass:"okeReviews-starRating-indicator",attrs:{"role":"presentation"}},[_c('span',{staticClass:"okeReviews-starRating-indicator-layer"}),_vm._v(" "),_c('span',{staticClass:"okeReviews-starRating-indicator-layer okeReviews-starRating-indicator-layer--foreground",style:({ width: _vm.rating * 20 + '%' })})])])};
var __vue_staticRenderFns__ = [];

  /* style */
  const __vue_inject_styles__ = undefined;
  /* scoped */
  const __vue_scope_id__ = undefined;
  /* module identifier */
  const __vue_module_identifier__ = undefined;
  /* functional template */
  const __vue_is_functional_template__ = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoStars = normalizeComponent(
    { render: __vue_render__, staticRenderFns: __vue_staticRenderFns__ },
    __vue_inject_styles__,
    __vue_script__,
    __vue_scope_id__,
    __vue_is_functional_template__,
    __vue_module_identifier__,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$1 = {
  props: {
    productId: {
      type: String,
      default: '',
      required: true
    },
    // Hide stars when there are no reviews for the product
    hideIfEmpty: {
      type: Boolean,
      default: true,
      required: false
    },
    // Id of element to scroll to when stars are clicked (defaults to reviews widget)
    scrollTargetId: {
      type: String,
      required: false
    }
  },
  components: {
    OkendoStars
  },

  data() {
    return {
      ratingAndReviewCount: 0,
      averageRating: '0',
      isLoading: true,
      isError: false,
      scrollTarget: undefined
    };
  },

  mounted() {
    const productId = OkendoAPI.transformProductId(this.productId);
    this.scrollTarget = this.scrollTargetId ? document.getElementById(this.scrollTargetId) : document.getElementsByClassName('js-okeReviews-reviewsWidget')[0];

    if (productId) {
      OkendoAPI.getReviewAggregateProduct(productId).then(response => {
        if (response && response.data && response.data.reviewAggregate) {
          const reviewAggregate = response.data.reviewAggregate;
          this.ratingAndReviewCount = reviewAggregate.ratingAndReviewCount;
          this.averageRating = reviewAggregate.ratingAndReviewCount ? (reviewAggregate.ratingAndReviewValuesTotal / reviewAggregate.ratingAndReviewCount).toFixed(1) : '0';
          this.isLoading = false;
        } else {
          this.isError = true;
        }
      }).catch(() => this.isError = true);
    }
  },

  methods: {
    scrollToReviews() {
      if (this.scrollTarget) {
        this.scrollTarget.scrollIntoView({
          behavior: 'smooth'
        });
      }
    }

  }
};

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$1 = script$1;

/* template */
var __vue_render__$1 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (!_vm.isError && (_vm.isLoading || _vm.ratingAndReviewCount || !_vm.hideIfEmpty))?_c('div',{staticClass:"okeReviews okeReviews--theme",on:{"click":function($event){return _vm.scrollToReviews()}}},[_c('div',{staticClass:"okeReviews-reviewsSummary",class:{
            'is-okeReviews-empty': _vm.ratingAndReviewCount === 0,
            'is-okeReviews-loading': _vm.isLoading,
            'is-okeReviews-clickable': _vm.scrollTarget
        }},[_c('div',{staticClass:"okeReviews-reviewsSummary-starRating"},[_c('okendo-stars',{attrs:{"rating":_vm.averageRating,"size":"small"}})],1),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewsSummary-ratingCount"},[_vm._v("\n            "+_vm._s(_vm.ratingAndReviewCount || '0')+" "+_vm._s(_vm.ratingAndReviewCount === 1 ? 'Review' : 'Reviews')+"\n        ")])])]):_vm._e()};
var __vue_staticRenderFns__$1 = [];

  /* style */
  const __vue_inject_styles__$1 = function (inject) {
    if (!inject) return
    inject("data-v-8421b528_0", { source: ".okeReviews .okeReviews-reviewsSummary-ratingCount,.okeReviews .okeReviews-starRating-indicator-layer--foreground{transition:opacity .5s ease}.okeReviews .okeReviews-reviewsSummary.is-okeReviews-loading{position:relative}.okeReviews .okeReviews-reviewsSummary.is-okeReviews-loading .okeReviews-reviewsSummary-ratingCount,.okeReviews .okeReviews-reviewsSummary.is-okeReviews-loading .okeReviews-starRating-indicator-layer--foreground{opacity:0}.okeReviews .okeReviews-reviewsSummary.is-okeReviews-loading .okeReviews-starRating-indicator::before{animation:loading 1.2s linear infinite;background:linear-gradient(to right,rgba(255,255,255,0) 0,rgba(255,255,255,.6) 100%);background-size:200% 100%;content:\"\";display:block;height:100%;position:absolute;width:100%;z-index:1}@keyframes loading{0%{background-position:200%}100%{background-position:0}}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$1 = undefined;
  /* module identifier */
  const __vue_module_identifier__$1 = undefined;
  /* functional template */
  const __vue_is_functional_template__$1 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoStarRating = normalizeComponent(
    { render: __vue_render__$1, staticRenderFns: __vue_staticRenderFns__$1 },
    __vue_inject_styles__$1,
    __vue_script__$1,
    __vue_scope_id__$1,
    __vue_is_functional_template__$1,
    __vue_module_identifier__$1,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$2 = {
  props: {
    attributes: {
      type: Array,
      required: true
    }
  },
  methods: {
    isListAttribute(attribute) {
      return Array.isArray(attribute.value);
    }

  }
};

/* script */
const __vue_script__$2 = script$2;

/* template */
var __vue_render__$2 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.attributes)?_c('div',{staticClass:"okeReviews-review-reviewer-attributes"},_vm._l((_vm.attributes),function(attribute){return _c('div',{key:attribute.title,staticClass:"okeReviews-review-reviewer-attributes-item"},[(attribute.value && _vm.isListAttribute(attribute))?_c('div',{staticClass:"okeReviews-sectionedList"},[_c('div',{staticClass:"okeReviews-sectionedList-heading"},[_vm._v(_vm._s(attribute.title))]),_vm._v(" "),_c('ul',{staticClass:"okeReviews-sectionedList-list"},_vm._l((attribute.value),function(value){return _c('li',{key:value,staticClass:"okeReviews-sectionedList-list-item"},[_vm._v(_vm._s(value))])}),0)]):_vm._e(),_vm._v(" "),(attribute.value && !_vm.isListAttribute(attribute))?_c('div',{staticClass:"okeReviews-review-attribute"},[_c('div',{staticClass:"okeReviews-review-attribute-label"},[_vm._v(_vm._s(attribute.title))]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-attribute-value"},[_vm._v(_vm._s(attribute.value))])]):_vm._e()])}),0):_vm._e()};
var __vue_staticRenderFns__$2 = [];

  /* style */
  const __vue_inject_styles__$2 = undefined;
  /* scoped */
  const __vue_scope_id__$2 = undefined;
  /* module identifier */
  const __vue_module_identifier__$2 = undefined;
  /* functional template */
  const __vue_is_functional_template__$2 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoCustomerAttributes = normalizeComponent(
    { render: __vue_render__$2, staticRenderFns: __vue_staticRenderFns__$2 },
    __vue_inject_styles__$2,
    __vue_script__$2,
    __vue_scope_id__$2,
    __vue_is_functional_template__$2,
    __vue_module_identifier__$2,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$3 = {
  props: {
    attributes: {
      type: Array,
      required: true
    }
  },
  methods: {
    isListAttribute(attribute) {
      return Array.isArray(attribute.value);
    }

  }
};

/* script */
const __vue_script__$3 = script$3;

/* template */
var __vue_render__$3 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.attributes)?_c('div',{staticClass:"okeReviews-review-arguments"},_vm._l((_vm.attributes),function(attribute){return _c('div',{key:attribute.title,staticClass:"okeReviews-review-arguments-item"},[(attribute.value && _vm.isListAttribute(attribute))?_c('div',{staticClass:"okeReviews-sectionedList"},[_c('div',{staticClass:"okeReviews-sectionedList-heading"},[_vm._v(_vm._s(attribute.title))]),_vm._v(" "),_c('ul',{staticClass:"okeReviews-sectionedList-list"},_vm._l((attribute.value),function(value){return _c('li',{key:value,staticClass:"okeReviews-sectionedList-list-item"},[_vm._v(_vm._s(value))])}),0)]):_vm._e(),_vm._v(" "),(attribute.value && !_vm.isListAttribute(attribute))?_c('div',{staticClass:"okeReviews-review-attribute"},[_c('div',{staticClass:"okeReviews-review-attribute-label"},[_vm._v(_vm._s(attribute.title))]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-attribute-value"},[_vm._v(_vm._s(attribute.value))])]):_vm._e()])}),0):_vm._e()};
var __vue_staticRenderFns__$3 = [];

  /* style */
  const __vue_inject_styles__$3 = undefined;
  /* scoped */
  const __vue_scope_id__$3 = undefined;
  /* module identifier */
  const __vue_module_identifier__$3 = undefined;
  /* functional template */
  const __vue_is_functional_template__$3 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoProductAttributes = normalizeComponent(
    { render: __vue_render__$3, staticRenderFns: __vue_staticRenderFns__$3 },
    __vue_inject_styles__$3,
    __vue_script__$3,
    __vue_scope_id__$3,
    __vue_is_functional_template__$3,
    __vue_module_identifier__$3,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$4 = {
  props: {
    reviewer: {
      type: Object,
      required: true
    }
  },
  computed: {
    reviewerInitials() {
      return (this.reviewer.displayName || '').split(' ').slice(0, 2).map(part => part.substr(0, 1).toLocaleUpperCase()).join('');
    },

    reviewerLocation() {
      if (this.reviewer.location && this.reviewer.location.zoneCode) {
        return `${this.reviewer.location.zoneCode}, ${this.reviewer.location.country.name}`;
      } else if (this.reviewer.country) {
        return this.reviewer.country.name;
      }
    }

  }
};

/* script */
const __vue_script__$4 = script$4;

/* template */
var __vue_render__$4 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-review-reviewer-profile"},[_c('div',{staticClass:"okeReviews-review-reviewer-profile-avatar"},[_c('div',{staticClass:"okeReviews-avatar okeReviews-avatar--reviewer",class:_vm.reviewer.socialConnection ? ("okeReviews-avatar--" + (_vm.reviewer.socialConnection)) : ''},[_c('div',{staticClass:"okeReviews-avatar-placeholder",attrs:{"aria-hidden":"true"}},[_vm._v("\n                "+_vm._s(_vm.reviewerInitials)+"\n            ")]),_vm._v(" "),(_vm.reviewer.avatarUrl)?_c('img',{staticClass:"okeReviews-avatar-image",attrs:{"src":_vm.reviewer.avatarUrl,"alt":""}}):_vm._e()])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-reviewer-profile-details"},[_c('div',{staticClass:"okeReviews-review-reviewer-profile-details-inner"},[_c('span',{staticClass:"okeReviews-review-reviewer-profile-name",attrs:{"aria-hidden":"true"}},[_vm._v("\n                "+_vm._s(_vm.reviewer.displayName)+"\n            ")]),_vm._v(" "),(_vm.reviewer.country)?[(_vm.reviewer.location && _vm.reviewer.location.zoneCode)?_c('span',{staticClass:"okeReviews-review-reviewer-profile-country-zone",attrs:{"aria-hidden":"true"}},[_vm._v("\n                    "+_vm._s(_vm.reviewerLocation)+"\n                ")]):_c('span',{staticClass:"okeReviews-review-reviewer-profile-country okeReviews-flag",class:("okeReviews-flag--" + (_vm.reviewer.country.code.toLowerCase())),attrs:{"title":_vm.reviewerLocation,"aria-hidden":"true"}}),_vm._v(" "),_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("\n                    "+_vm._s(("Reviewed by " + (_vm.reviewer.displayName) + ", from " + _vm.reviewerLocation))+"\n                ")])]:[_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v(_vm._s(("Reviewed by " + (_vm.reviewer.displayName))))])]],2),_vm._v(" "),(_vm.reviewer.isVerified)?_c('div',{staticClass:"okeReviews-review-reviewer-profile-status okeReviews-review-reviewer-profile-status--verified"},[_vm._v("\n            Verified Buyer\n        ")]):_vm._e()])])};
var __vue_staticRenderFns__$4 = [];

  /* style */
  const __vue_inject_styles__$4 = function (inject) {
    if (!inject) return
    inject("data-v-279f07f8_0", { source: ".okeReviews .okeReviews-review-reviewer-profile-country-zone[data-v-279f07f8]{margin-bottom:0}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$4 = "data-v-279f07f8";
  /* module identifier */
  const __vue_module_identifier__$4 = undefined;
  /* functional template */
  const __vue_is_functional_template__$4 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoReviewerProfile = normalizeComponent(
    { render: __vue_render__$4, staticRenderFns: __vue_staticRenderFns__$4 },
    __vue_inject_styles__$4,
    __vue_script__$4,
    __vue_scope_id__$4,
    __vue_is_functional_template__$4,
    __vue_module_identifier__$4,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$5 = {
  props: {
    avatarUrl: {
      type: String,
      required: false
    },
    displayName: {
      type: String,
      required: true
    },
    width: {
      type: String,
      required: false,
      default: '40px'
    }
  },
  computed: {
    initials() {
      return (this.displayName || '').split(' ').slice(0, 2).map(part => part.substr(0, 1).toLocaleUpperCase()).join('');
    }

  }
};

/* script */
const __vue_script__$5 = script$5;

/* template */
var __vue_render__$5 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-avatar",style:({ width: _vm.width, height: _vm.width, 'line-height': _vm.width })},[_c('span',{staticClass:"oke-avatar-placeholder"},[_vm._v("\n        "+_vm._s(_vm.initials)+"\n    ")]),_vm._v(" "),(_vm.avatarUrl)?_c('img',{staticClass:"oke-avatar-image",attrs:{"src":_vm.avatarUrl,"alt":""}}):_vm._e()])};
var __vue_staticRenderFns__$5 = [];

  /* style */
  const __vue_inject_styles__$5 = function (inject) {
    if (!inject) return
    inject("data-v-6e285aa8_0", { source: ".oke-avatar{background-color:#eee;width:32px;height:32px;margin-right:16px;line-height:32px;border-radius:50%;overflow:hidden;position:relative}.oke-avatar-image{display:block;position:absolute;width:100%;height:100%}.oke-avatar-placeholder{font-size:12px;font-weight:600;user-select:none;width:100%;left:0;position:absolute;text-align:center}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$5 = undefined;
  /* module identifier */
  const __vue_module_identifier__$5 = undefined;
  /* functional template */
  const __vue_is_functional_template__$5 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var Avatar = normalizeComponent(
    { render: __vue_render__$5, staticRenderFns: __vue_staticRenderFns__$5 },
    __vue_inject_styles__$5,
    __vue_script__$5,
    __vue_scope_id__$5,
    __vue_is_functional_template__$5,
    __vue_module_identifier__$5,
    false,
    createInjector,
    undefined,
    undefined
  );

function toInteger(dirtyNumber) {
  if (dirtyNumber === null || dirtyNumber === true || dirtyNumber === false) {
    return NaN;
  }

  var number = Number(dirtyNumber);

  if (isNaN(number)) {
    return number;
  }

  return number < 0 ? Math.ceil(number) : Math.floor(number);
}

function requiredArgs(required, args) {
  if (args.length < required) {
    throw new TypeError(required + ' argument' + (required > 1 ? 's' : '') + ' required, but only ' + args.length + ' present');
  }
}

/**
 * @name toDate
 * @category Common Helpers
 * @summary Convert the given argument to an instance of Date.
 *
 * @description
 * Convert the given argument to an instance of Date.
 *
 * If the argument is an instance of Date, the function returns its clone.
 *
 * If the argument is a number, it is treated as a timestamp.
 *
 * If the argument is none of the above, the function returns Invalid Date.
 *
 * **Note**: *all* Date arguments passed to any *date-fns* function is processed by `toDate`.
 *
 * @param {Date|Number} argument - the value to convert
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Clone the date:
 * const result = toDate(new Date(2014, 1, 11, 11, 30, 30))
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert the timestamp to date:
 * const result = toDate(1392098430000)
 * //=> Tue Feb 11 2014 11:30:30
 */

function toDate(argument) {
  requiredArgs(1, arguments);
  var argStr = Object.prototype.toString.call(argument); // Clone the date

  if (argument instanceof Date || typeof argument === 'object' && argStr === '[object Date]') {
    // Prevent the date to lose the milliseconds when passed to new Date() in IE10
    return new Date(argument.getTime());
  } else if (typeof argument === 'number' || argStr === '[object Number]') {
    return new Date(argument);
  } else {
    if ((typeof argument === 'string' || argStr === '[object String]') && typeof console !== 'undefined') {
      // eslint-disable-next-line no-console
      console.warn("Starting with v2.0.0-beta.1 date-fns doesn't accept strings as date arguments. Please use `parseISO` to parse strings. See: https://git.io/fjule"); // eslint-disable-next-line no-console

      console.warn(new Error().stack);
    }

    return new Date(NaN);
  }
}

/**
 * @name addMilliseconds
 * @category Millisecond Helpers
 * @summary Add the specified number of milliseconds to the given date.
 *
 * @description
 * Add the specified number of milliseconds to the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be added. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds added
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Add 750 milliseconds to 10 July 2014 12:45:30.000:
 * const result = addMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:30.750
 */

function addMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var timestamp = toDate(dirtyDate).getTime();
  var amount = toInteger(dirtyAmount);
  return new Date(timestamp + amount);
}

/**
 * Google Chrome as of 67.0.3396.87 introduced timezones with offset that includes seconds.
 * They usually appear for dates that denote time before the timezones were introduced
 * (e.g. for 'Europe/Prague' timezone the offset is GMT+00:57:44 before 1 October 1891
 * and GMT+01:00:00 after that date)
 *
 * Date#getTimezoneOffset returns the offset in minutes and would return 57 for the example above,
 * which would lead to incorrect calculations.
 *
 * This function returns the timezone offset in milliseconds that takes seconds in account.
 */
function getTimezoneOffsetInMilliseconds(date) {
  var utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds(), date.getMilliseconds()));
  utcDate.setUTCFullYear(date.getFullYear());
  return date.getTime() - utcDate.getTime();
}

/**
 * @name compareAsc
 * @category Common Helpers
 * @summary Compare the two dates and return -1, 0 or 1.
 *
 * @description
 * Compare the two dates and return 1 if the first date is after the second,
 * -1 if the first date is before the second or 0 if dates are equal.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the first date to compare
 * @param {Date|Number} dateRight - the second date to compare
 * @returns {Number} the result of the comparison
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Compare 11 February 1987 and 10 July 1989:
 * const result = compareAsc(new Date(1987, 1, 11), new Date(1989, 6, 10))
 * //=> -1
 *
 * @example
 * // Sort the array of dates:
 * const result = [
 *   new Date(1995, 6, 2),
 *   new Date(1987, 1, 11),
 *   new Date(1989, 6, 10)
 * ].sort(compareAsc)
 * //=> [
 * //   Wed Feb 11 1987 00:00:00,
 * //   Mon Jul 10 1989 00:00:00,
 * //   Sun Jul 02 1995 00:00:00
 * // ]
 */

function compareAsc(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var diff = dateLeft.getTime() - dateRight.getTime();

  if (diff < 0) {
    return -1;
  } else if (diff > 0) {
    return 1; // Return 0 if diff is 0; return NaN if diff is NaN
  } else {
    return diff;
  }
}

/**
 * @name isValid
 * @category Common Helpers
 * @summary Is the given date valid?
 *
 * @description
 * Returns false if argument is Invalid Date and true otherwise.
 * Argument is converted to Date using `toDate`. See [toDate]{@link https://date-fns.org/docs/toDate}
 * Invalid Date is a Date, whose time value is NaN.
 *
 * Time value of Date: http://es5.github.io/#x15.9.1.1
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - Now `isValid` doesn't throw an exception
 *   if the first argument is not an instance of Date.
 *   Instead, argument is converted beforehand using `toDate`.
 *
 *   Examples:
 *
 *   | `isValid` argument        | Before v2.0.0 | v2.0.0 onward |
 *   |---------------------------|---------------|---------------|
 *   | `new Date()`              | `true`        | `true`        |
 *   | `new Date('2016-01-01')`  | `true`        | `true`        |
 *   | `new Date('')`            | `false`       | `false`       |
 *   | `new Date(1488370835081)` | `true`        | `true`        |
 *   | `new Date(NaN)`           | `false`       | `false`       |
 *   | `'2016-01-01'`            | `TypeError`   | `false`       |
 *   | `''`                      | `TypeError`   | `false`       |
 *   | `1488370835081`           | `TypeError`   | `true`        |
 *   | `NaN`                     | `TypeError`   | `false`       |
 *
 *   We introduce this change to make *date-fns* consistent with ECMAScript behavior
 *   that try to coerce arguments to the expected type
 *   (which is also the case with other *date-fns* functions).
 *
 * @param {*} date - the date to check
 * @returns {Boolean} the date is valid
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // For the valid date:
 * var result = isValid(new Date(2014, 1, 31))
 * //=> true
 *
 * @example
 * // For the value, convertable into a date:
 * var result = isValid(1393804800000)
 * //=> true
 *
 * @example
 * // For the invalid date:
 * var result = isValid(new Date(''))
 * //=> false
 */

function isValid(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  return !isNaN(date);
}

/**
 * @name differenceInCalendarMonths
 * @category Month Helpers
 * @summary Get the number of calendar months between the given dates.
 *
 * @description
 * Get the number of calendar months between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of calendar months
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many calendar months are between 31 January 2014 and 1 September 2014?
 * var result = differenceInCalendarMonths(
 *   new Date(2014, 8, 1),
 *   new Date(2014, 0, 31)
 * )
 * //=> 8
 */

function differenceInCalendarMonths(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var yearDiff = dateLeft.getFullYear() - dateRight.getFullYear();
  var monthDiff = dateLeft.getMonth() - dateRight.getMonth();
  return yearDiff * 12 + monthDiff;
}

/**
 * @name differenceInMilliseconds
 * @category Millisecond Helpers
 * @summary Get the number of milliseconds between the given dates.
 *
 * @description
 * Get the number of milliseconds between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of milliseconds
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many milliseconds are between
 * // 2 July 2014 12:30:20.600 and 2 July 2014 12:30:21.700?
 * const result = differenceInMilliseconds(
 *   new Date(2014, 6, 2, 12, 30, 21, 700),
 *   new Date(2014, 6, 2, 12, 30, 20, 600)
 * )
 * //=> 1100
 */

function differenceInMilliseconds(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  return dateLeft.getTime() - dateRight.getTime();
}

/**
 * @name endOfDay
 * @category Day Helpers
 * @summary Return the end of a day for the given date.
 *
 * @description
 * Return the end of a day for the given date.
 * The result will be in the local timezone.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the end of a day
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The end of a day for 2 September 2014 11:55:00:
 * const result = endOfDay(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 02 2014 23:59:59.999
 */

function endOfDay(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * @name endOfMonth
 * @category Month Helpers
 * @summary Return the end of a month for the given date.
 *
 * @description
 * Return the end of a month for the given date.
 * The result will be in the local timezone.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the original date
 * @returns {Date} the end of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // The end of a month for 2 September 2014 11:55:00:
 * const result = endOfMonth(new Date(2014, 8, 2, 11, 55, 0))
 * //=> Tue Sep 30 2014 23:59:59.999
 */

function endOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var month = date.getMonth();
  date.setFullYear(date.getFullYear(), month + 1, 0);
  date.setHours(23, 59, 59, 999);
  return date;
}

/**
 * @name isLastDayOfMonth
 * @category Month Helpers
 * @summary Is the given date the last day of a month?
 *
 * @description
 * Is the given date the last day of a month?
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to check
 * @returns {Boolean} the date is the last day of a month
 * @throws {TypeError} 1 argument required
 *
 * @example
 * // Is 28 February 2014 the last day of a month?
 * var result = isLastDayOfMonth(new Date(2014, 1, 28))
 * //=> true
 */

function isLastDayOfMonth(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  return endOfDay(date).getTime() === endOfMonth(date).getTime();
}

/**
 * @name differenceInMonths
 * @category Month Helpers
 * @summary Get the number of full months between the given dates.
 *
 * @description
 * Get the number of full months between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of full months
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many full months are between 31 January 2014 and 1 September 2014?
 * var result = differenceInMonths(new Date(2014, 8, 1), new Date(2014, 0, 31))
 * //=> 7
 */

function differenceInMonths(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var dateLeft = toDate(dirtyDateLeft);
  var dateRight = toDate(dirtyDateRight);
  var sign = compareAsc(dateLeft, dateRight);
  var difference = Math.abs(differenceInCalendarMonths(dateLeft, dateRight));
  var result; // Check for the difference of less than month

  if (difference < 1) {
    result = 0;
  } else {
    if (dateLeft.getMonth() === 1 && dateLeft.getDate() > 27) {
      // This will check if the date is end of Feb and assign a higher end of month date
      // to compare it with Jan
      dateLeft.setDate(30);
    }

    dateLeft.setMonth(dateLeft.getMonth() - sign * difference); // Math.abs(diff in full months - diff in calendar months) === 1 if last calendar month is not full
    // If so, result must be decreased by 1 in absolute value

    var isLastMonthNotFull = compareAsc(dateLeft, dateRight) === -sign; // Check for cases of one full calendar month

    if (isLastDayOfMonth(toDate(dirtyDateLeft)) && difference === 1 && compareAsc(dirtyDateLeft, dateRight) === 1) {
      isLastMonthNotFull = false;
    }

    result = sign * (difference - Number(isLastMonthNotFull));
  } // Prevent negative zero


  return result === 0 ? 0 : result;
}

/**
 * @name differenceInSeconds
 * @category Second Helpers
 * @summary Get the number of seconds between the given dates.
 *
 * @description
 * Get the number of seconds between the given dates.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} dateLeft - the later date
 * @param {Date|Number} dateRight - the earlier date
 * @returns {Number} the number of seconds
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // How many seconds are between
 * // 2 July 2014 12:30:07.999 and 2 July 2014 12:30:20.000?
 * const result = differenceInSeconds(
 *   new Date(2014, 6, 2, 12, 30, 20, 0),
 *   new Date(2014, 6, 2, 12, 30, 7, 999)
 * )
 * //=> 12
 */

function differenceInSeconds(dirtyDateLeft, dirtyDateRight) {
  requiredArgs(2, arguments);
  var diff = differenceInMilliseconds(dirtyDateLeft, dirtyDateRight) / 1000;
  return diff > 0 ? Math.floor(diff) : Math.ceil(diff);
}

var formatDistanceLocale = {
  lessThanXSeconds: {
    one: 'less than a second',
    other: 'less than {{count}} seconds'
  },
  xSeconds: {
    one: '1 second',
    other: '{{count}} seconds'
  },
  halfAMinute: 'half a minute',
  lessThanXMinutes: {
    one: 'less than a minute',
    other: 'less than {{count}} minutes'
  },
  xMinutes: {
    one: '1 minute',
    other: '{{count}} minutes'
  },
  aboutXHours: {
    one: 'about 1 hour',
    other: 'about {{count}} hours'
  },
  xHours: {
    one: '1 hour',
    other: '{{count}} hours'
  },
  xDays: {
    one: '1 day',
    other: '{{count}} days'
  },
  aboutXWeeks: {
    one: 'about 1 week',
    other: 'about {{count}} weeks'
  },
  xWeeks: {
    one: '1 week',
    other: '{{count}} weeks'
  },
  aboutXMonths: {
    one: 'about 1 month',
    other: 'about {{count}} months'
  },
  xMonths: {
    one: '1 month',
    other: '{{count}} months'
  },
  aboutXYears: {
    one: 'about 1 year',
    other: 'about {{count}} years'
  },
  xYears: {
    one: '1 year',
    other: '{{count}} years'
  },
  overXYears: {
    one: 'over 1 year',
    other: 'over {{count}} years'
  },
  almostXYears: {
    one: 'almost 1 year',
    other: 'almost {{count}} years'
  }
};
function formatDistance(token, count, options) {
  options = options || {};
  var result;

  if (typeof formatDistanceLocale[token] === 'string') {
    result = formatDistanceLocale[token];
  } else if (count === 1) {
    result = formatDistanceLocale[token].one;
  } else {
    result = formatDistanceLocale[token].other.replace('{{count}}', count);
  }

  if (options.addSuffix) {
    if (options.comparison > 0) {
      return 'in ' + result;
    } else {
      return result + ' ago';
    }
  }

  return result;
}

function buildFormatLongFn(args) {
  return function (dirtyOptions) {
    var options = dirtyOptions || {};
    var width = options.width ? String(options.width) : args.defaultWidth;
    var format = args.formats[width] || args.formats[args.defaultWidth];
    return format;
  };
}

var dateFormats = {
  full: 'EEEE, MMMM do, y',
  long: 'MMMM do, y',
  medium: 'MMM d, y',
  short: 'MM/dd/yyyy'
};
var timeFormats = {
  full: 'h:mm:ss a zzzz',
  long: 'h:mm:ss a z',
  medium: 'h:mm:ss a',
  short: 'h:mm a'
};
var dateTimeFormats = {
  full: "{{date}} 'at' {{time}}",
  long: "{{date}} 'at' {{time}}",
  medium: '{{date}}, {{time}}',
  short: '{{date}}, {{time}}'
};
var formatLong = {
  date: buildFormatLongFn({
    formats: dateFormats,
    defaultWidth: 'full'
  }),
  time: buildFormatLongFn({
    formats: timeFormats,
    defaultWidth: 'full'
  }),
  dateTime: buildFormatLongFn({
    formats: dateTimeFormats,
    defaultWidth: 'full'
  })
};

var formatRelativeLocale = {
  lastWeek: "'last' eeee 'at' p",
  yesterday: "'yesterday at' p",
  today: "'today at' p",
  tomorrow: "'tomorrow at' p",
  nextWeek: "eeee 'at' p",
  other: 'P'
};
function formatRelative(token, _date, _baseDate, _options) {
  return formatRelativeLocale[token];
}

function buildLocalizeFn(args) {
  return function (dirtyIndex, dirtyOptions) {
    var options = dirtyOptions || {};
    var context = options.context ? String(options.context) : 'standalone';
    var valuesArray;

    if (context === 'formatting' && args.formattingValues) {
      var defaultWidth = args.defaultFormattingWidth || args.defaultWidth;
      var width = options.width ? String(options.width) : defaultWidth;
      valuesArray = args.formattingValues[width] || args.formattingValues[defaultWidth];
    } else {
      var _defaultWidth = args.defaultWidth;

      var _width = options.width ? String(options.width) : args.defaultWidth;

      valuesArray = args.values[_width] || args.values[_defaultWidth];
    }

    var index = args.argumentCallback ? args.argumentCallback(dirtyIndex) : dirtyIndex;
    return valuesArray[index];
  };
}

var eraValues = {
  narrow: ['B', 'A'],
  abbreviated: ['BC', 'AD'],
  wide: ['Before Christ', 'Anno Domini']
};
var quarterValues = {
  narrow: ['1', '2', '3', '4'],
  abbreviated: ['Q1', 'Q2', 'Q3', 'Q4'],
  wide: ['1st quarter', '2nd quarter', '3rd quarter', '4th quarter'] // Note: in English, the names of days of the week and months are capitalized.
  // If you are making a new locale based on this one, check if the same is true for the language you're working on.
  // Generally, formatted dates should look like they are in the middle of a sentence,
  // e.g. in Spanish language the weekdays and months should be in the lowercase.

};
var monthValues = {
  narrow: ['J', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'],
  abbreviated: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
  wide: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']
};
var dayValues = {
  narrow: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
  short: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
  abbreviated: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  wide: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
};
var dayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'morning',
    afternoon: 'afternoon',
    evening: 'evening',
    night: 'night'
  }
};
var formattingDayPeriodValues = {
  narrow: {
    am: 'a',
    pm: 'p',
    midnight: 'mi',
    noon: 'n',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  abbreviated: {
    am: 'AM',
    pm: 'PM',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  },
  wide: {
    am: 'a.m.',
    pm: 'p.m.',
    midnight: 'midnight',
    noon: 'noon',
    morning: 'in the morning',
    afternoon: 'in the afternoon',
    evening: 'in the evening',
    night: 'at night'
  }
};

function ordinalNumber(dirtyNumber, _dirtyOptions) {
  var number = Number(dirtyNumber); // If ordinal numbers depend on context, for example,
  // if they are different for different grammatical genders,
  // use `options.unit`:
  //
  //   var options = dirtyOptions || {}
  //   var unit = String(options.unit)
  //
  // where `unit` can be 'year', 'quarter', 'month', 'week', 'date', 'dayOfYear',
  // 'day', 'hour', 'minute', 'second'

  var rem100 = number % 100;

  if (rem100 > 20 || rem100 < 10) {
    switch (rem100 % 10) {
      case 1:
        return number + 'st';

      case 2:
        return number + 'nd';

      case 3:
        return number + 'rd';
    }
  }

  return number + 'th';
}

var localize = {
  ordinalNumber: ordinalNumber,
  era: buildLocalizeFn({
    values: eraValues,
    defaultWidth: 'wide'
  }),
  quarter: buildLocalizeFn({
    values: quarterValues,
    defaultWidth: 'wide',
    argumentCallback: function (quarter) {
      return Number(quarter) - 1;
    }
  }),
  month: buildLocalizeFn({
    values: monthValues,
    defaultWidth: 'wide'
  }),
  day: buildLocalizeFn({
    values: dayValues,
    defaultWidth: 'wide'
  }),
  dayPeriod: buildLocalizeFn({
    values: dayPeriodValues,
    defaultWidth: 'wide',
    formattingValues: formattingDayPeriodValues,
    defaultFormattingWidth: 'wide'
  })
};

function buildMatchPatternFn(args) {
  return function (dirtyString, dirtyOptions) {
    var string = String(dirtyString);
    var options = dirtyOptions || {};
    var matchResult = string.match(args.matchPattern);

    if (!matchResult) {
      return null;
    }

    var matchedString = matchResult[0];
    var parseResult = string.match(args.parsePattern);

    if (!parseResult) {
      return null;
    }

    var value = args.valueCallback ? args.valueCallback(parseResult[0]) : parseResult[0];
    value = options.valueCallback ? options.valueCallback(value) : value;
    return {
      value: value,
      rest: string.slice(matchedString.length)
    };
  };
}

function buildMatchFn(args) {
  return function (dirtyString, dirtyOptions) {
    var string = String(dirtyString);
    var options = dirtyOptions || {};
    var width = options.width;
    var matchPattern = width && args.matchPatterns[width] || args.matchPatterns[args.defaultMatchWidth];
    var matchResult = string.match(matchPattern);

    if (!matchResult) {
      return null;
    }

    var matchedString = matchResult[0];
    var parsePatterns = width && args.parsePatterns[width] || args.parsePatterns[args.defaultParseWidth];
    var value;

    if (Object.prototype.toString.call(parsePatterns) === '[object Array]') {
      value = findIndex(parsePatterns, function (pattern) {
        return pattern.test(matchedString);
      });
    } else {
      value = findKey(parsePatterns, function (pattern) {
        return pattern.test(matchedString);
      });
    }

    value = args.valueCallback ? args.valueCallback(value) : value;
    value = options.valueCallback ? options.valueCallback(value) : value;
    return {
      value: value,
      rest: string.slice(matchedString.length)
    };
  };
}

function findKey(object, predicate) {
  for (var key in object) {
    if (object.hasOwnProperty(key) && predicate(object[key])) {
      return key;
    }
  }
}

function findIndex(array, predicate) {
  for (var key = 0; key < array.length; key++) {
    if (predicate(array[key])) {
      return key;
    }
  }
}

var matchOrdinalNumberPattern = /^(\d+)(th|st|nd|rd)?/i;
var parseOrdinalNumberPattern = /\d+/i;
var matchEraPatterns = {
  narrow: /^(b|a)/i,
  abbreviated: /^(b\.?\s?c\.?|b\.?\s?c\.?\s?e\.?|a\.?\s?d\.?|c\.?\s?e\.?)/i,
  wide: /^(before christ|before common era|anno domini|common era)/i
};
var parseEraPatterns = {
  any: [/^b/i, /^(a|c)/i]
};
var matchQuarterPatterns = {
  narrow: /^[1234]/i,
  abbreviated: /^q[1234]/i,
  wide: /^[1234](th|st|nd|rd)? quarter/i
};
var parseQuarterPatterns = {
  any: [/1/i, /2/i, /3/i, /4/i]
};
var matchMonthPatterns = {
  narrow: /^[jfmasond]/i,
  abbreviated: /^(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)/i,
  wide: /^(january|february|march|april|may|june|july|august|september|october|november|december)/i
};
var parseMonthPatterns = {
  narrow: [/^j/i, /^f/i, /^m/i, /^a/i, /^m/i, /^j/i, /^j/i, /^a/i, /^s/i, /^o/i, /^n/i, /^d/i],
  any: [/^ja/i, /^f/i, /^mar/i, /^ap/i, /^may/i, /^jun/i, /^jul/i, /^au/i, /^s/i, /^o/i, /^n/i, /^d/i]
};
var matchDayPatterns = {
  narrow: /^[smtwf]/i,
  short: /^(su|mo|tu|we|th|fr|sa)/i,
  abbreviated: /^(sun|mon|tue|wed|thu|fri|sat)/i,
  wide: /^(sunday|monday|tuesday|wednesday|thursday|friday|saturday)/i
};
var parseDayPatterns = {
  narrow: [/^s/i, /^m/i, /^t/i, /^w/i, /^t/i, /^f/i, /^s/i],
  any: [/^su/i, /^m/i, /^tu/i, /^w/i, /^th/i, /^f/i, /^sa/i]
};
var matchDayPeriodPatterns = {
  narrow: /^(a|p|mi|n|(in the|at) (morning|afternoon|evening|night))/i,
  any: /^([ap]\.?\s?m\.?|midnight|noon|(in the|at) (morning|afternoon|evening|night))/i
};
var parseDayPeriodPatterns = {
  any: {
    am: /^a/i,
    pm: /^p/i,
    midnight: /^mi/i,
    noon: /^no/i,
    morning: /morning/i,
    afternoon: /afternoon/i,
    evening: /evening/i,
    night: /night/i
  }
};
var match = {
  ordinalNumber: buildMatchPatternFn({
    matchPattern: matchOrdinalNumberPattern,
    parsePattern: parseOrdinalNumberPattern,
    valueCallback: function (value) {
      return parseInt(value, 10);
    }
  }),
  era: buildMatchFn({
    matchPatterns: matchEraPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseEraPatterns,
    defaultParseWidth: 'any'
  }),
  quarter: buildMatchFn({
    matchPatterns: matchQuarterPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseQuarterPatterns,
    defaultParseWidth: 'any',
    valueCallback: function (index) {
      return index + 1;
    }
  }),
  month: buildMatchFn({
    matchPatterns: matchMonthPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseMonthPatterns,
    defaultParseWidth: 'any'
  }),
  day: buildMatchFn({
    matchPatterns: matchDayPatterns,
    defaultMatchWidth: 'wide',
    parsePatterns: parseDayPatterns,
    defaultParseWidth: 'any'
  }),
  dayPeriod: buildMatchFn({
    matchPatterns: matchDayPeriodPatterns,
    defaultMatchWidth: 'any',
    parsePatterns: parseDayPeriodPatterns,
    defaultParseWidth: 'any'
  })
};

/**
 * @type {Locale}
 * @category Locales
 * @summary English locale (United States).
 * @language English
 * @iso-639-2 eng
 * @author Sasha Koss [@kossnocorp]{@link https://github.com/kossnocorp}
 * @author Lesha Koss [@leshakoss]{@link https://github.com/leshakoss}
 */

var locale = {
  code: 'en-US',
  formatDistance: formatDistance,
  formatLong: formatLong,
  formatRelative: formatRelative,
  localize: localize,
  match: match,
  options: {
    weekStartsOn: 0
    /* Sunday */
    ,
    firstWeekContainsDate: 1
  }
};

/**
 * @name subMilliseconds
 * @category Millisecond Helpers
 * @summary Subtract the specified number of milliseconds from the given date.
 *
 * @description
 * Subtract the specified number of milliseconds from the given date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * @param {Date|Number} date - the date to be changed
 * @param {Number} amount - the amount of milliseconds to be subtracted. Positive decimals will be rounded using `Math.floor`, decimals less than zero will be rounded using `Math.ceil`.
 * @returns {Date} the new date with the milliseconds subtracted
 * @throws {TypeError} 2 arguments required
 *
 * @example
 * // Subtract 750 milliseconds from 10 July 2014 12:45:30.000:
 * const result = subMilliseconds(new Date(2014, 6, 10, 12, 45, 30, 0), 750)
 * //=> Thu Jul 10 2014 12:45:29.250
 */

function subMilliseconds(dirtyDate, dirtyAmount) {
  requiredArgs(2, arguments);
  var amount = toInteger(dirtyAmount);
  return addMilliseconds(dirtyDate, -amount);
}

function addLeadingZeros(number, targetLength) {
  var sign = number < 0 ? '-' : '';
  var output = Math.abs(number).toString();

  while (output.length < targetLength) {
    output = '0' + output;
  }

  return sign + output;
}

/*
 * |     | Unit                           |     | Unit                           |
 * |-----|--------------------------------|-----|--------------------------------|
 * |  a  | AM, PM                         |  A* |                                |
 * |  d  | Day of month                   |  D  |                                |
 * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
 * |  m  | Minute                         |  M  | Month                          |
 * |  s  | Second                         |  S  | Fraction of second             |
 * |  y  | Year (abs)                     |  Y  |                                |
 *
 * Letters marked by * are not implemented but reserved by Unicode standard.
 */

var formatters = {
  // Year
  y: function (date, token) {
    // From http://www.unicode.org/reports/tr35/tr35-31/tr35-dates.html#Date_Format_tokens
    // | Year     |     y | yy |   yyy |  yyyy | yyyyy |
    // |----------|-------|----|-------|-------|-------|
    // | AD 1     |     1 | 01 |   001 |  0001 | 00001 |
    // | AD 12    |    12 | 12 |   012 |  0012 | 00012 |
    // | AD 123   |   123 | 23 |   123 |  0123 | 00123 |
    // | AD 1234  |  1234 | 34 |  1234 |  1234 | 01234 |
    // | AD 12345 | 12345 | 45 | 12345 | 12345 | 12345 |
    var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var year = signedYear > 0 ? signedYear : 1 - signedYear;
    return addLeadingZeros(token === 'yy' ? year % 100 : year, token.length);
  },
  // Month
  M: function (date, token) {
    var month = date.getUTCMonth();
    return token === 'M' ? String(month + 1) : addLeadingZeros(month + 1, 2);
  },
  // Day of the month
  d: function (date, token) {
    return addLeadingZeros(date.getUTCDate(), token.length);
  },
  // AM or PM
  a: function (date, token) {
    var dayPeriodEnumValue = date.getUTCHours() / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return dayPeriodEnumValue.toUpperCase();

      case 'aaa':
        return dayPeriodEnumValue;

      case 'aaaaa':
        return dayPeriodEnumValue[0];

      case 'aaaa':
      default:
        return dayPeriodEnumValue === 'am' ? 'a.m.' : 'p.m.';
    }
  },
  // Hour [1-12]
  h: function (date, token) {
    return addLeadingZeros(date.getUTCHours() % 12 || 12, token.length);
  },
  // Hour [0-23]
  H: function (date, token) {
    return addLeadingZeros(date.getUTCHours(), token.length);
  },
  // Minute
  m: function (date, token) {
    return addLeadingZeros(date.getUTCMinutes(), token.length);
  },
  // Second
  s: function (date, token) {
    return addLeadingZeros(date.getUTCSeconds(), token.length);
  },
  // Fraction of second
  S: function (date, token) {
    var numberOfDigits = token.length;
    var milliseconds = date.getUTCMilliseconds();
    var fractionalSeconds = Math.floor(milliseconds * Math.pow(10, numberOfDigits - 3));
    return addLeadingZeros(fractionalSeconds, token.length);
  }
};

var MILLISECONDS_IN_DAY = 86400000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCDayOfYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var timestamp = date.getTime();
  date.setUTCMonth(0, 1);
  date.setUTCHours(0, 0, 0, 0);
  var startOfYearTimestamp = date.getTime();
  var difference = timestamp - startOfYearTimestamp;
  return Math.floor(difference / MILLISECONDS_IN_DAY) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var weekStartsOn = 1;
  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var year = date.getUTCFullYear();
  var fourthOfJanuaryOfNextYear = new Date(0);
  fourthOfJanuaryOfNextYear.setUTCFullYear(year + 1, 0, 4);
  fourthOfJanuaryOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCISOWeek(fourthOfJanuaryOfNextYear);
  var fourthOfJanuaryOfThisYear = new Date(0);
  fourthOfJanuaryOfThisYear.setUTCFullYear(year, 0, 4);
  fourthOfJanuaryOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCISOWeek(fourthOfJanuaryOfThisYear);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCISOWeekYear(dirtyDate) {
  requiredArgs(1, arguments);
  var year = getUTCISOWeekYear(dirtyDate);
  var fourthOfJanuary = new Date(0);
  fourthOfJanuary.setUTCFullYear(year, 0, 4);
  fourthOfJanuary.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCISOWeek(fourthOfJanuary);
  return date;
}

var MILLISECONDS_IN_WEEK = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCISOWeek(dirtyDate) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCISOWeek(date).getTime() - startOfUTCISOWeekYear(date).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK) + 1;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeek(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeWeekStartsOn = locale && locale.options && locale.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  var date = toDate(dirtyDate);
  var day = date.getUTCDay();
  var diff = (day < weekStartsOn ? 7 : 0) + day - weekStartsOn;
  date.setUTCDate(date.getUTCDate() - diff);
  date.setUTCHours(0, 0, 0, 0);
  return date;
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate, dirtyOptions);
  var year = date.getUTCFullYear();
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var firstWeekOfNextYear = new Date(0);
  firstWeekOfNextYear.setUTCFullYear(year + 1, 0, firstWeekContainsDate);
  firstWeekOfNextYear.setUTCHours(0, 0, 0, 0);
  var startOfNextYear = startOfUTCWeek(firstWeekOfNextYear, dirtyOptions);
  var firstWeekOfThisYear = new Date(0);
  firstWeekOfThisYear.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeekOfThisYear.setUTCHours(0, 0, 0, 0);
  var startOfThisYear = startOfUTCWeek(firstWeekOfThisYear, dirtyOptions);

  if (date.getTime() >= startOfNextYear.getTime()) {
    return year + 1;
  } else if (date.getTime() >= startOfThisYear.getTime()) {
    return year;
  } else {
    return year - 1;
  }
}

// See issue: https://github.com/date-fns/date-fns/issues/376

function startOfUTCWeekYear(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var locale = options.locale;
  var localeFirstWeekContainsDate = locale && locale.options && locale.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate);
  var year = getUTCWeekYear(dirtyDate, dirtyOptions);
  var firstWeek = new Date(0);
  firstWeek.setUTCFullYear(year, 0, firstWeekContainsDate);
  firstWeek.setUTCHours(0, 0, 0, 0);
  var date = startOfUTCWeek(firstWeek, dirtyOptions);
  return date;
}

var MILLISECONDS_IN_WEEK$1 = 604800000; // This function will be a part of public API when UTC function will be implemented.
// See issue: https://github.com/date-fns/date-fns/issues/376

function getUTCWeek(dirtyDate, options) {
  requiredArgs(1, arguments);
  var date = toDate(dirtyDate);
  var diff = startOfUTCWeek(date, options).getTime() - startOfUTCWeekYear(date, options).getTime(); // Round the number of days to the nearest integer
  // because the number of milliseconds in a week is not constant
  // (e.g. it's different in the week of the daylight saving time clock shift)

  return Math.round(diff / MILLISECONDS_IN_WEEK$1) + 1;
}

var dayPeriodEnum = {
  am: 'am',
  pm: 'pm',
  midnight: 'midnight',
  noon: 'noon',
  morning: 'morning',
  afternoon: 'afternoon',
  evening: 'evening',
  night: 'night'
  /*
   * |     | Unit                           |     | Unit                           |
   * |-----|--------------------------------|-----|--------------------------------|
   * |  a  | AM, PM                         |  A* | Milliseconds in day            |
   * |  b  | AM, PM, noon, midnight         |  B  | Flexible day period            |
   * |  c  | Stand-alone local day of week  |  C* | Localized hour w/ day period   |
   * |  d  | Day of month                   |  D  | Day of year                    |
   * |  e  | Local day of week              |  E  | Day of week                    |
   * |  f  |                                |  F* | Day of week in month           |
   * |  g* | Modified Julian day            |  G  | Era                            |
   * |  h  | Hour [1-12]                    |  H  | Hour [0-23]                    |
   * |  i! | ISO day of week                |  I! | ISO week of year               |
   * |  j* | Localized hour w/ day period   |  J* | Localized hour w/o day period  |
   * |  k  | Hour [1-24]                    |  K  | Hour [0-11]                    |
   * |  l* | (deprecated)                   |  L  | Stand-alone month              |
   * |  m  | Minute                         |  M  | Month                          |
   * |  n  |                                |  N  |                                |
   * |  o! | Ordinal number modifier        |  O  | Timezone (GMT)                 |
   * |  p! | Long localized time            |  P! | Long localized date            |
   * |  q  | Stand-alone quarter            |  Q  | Quarter                        |
   * |  r* | Related Gregorian year         |  R! | ISO week-numbering year        |
   * |  s  | Second                         |  S  | Fraction of second             |
   * |  t! | Seconds timestamp              |  T! | Milliseconds timestamp         |
   * |  u  | Extended year                  |  U* | Cyclic year                    |
   * |  v* | Timezone (generic non-locat.)  |  V* | Timezone (location)            |
   * |  w  | Local week of year             |  W* | Week of month                  |
   * |  x  | Timezone (ISO-8601 w/o Z)      |  X  | Timezone (ISO-8601)            |
   * |  y  | Year (abs)                     |  Y  | Local week-numbering year      |
   * |  z  | Timezone (specific non-locat.) |  Z* | Timezone (aliases)             |
   *
   * Letters marked by * are not implemented but reserved by Unicode standard.
   *
   * Letters marked by ! are non-standard, but implemented by date-fns:
   * - `o` modifies the previous token to turn it into an ordinal (see `format` docs)
   * - `i` is ISO day of week. For `i` and `ii` is returns numeric ISO week days,
   *   i.e. 7 for Sunday, 1 for Monday, etc.
   * - `I` is ISO week of year, as opposed to `w` which is local week of year.
   * - `R` is ISO week-numbering year, as opposed to `Y` which is local week-numbering year.
   *   `R` is supposed to be used in conjunction with `I` and `i`
   *   for universal ISO week-numbering date, whereas
   *   `Y` is supposed to be used in conjunction with `w` and `e`
   *   for week-numbering date specific to the locale.
   * - `P` is long localized date format
   * - `p` is long localized time format
   */

};
var formatters$1 = {
  // Era
  G: function (date, token, localize) {
    var era = date.getUTCFullYear() > 0 ? 1 : 0;

    switch (token) {
      // AD, BC
      case 'G':
      case 'GG':
      case 'GGG':
        return localize.era(era, {
          width: 'abbreviated'
        });
      // A, B

      case 'GGGGG':
        return localize.era(era, {
          width: 'narrow'
        });
      // Anno Domini, Before Christ

      case 'GGGG':
      default:
        return localize.era(era, {
          width: 'wide'
        });
    }
  },
  // Year
  y: function (date, token, localize) {
    // Ordinal number
    if (token === 'yo') {
      var signedYear = date.getUTCFullYear(); // Returns 1 for 1 BC (which is year 0 in JavaScript)

      var year = signedYear > 0 ? signedYear : 1 - signedYear;
      return localize.ordinalNumber(year, {
        unit: 'year'
      });
    }

    return formatters.y(date, token);
  },
  // Local week-numbering year
  Y: function (date, token, localize, options) {
    var signedWeekYear = getUTCWeekYear(date, options); // Returns 1 for 1 BC (which is year 0 in JavaScript)

    var weekYear = signedWeekYear > 0 ? signedWeekYear : 1 - signedWeekYear; // Two digit year

    if (token === 'YY') {
      var twoDigitYear = weekYear % 100;
      return addLeadingZeros(twoDigitYear, 2);
    } // Ordinal number


    if (token === 'Yo') {
      return localize.ordinalNumber(weekYear, {
        unit: 'year'
      });
    } // Padding


    return addLeadingZeros(weekYear, token.length);
  },
  // ISO week-numbering year
  R: function (date, token) {
    var isoWeekYear = getUTCISOWeekYear(date); // Padding

    return addLeadingZeros(isoWeekYear, token.length);
  },
  // Extended year. This is a single number designating the year of this calendar system.
  // The main difference between `y` and `u` localizers are B.C. years:
  // | Year | `y` | `u` |
  // |------|-----|-----|
  // | AC 1 |   1 |   1 |
  // | BC 1 |   1 |   0 |
  // | BC 2 |   2 |  -1 |
  // Also `yy` always returns the last two digits of a year,
  // while `uu` pads single digit years to 2 characters and returns other years unchanged.
  u: function (date, token) {
    var year = date.getUTCFullYear();
    return addLeadingZeros(year, token.length);
  },
  // Quarter
  Q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'Q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'QQ':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'Qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'QQQ':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'QQQQQ':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'formatting'
        });
      // 1st quarter, 2nd quarter, ...

      case 'QQQQ':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone quarter
  q: function (date, token, localize) {
    var quarter = Math.ceil((date.getUTCMonth() + 1) / 3);

    switch (token) {
      // 1, 2, 3, 4
      case 'q':
        return String(quarter);
      // 01, 02, 03, 04

      case 'qq':
        return addLeadingZeros(quarter, 2);
      // 1st, 2nd, 3rd, 4th

      case 'qo':
        return localize.ordinalNumber(quarter, {
          unit: 'quarter'
        });
      // Q1, Q2, Q3, Q4

      case 'qqq':
        return localize.quarter(quarter, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // 1, 2, 3, 4 (narrow quarter; could be not numerical)

      case 'qqqqq':
        return localize.quarter(quarter, {
          width: 'narrow',
          context: 'standalone'
        });
      // 1st quarter, 2nd quarter, ...

      case 'qqqq':
      default:
        return localize.quarter(quarter, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Month
  M: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      case 'M':
      case 'MM':
        return formatters.M(date, token);
      // 1st, 2nd, ..., 12th

      case 'Mo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'MMM':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // J, F, ..., D

      case 'MMMMM':
        return localize.month(month, {
          width: 'narrow',
          context: 'formatting'
        });
      // January, February, ..., December

      case 'MMMM':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone month
  L: function (date, token, localize) {
    var month = date.getUTCMonth();

    switch (token) {
      // 1, 2, ..., 12
      case 'L':
        return String(month + 1);
      // 01, 02, ..., 12

      case 'LL':
        return addLeadingZeros(month + 1, 2);
      // 1st, 2nd, ..., 12th

      case 'Lo':
        return localize.ordinalNumber(month + 1, {
          unit: 'month'
        });
      // Jan, Feb, ..., Dec

      case 'LLL':
        return localize.month(month, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // J, F, ..., D

      case 'LLLLL':
        return localize.month(month, {
          width: 'narrow',
          context: 'standalone'
        });
      // January, February, ..., December

      case 'LLLL':
      default:
        return localize.month(month, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // Local week of year
  w: function (date, token, localize, options) {
    var week = getUTCWeek(date, options);

    if (token === 'wo') {
      return localize.ordinalNumber(week, {
        unit: 'week'
      });
    }

    return addLeadingZeros(week, token.length);
  },
  // ISO week of year
  I: function (date, token, localize) {
    var isoWeek = getUTCISOWeek(date);

    if (token === 'Io') {
      return localize.ordinalNumber(isoWeek, {
        unit: 'week'
      });
    }

    return addLeadingZeros(isoWeek, token.length);
  },
  // Day of the month
  d: function (date, token, localize) {
    if (token === 'do') {
      return localize.ordinalNumber(date.getUTCDate(), {
        unit: 'date'
      });
    }

    return formatters.d(date, token);
  },
  // Day of year
  D: function (date, token, localize) {
    var dayOfYear = getUTCDayOfYear(date);

    if (token === 'Do') {
      return localize.ordinalNumber(dayOfYear, {
        unit: 'dayOfYear'
      });
    }

    return addLeadingZeros(dayOfYear, token.length);
  },
  // Day of week
  E: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();

    switch (token) {
      // Tue
      case 'E':
      case 'EE':
      case 'EEE':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'EEEEE':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'EEEEEE':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'EEEE':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Local day of week
  e: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (Nth day of week with current locale or weekStartsOn)
      case 'e':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'ee':
        return addLeadingZeros(localDayOfWeek, 2);
      // 1st, 2nd, ..., 7th

      case 'eo':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'eee':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'eeeee':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'eeeeee':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'eeee':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Stand-alone local day of week
  c: function (date, token, localize, options) {
    var dayOfWeek = date.getUTCDay();
    var localDayOfWeek = (dayOfWeek - options.weekStartsOn + 8) % 7 || 7;

    switch (token) {
      // Numerical value (same as in `e`)
      case 'c':
        return String(localDayOfWeek);
      // Padded numerical value

      case 'cc':
        return addLeadingZeros(localDayOfWeek, token.length);
      // 1st, 2nd, ..., 7th

      case 'co':
        return localize.ordinalNumber(localDayOfWeek, {
          unit: 'day'
        });

      case 'ccc':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'standalone'
        });
      // T

      case 'ccccc':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'standalone'
        });
      // Tu

      case 'cccccc':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'standalone'
        });
      // Tuesday

      case 'cccc':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'standalone'
        });
    }
  },
  // ISO day of week
  i: function (date, token, localize) {
    var dayOfWeek = date.getUTCDay();
    var isoDayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;

    switch (token) {
      // 2
      case 'i':
        return String(isoDayOfWeek);
      // 02

      case 'ii':
        return addLeadingZeros(isoDayOfWeek, token.length);
      // 2nd

      case 'io':
        return localize.ordinalNumber(isoDayOfWeek, {
          unit: 'day'
        });
      // Tue

      case 'iii':
        return localize.day(dayOfWeek, {
          width: 'abbreviated',
          context: 'formatting'
        });
      // T

      case 'iiiii':
        return localize.day(dayOfWeek, {
          width: 'narrow',
          context: 'formatting'
        });
      // Tu

      case 'iiiiii':
        return localize.day(dayOfWeek, {
          width: 'short',
          context: 'formatting'
        });
      // Tuesday

      case 'iiii':
      default:
        return localize.day(dayOfWeek, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM or PM
  a: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';

    switch (token) {
      case 'a':
      case 'aa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'aaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'aaaaa':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'aaaa':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // AM, PM, midnight, noon
  b: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours === 12) {
      dayPeriodEnumValue = dayPeriodEnum.noon;
    } else if (hours === 0) {
      dayPeriodEnumValue = dayPeriodEnum.midnight;
    } else {
      dayPeriodEnumValue = hours / 12 >= 1 ? 'pm' : 'am';
    }

    switch (token) {
      case 'b':
      case 'bb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'bbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        }).toLowerCase();

      case 'bbbbb':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'bbbb':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // in the morning, in the afternoon, in the evening, at night
  B: function (date, token, localize) {
    var hours = date.getUTCHours();
    var dayPeriodEnumValue;

    if (hours >= 17) {
      dayPeriodEnumValue = dayPeriodEnum.evening;
    } else if (hours >= 12) {
      dayPeriodEnumValue = dayPeriodEnum.afternoon;
    } else if (hours >= 4) {
      dayPeriodEnumValue = dayPeriodEnum.morning;
    } else {
      dayPeriodEnumValue = dayPeriodEnum.night;
    }

    switch (token) {
      case 'B':
      case 'BB':
      case 'BBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'abbreviated',
          context: 'formatting'
        });

      case 'BBBBB':
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'narrow',
          context: 'formatting'
        });

      case 'BBBB':
      default:
        return localize.dayPeriod(dayPeriodEnumValue, {
          width: 'wide',
          context: 'formatting'
        });
    }
  },
  // Hour [1-12]
  h: function (date, token, localize) {
    if (token === 'ho') {
      var hours = date.getUTCHours() % 12;
      if (hours === 0) hours = 12;
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return formatters.h(date, token);
  },
  // Hour [0-23]
  H: function (date, token, localize) {
    if (token === 'Ho') {
      return localize.ordinalNumber(date.getUTCHours(), {
        unit: 'hour'
      });
    }

    return formatters.H(date, token);
  },
  // Hour [0-11]
  K: function (date, token, localize) {
    var hours = date.getUTCHours() % 12;

    if (token === 'Ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Hour [1-24]
  k: function (date, token, localize) {
    var hours = date.getUTCHours();
    if (hours === 0) hours = 24;

    if (token === 'ko') {
      return localize.ordinalNumber(hours, {
        unit: 'hour'
      });
    }

    return addLeadingZeros(hours, token.length);
  },
  // Minute
  m: function (date, token, localize) {
    if (token === 'mo') {
      return localize.ordinalNumber(date.getUTCMinutes(), {
        unit: 'minute'
      });
    }

    return formatters.m(date, token);
  },
  // Second
  s: function (date, token, localize) {
    if (token === 'so') {
      return localize.ordinalNumber(date.getUTCSeconds(), {
        unit: 'second'
      });
    }

    return formatters.s(date, token);
  },
  // Fraction of second
  S: function (date, token) {
    return formatters.S(date, token);
  },
  // Timezone (ISO-8601. If offset is 0, output is always `'Z'`)
  X: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    if (timezoneOffset === 0) {
      return 'Z';
    }

    switch (token) {
      // Hours and optional minutes
      case 'X':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XX`

      case 'XXXX':
      case 'XX':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `XXX`

      case 'XXXXX':
      case 'XXX': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (ISO-8601. If offset is 0, output is `'+00:00'` or equivalent)
  x: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Hours and optional minutes
      case 'x':
        return formatTimezoneWithOptionalMinutes(timezoneOffset);
      // Hours, minutes and optional seconds without `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xx`

      case 'xxxx':
      case 'xx':
        // Hours and minutes without `:` delimiter
        return formatTimezone(timezoneOffset);
      // Hours, minutes and optional seconds with `:` delimiter
      // Note: neither ISO-8601 nor JavaScript supports seconds in timezone offsets
      // so this token always has the same output as `xxx`

      case 'xxxxx':
      case 'xxx': // Hours and minutes with `:` delimiter

      default:
        return formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (GMT)
  O: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'O':
      case 'OO':
      case 'OOO':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'OOOO':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Timezone (specific non-location)
  z: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timezoneOffset = originalDate.getTimezoneOffset();

    switch (token) {
      // Short
      case 'z':
      case 'zz':
      case 'zzz':
        return 'GMT' + formatTimezoneShort(timezoneOffset, ':');
      // Long

      case 'zzzz':
      default:
        return 'GMT' + formatTimezone(timezoneOffset, ':');
    }
  },
  // Seconds timestamp
  t: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = Math.floor(originalDate.getTime() / 1000);
    return addLeadingZeros(timestamp, token.length);
  },
  // Milliseconds timestamp
  T: function (date, token, _localize, options) {
    var originalDate = options._originalDate || date;
    var timestamp = originalDate.getTime();
    return addLeadingZeros(timestamp, token.length);
  }
};

function formatTimezoneShort(offset, dirtyDelimiter) {
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = Math.floor(absOffset / 60);
  var minutes = absOffset % 60;

  if (minutes === 0) {
    return sign + String(hours);
  }

  var delimiter = dirtyDelimiter || '';
  return sign + String(hours) + delimiter + addLeadingZeros(minutes, 2);
}

function formatTimezoneWithOptionalMinutes(offset, dirtyDelimiter) {
  if (offset % 60 === 0) {
    var sign = offset > 0 ? '-' : '+';
    return sign + addLeadingZeros(Math.abs(offset) / 60, 2);
  }

  return formatTimezone(offset, dirtyDelimiter);
}

function formatTimezone(offset, dirtyDelimiter) {
  var delimiter = dirtyDelimiter || '';
  var sign = offset > 0 ? '-' : '+';
  var absOffset = Math.abs(offset);
  var hours = addLeadingZeros(Math.floor(absOffset / 60), 2);
  var minutes = addLeadingZeros(absOffset % 60, 2);
  return sign + hours + delimiter + minutes;
}

function dateLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'P':
      return formatLong.date({
        width: 'short'
      });

    case 'PP':
      return formatLong.date({
        width: 'medium'
      });

    case 'PPP':
      return formatLong.date({
        width: 'long'
      });

    case 'PPPP':
    default:
      return formatLong.date({
        width: 'full'
      });
  }
}

function timeLongFormatter(pattern, formatLong) {
  switch (pattern) {
    case 'p':
      return formatLong.time({
        width: 'short'
      });

    case 'pp':
      return formatLong.time({
        width: 'medium'
      });

    case 'ppp':
      return formatLong.time({
        width: 'long'
      });

    case 'pppp':
    default:
      return formatLong.time({
        width: 'full'
      });
  }
}

function dateTimeLongFormatter(pattern, formatLong) {
  var matchResult = pattern.match(/(P+)(p+)?/);
  var datePattern = matchResult[1];
  var timePattern = matchResult[2];

  if (!timePattern) {
    return dateLongFormatter(pattern, formatLong);
  }

  var dateTimeFormat;

  switch (datePattern) {
    case 'P':
      dateTimeFormat = formatLong.dateTime({
        width: 'short'
      });
      break;

    case 'PP':
      dateTimeFormat = formatLong.dateTime({
        width: 'medium'
      });
      break;

    case 'PPP':
      dateTimeFormat = formatLong.dateTime({
        width: 'long'
      });
      break;

    case 'PPPP':
    default:
      dateTimeFormat = formatLong.dateTime({
        width: 'full'
      });
      break;
  }

  return dateTimeFormat.replace('{{date}}', dateLongFormatter(datePattern, formatLong)).replace('{{time}}', timeLongFormatter(timePattern, formatLong));
}

var longFormatters = {
  p: timeLongFormatter,
  P: dateTimeLongFormatter
};

var protectedDayOfYearTokens = ['D', 'DD'];
var protectedWeekYearTokens = ['YY', 'YYYY'];
function isProtectedDayOfYearToken(token) {
  return protectedDayOfYearTokens.indexOf(token) !== -1;
}
function isProtectedWeekYearToken(token) {
  return protectedWeekYearTokens.indexOf(token) !== -1;
}
function throwProtectedError(token, format, input) {
  if (token === 'YYYY') {
    throw new RangeError("Use `yyyy` instead of `YYYY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'YY') {
    throw new RangeError("Use `yy` instead of `YY` (in `".concat(format, "`) for formatting years to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'D') {
    throw new RangeError("Use `d` instead of `D` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  } else if (token === 'DD') {
    throw new RangeError("Use `dd` instead of `DD` (in `".concat(format, "`) for formatting days of the month to the input `").concat(input, "`; see: https://git.io/fxCyr"));
  }
}

// - [yYQqMLwIdDecihHKkms]o matches any available ordinal number token
//   (one of the certain letters followed by `o`)
// - (\w)\1* matches any sequences of the same letter
// - '' matches two quote characters in a row
// - '(''|[^'])+('|$) matches anything surrounded by two quote characters ('),
//   except a single quote symbol, which ends the sequence.
//   Two quote characters do not end the sequence.
//   If there is no matching single quote
//   then the sequence will continue until the end of the string.
// - . matches any single character unmatched by previous parts of the RegExps

var formattingTokensRegExp = /[yYQqMLwIdDecihHKkms]o|(\w)\1*|''|'(''|[^'])+('|$)|./g; // This RegExp catches symbols escaped by quotes, and also
// sequences of symbols P, p, and the combinations like `PPPPPPPppppp`

var longFormattingTokensRegExp = /P+p+|P+|p+|''|'(''|[^'])+('|$)|./g;
var escapedStringRegExp = /^'([^]*?)'?$/;
var doubleQuoteRegExp = /''/g;
var unescapedLatinCharacterRegExp = /[a-zA-Z]/;
/**
 * @name format
 * @category Common Helpers
 * @summary Format the date.
 *
 * @description
 * Return the formatted date string in the given format. The result may vary by locale.
 *
 * > ⚠️ Please note that the `format` tokens differ from Moment.js and other libraries.
 * > See: https://git.io/fxCyr
 *
 * The characters wrapped between two single quotes characters (') are escaped.
 * Two single quotes in a row, whether inside or outside a quoted sequence, represent a 'real' single quote.
 * (see the last example)
 *
 * Format of the string is based on Unicode Technical Standard #35:
 * https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table
 * with a few additions (see note 7 below the table).
 *
 * Accepted patterns:
 * | Unit                            | Pattern | Result examples                   | Notes |
 * |---------------------------------|---------|-----------------------------------|-------|
 * | Era                             | G..GGG  | AD, BC                            |       |
 * |                                 | GGGG    | Anno Domini, Before Christ        | 2     |
 * |                                 | GGGGG   | A, B                              |       |
 * | Calendar year                   | y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | yo      | 44th, 1st, 0th, 17th              | 5,7   |
 * |                                 | yy      | 44, 01, 00, 17                    | 5     |
 * |                                 | yyy     | 044, 001, 1900, 2017              | 5     |
 * |                                 | yyyy    | 0044, 0001, 1900, 2017            | 5     |
 * |                                 | yyyyy   | ...                               | 3,5   |
 * | Local week-numbering year       | Y       | 44, 1, 1900, 2017                 | 5     |
 * |                                 | Yo      | 44th, 1st, 1900th, 2017th         | 5,7   |
 * |                                 | YY      | 44, 01, 00, 17                    | 5,8   |
 * |                                 | YYY     | 044, 001, 1900, 2017              | 5     |
 * |                                 | YYYY    | 0044, 0001, 1900, 2017            | 5,8   |
 * |                                 | YYYYY   | ...                               | 3,5   |
 * | ISO week-numbering year         | R       | -43, 0, 1, 1900, 2017             | 5,7   |
 * |                                 | RR      | -43, 00, 01, 1900, 2017           | 5,7   |
 * |                                 | RRR     | -043, 000, 001, 1900, 2017        | 5,7   |
 * |                                 | RRRR    | -0043, 0000, 0001, 1900, 2017     | 5,7   |
 * |                                 | RRRRR   | ...                               | 3,5,7 |
 * | Extended year                   | u       | -43, 0, 1, 1900, 2017             | 5     |
 * |                                 | uu      | -43, 01, 1900, 2017               | 5     |
 * |                                 | uuu     | -043, 001, 1900, 2017             | 5     |
 * |                                 | uuuu    | -0043, 0001, 1900, 2017           | 5     |
 * |                                 | uuuuu   | ...                               | 3,5   |
 * | Quarter (formatting)            | Q       | 1, 2, 3, 4                        |       |
 * |                                 | Qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | QQ      | 01, 02, 03, 04                    |       |
 * |                                 | QQQ     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | QQQQ    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | QQQQQ   | 1, 2, 3, 4                        | 4     |
 * | Quarter (stand-alone)           | q       | 1, 2, 3, 4                        |       |
 * |                                 | qo      | 1st, 2nd, 3rd, 4th                | 7     |
 * |                                 | qq      | 01, 02, 03, 04                    |       |
 * |                                 | qqq     | Q1, Q2, Q3, Q4                    |       |
 * |                                 | qqqq    | 1st quarter, 2nd quarter, ...     | 2     |
 * |                                 | qqqqq   | 1, 2, 3, 4                        | 4     |
 * | Month (formatting)              | M       | 1, 2, ..., 12                     |       |
 * |                                 | Mo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | MM      | 01, 02, ..., 12                   |       |
 * |                                 | MMM     | Jan, Feb, ..., Dec                |       |
 * |                                 | MMMM    | January, February, ..., December  | 2     |
 * |                                 | MMMMM   | J, F, ..., D                      |       |
 * | Month (stand-alone)             | L       | 1, 2, ..., 12                     |       |
 * |                                 | Lo      | 1st, 2nd, ..., 12th               | 7     |
 * |                                 | LL      | 01, 02, ..., 12                   |       |
 * |                                 | LLL     | Jan, Feb, ..., Dec                |       |
 * |                                 | LLLL    | January, February, ..., December  | 2     |
 * |                                 | LLLLL   | J, F, ..., D                      |       |
 * | Local week of year              | w       | 1, 2, ..., 53                     |       |
 * |                                 | wo      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | ww      | 01, 02, ..., 53                   |       |
 * | ISO week of year                | I       | 1, 2, ..., 53                     | 7     |
 * |                                 | Io      | 1st, 2nd, ..., 53th               | 7     |
 * |                                 | II      | 01, 02, ..., 53                   | 7     |
 * | Day of month                    | d       | 1, 2, ..., 31                     |       |
 * |                                 | do      | 1st, 2nd, ..., 31st               | 7     |
 * |                                 | dd      | 01, 02, ..., 31                   |       |
 * | Day of year                     | D       | 1, 2, ..., 365, 366               | 9     |
 * |                                 | Do      | 1st, 2nd, ..., 365th, 366th       | 7     |
 * |                                 | DD      | 01, 02, ..., 365, 366             | 9     |
 * |                                 | DDD     | 001, 002, ..., 365, 366           |       |
 * |                                 | DDDD    | ...                               | 3     |
 * | Day of week (formatting)        | E..EEE  | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | EEEE    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | EEEEE   | M, T, W, T, F, S, S               |       |
 * |                                 | EEEEEE  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | ISO day of week (formatting)    | i       | 1, 2, 3, ..., 7                   | 7     |
 * |                                 | io      | 1st, 2nd, ..., 7th                | 7     |
 * |                                 | ii      | 01, 02, ..., 07                   | 7     |
 * |                                 | iii     | Mon, Tue, Wed, ..., Sun           | 7     |
 * |                                 | iiii    | Monday, Tuesday, ..., Sunday      | 2,7   |
 * |                                 | iiiii   | M, T, W, T, F, S, S               | 7     |
 * |                                 | iiiiii  | Mo, Tu, We, Th, Fr, Su, Sa        | 7     |
 * | Local day of week (formatting)  | e       | 2, 3, 4, ..., 1                   |       |
 * |                                 | eo      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | ee      | 02, 03, ..., 01                   |       |
 * |                                 | eee     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | eeee    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | eeeee   | M, T, W, T, F, S, S               |       |
 * |                                 | eeeeee  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | Local day of week (stand-alone) | c       | 2, 3, 4, ..., 1                   |       |
 * |                                 | co      | 2nd, 3rd, ..., 1st                | 7     |
 * |                                 | cc      | 02, 03, ..., 01                   |       |
 * |                                 | ccc     | Mon, Tue, Wed, ..., Sun           |       |
 * |                                 | cccc    | Monday, Tuesday, ..., Sunday      | 2     |
 * |                                 | ccccc   | M, T, W, T, F, S, S               |       |
 * |                                 | cccccc  | Mo, Tu, We, Th, Fr, Su, Sa        |       |
 * | AM, PM                          | a..aa   | AM, PM                            |       |
 * |                                 | aaa     | am, pm                            |       |
 * |                                 | aaaa    | a.m., p.m.                        | 2     |
 * |                                 | aaaaa   | a, p                              |       |
 * | AM, PM, noon, midnight          | b..bb   | AM, PM, noon, midnight            |       |
 * |                                 | bbb     | am, pm, noon, midnight            |       |
 * |                                 | bbbb    | a.m., p.m., noon, midnight        | 2     |
 * |                                 | bbbbb   | a, p, n, mi                       |       |
 * | Flexible day period             | B..BBB  | at night, in the morning, ...     |       |
 * |                                 | BBBB    | at night, in the morning, ...     | 2     |
 * |                                 | BBBBB   | at night, in the morning, ...     |       |
 * | Hour [1-12]                     | h       | 1, 2, ..., 11, 12                 |       |
 * |                                 | ho      | 1st, 2nd, ..., 11th, 12th         | 7     |
 * |                                 | hh      | 01, 02, ..., 11, 12               |       |
 * | Hour [0-23]                     | H       | 0, 1, 2, ..., 23                  |       |
 * |                                 | Ho      | 0th, 1st, 2nd, ..., 23rd          | 7     |
 * |                                 | HH      | 00, 01, 02, ..., 23               |       |
 * | Hour [0-11]                     | K       | 1, 2, ..., 11, 0                  |       |
 * |                                 | Ko      | 1st, 2nd, ..., 11th, 0th          | 7     |
 * |                                 | KK      | 01, 02, ..., 11, 00               |       |
 * | Hour [1-24]                     | k       | 24, 1, 2, ..., 23                 |       |
 * |                                 | ko      | 24th, 1st, 2nd, ..., 23rd         | 7     |
 * |                                 | kk      | 24, 01, 02, ..., 23               |       |
 * | Minute                          | m       | 0, 1, ..., 59                     |       |
 * |                                 | mo      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | mm      | 00, 01, ..., 59                   |       |
 * | Second                          | s       | 0, 1, ..., 59                     |       |
 * |                                 | so      | 0th, 1st, ..., 59th               | 7     |
 * |                                 | ss      | 00, 01, ..., 59                   |       |
 * | Fraction of second              | S       | 0, 1, ..., 9                      |       |
 * |                                 | SS      | 00, 01, ..., 99                   |       |
 * |                                 | SSS     | 000, 001, ..., 999                |       |
 * |                                 | SSSS    | ...                               | 3     |
 * | Timezone (ISO-8601 w/ Z)        | X       | -08, +0530, Z                     |       |
 * |                                 | XX      | -0800, +0530, Z                   |       |
 * |                                 | XXX     | -08:00, +05:30, Z                 |       |
 * |                                 | XXXX    | -0800, +0530, Z, +123456          | 2     |
 * |                                 | XXXXX   | -08:00, +05:30, Z, +12:34:56      |       |
 * | Timezone (ISO-8601 w/o Z)       | x       | -08, +0530, +00                   |       |
 * |                                 | xx      | -0800, +0530, +0000               |       |
 * |                                 | xxx     | -08:00, +05:30, +00:00            | 2     |
 * |                                 | xxxx    | -0800, +0530, +0000, +123456      |       |
 * |                                 | xxxxx   | -08:00, +05:30, +00:00, +12:34:56 |       |
 * | Timezone (GMT)                  | O...OOO | GMT-8, GMT+5:30, GMT+0            |       |
 * |                                 | OOOO    | GMT-08:00, GMT+05:30, GMT+00:00   | 2     |
 * | Timezone (specific non-locat.)  | z...zzz | GMT-8, GMT+5:30, GMT+0            | 6     |
 * |                                 | zzzz    | GMT-08:00, GMT+05:30, GMT+00:00   | 2,6   |
 * | Seconds timestamp               | t       | 512969520                         | 7     |
 * |                                 | tt      | ...                               | 3,7   |
 * | Milliseconds timestamp          | T       | 512969520900                      | 7     |
 * |                                 | TT      | ...                               | 3,7   |
 * | Long localized date             | P       | 04/29/1453                        | 7     |
 * |                                 | PP      | Apr 29, 1453                      | 7     |
 * |                                 | PPP     | April 29th, 1453                  | 7     |
 * |                                 | PPPP    | Friday, April 29th, 1453          | 2,7   |
 * | Long localized time             | p       | 12:00 AM                          | 7     |
 * |                                 | pp      | 12:00:00 AM                       | 7     |
 * |                                 | ppp     | 12:00:00 AM GMT+2                 | 7     |
 * |                                 | pppp    | 12:00:00 AM GMT+02:00             | 2,7   |
 * | Combination of date and time    | Pp      | 04/29/1453, 12:00 AM              | 7     |
 * |                                 | PPpp    | Apr 29, 1453, 12:00:00 AM         | 7     |
 * |                                 | PPPppp  | April 29th, 1453 at ...           | 7     |
 * |                                 | PPPPpppp| Friday, April 29th, 1453 at ...   | 2,7   |
 * Notes:
 * 1. "Formatting" units (e.g. formatting quarter) in the default en-US locale
 *    are the same as "stand-alone" units, but are different in some languages.
 *    "Formatting" units are declined according to the rules of the language
 *    in the context of a date. "Stand-alone" units are always nominative singular:
 *
 *    `format(new Date(2017, 10, 6), 'do LLLL', {locale: cs}) //=> '6. listopad'`
 *
 *    `format(new Date(2017, 10, 6), 'do MMMM', {locale: cs}) //=> '6. listopadu'`
 *
 * 2. Any sequence of the identical letters is a pattern, unless it is escaped by
 *    the single quote characters (see below).
 *    If the sequence is longer than listed in table (e.g. `EEEEEEEEEEE`)
 *    the output will be the same as default pattern for this unit, usually
 *    the longest one (in case of ISO weekdays, `EEEE`). Default patterns for units
 *    are marked with "2" in the last column of the table.
 *
 *    `format(new Date(2017, 10, 6), 'MMM') //=> 'Nov'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMM') //=> 'N'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMM') //=> 'November'`
 *
 *    `format(new Date(2017, 10, 6), 'MMMMMMM') //=> 'November'`
 *
 * 3. Some patterns could be unlimited length (such as `yyyyyyyy`).
 *    The output will be padded with zeros to match the length of the pattern.
 *
 *    `format(new Date(2017, 10, 6), 'yyyyyyyy') //=> '00002017'`
 *
 * 4. `QQQQQ` and `qqqqq` could be not strictly numerical in some locales.
 *    These tokens represent the shortest form of the quarter.
 *
 * 5. The main difference between `y` and `u` patterns are B.C. years:
 *
 *    | Year | `y` | `u` |
 *    |------|-----|-----|
 *    | AC 1 |   1 |   1 |
 *    | BC 1 |   1 |   0 |
 *    | BC 2 |   2 |  -1 |
 *
 *    Also `yy` always returns the last two digits of a year,
 *    while `uu` pads single digit years to 2 characters and returns other years unchanged:
 *
 *    | Year | `yy` | `uu` |
 *    |------|------|------|
 *    | 1    |   01 |   01 |
 *    | 14   |   14 |   14 |
 *    | 376  |   76 |  376 |
 *    | 1453 |   53 | 1453 |
 *
 *    The same difference is true for local and ISO week-numbering years (`Y` and `R`),
 *    except local week-numbering years are dependent on `options.weekStartsOn`
 *    and `options.firstWeekContainsDate` (compare [getISOWeekYear]{@link https://date-fns.org/docs/getISOWeekYear}
 *    and [getWeekYear]{@link https://date-fns.org/docs/getWeekYear}).
 *
 * 6. Specific non-location timezones are currently unavailable in `date-fns`,
 *    so right now these tokens fall back to GMT timezones.
 *
 * 7. These patterns are not in the Unicode Technical Standard #35:
 *    - `i`: ISO day of week
 *    - `I`: ISO week of year
 *    - `R`: ISO week-numbering year
 *    - `t`: seconds timestamp
 *    - `T`: milliseconds timestamp
 *    - `o`: ordinal number modifier
 *    - `P`: long localized date
 *    - `p`: long localized time
 *
 * 8. `YY` and `YYYY` tokens represent week-numbering years but they are often confused with years.
 *    You should enable `options.useAdditionalWeekYearTokens` to use them. See: https://git.io/fxCyr
 *
 * 9. `D` and `DD` tokens represent days of the year but they are ofthen confused with days of the month.
 *    You should enable `options.useAdditionalDayOfYearTokens` to use them. See: https://git.io/fxCyr
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The second argument is now required for the sake of explicitness.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   format(new Date(2016, 0, 1))
 *
 *   // v2.0.0 onward
 *   format(new Date(2016, 0, 1), "yyyy-MM-dd'T'HH:mm:ss.SSSxxx")
 *   ```
 *
 * - New format string API for `format` function
 *   which is based on [Unicode Technical Standard #35](https://www.unicode.org/reports/tr35/tr35-dates.html#Date_Field_Symbol_Table).
 *   See [this post](https://blog.date-fns.org/post/unicode-tokens-in-date-fns-v2-sreatyki91jg) for more details.
 *
 * - Characters are now escaped using single quote symbols (`'`) instead of square brackets.
 *
 * @param {Date|Number} date - the original date
 * @param {String} format - the string of tokens
 * @param {Object} [options] - an object with options.
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @param {0|1|2|3|4|5|6} [options.weekStartsOn=0] - the index of the first day of the week (0 - Sunday)
 * @param {Number} [options.firstWeekContainsDate=1] - the day of January, which is
 * @param {Boolean} [options.useAdditionalWeekYearTokens=false] - if true, allows usage of the week-numbering year tokens `YY` and `YYYY`;
 *   see: https://git.io/fxCyr
 * @param {Boolean} [options.useAdditionalDayOfYearTokens=false] - if true, allows usage of the day of year tokens `D` and `DD`;
 *   see: https://git.io/fxCyr
 * @returns {String} the formatted date string
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `localize` property
 * @throws {RangeError} `options.locale` must contain `formatLong` property
 * @throws {RangeError} `options.weekStartsOn` must be between 0 and 6
 * @throws {RangeError} `options.firstWeekContainsDate` must be between 1 and 7
 * @throws {RangeError} use `yyyy` instead of `YYYY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `yy` instead of `YY` for formatting years using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `d` instead of `D` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} use `dd` instead of `DD` for formatting days of the month using [format provided] to the input [input provided]; see: https://git.io/fxCyr
 * @throws {RangeError} format string contains an unescaped latin alphabet character
 *
 * @example
 * // Represent 11 February 2014 in middle-endian format:
 * var result = format(new Date(2014, 1, 11), 'MM/dd/yyyy')
 * //=> '02/11/2014'
 *
 * @example
 * // Represent 2 July 2014 in Esperanto:
 * import { eoLocale } from 'date-fns/locale/eo'
 * var result = format(new Date(2014, 6, 2), "do 'de' MMMM yyyy", {
 *   locale: eoLocale
 * })
 * //=> '2-a de julio 2014'
 *
 * @example
 * // Escape string by single quote characters:
 * var result = format(new Date(2014, 6, 2, 15), "h 'o''clock'")
 * //=> "3 o'clock"
 */

function format(dirtyDate, dirtyFormatStr, dirtyOptions) {
  requiredArgs(2, arguments);
  var formatStr = String(dirtyFormatStr);
  var options = dirtyOptions || {};
  var locale$1 = options.locale || locale;
  var localeFirstWeekContainsDate = locale$1.options && locale$1.options.firstWeekContainsDate;
  var defaultFirstWeekContainsDate = localeFirstWeekContainsDate == null ? 1 : toInteger(localeFirstWeekContainsDate);
  var firstWeekContainsDate = options.firstWeekContainsDate == null ? defaultFirstWeekContainsDate : toInteger(options.firstWeekContainsDate); // Test if weekStartsOn is between 1 and 7 _and_ is not NaN

  if (!(firstWeekContainsDate >= 1 && firstWeekContainsDate <= 7)) {
    throw new RangeError('firstWeekContainsDate must be between 1 and 7 inclusively');
  }

  var localeWeekStartsOn = locale$1.options && locale$1.options.weekStartsOn;
  var defaultWeekStartsOn = localeWeekStartsOn == null ? 0 : toInteger(localeWeekStartsOn);
  var weekStartsOn = options.weekStartsOn == null ? defaultWeekStartsOn : toInteger(options.weekStartsOn); // Test if weekStartsOn is between 0 and 6 _and_ is not NaN

  if (!(weekStartsOn >= 0 && weekStartsOn <= 6)) {
    throw new RangeError('weekStartsOn must be between 0 and 6 inclusively');
  }

  if (!locale$1.localize) {
    throw new RangeError('locale must contain localize property');
  }

  if (!locale$1.formatLong) {
    throw new RangeError('locale must contain formatLong property');
  }

  var originalDate = toDate(dirtyDate);

  if (!isValid(originalDate)) {
    throw new RangeError('Invalid time value');
  } // Convert the date in system timezone to the same date in UTC+00:00 timezone.
  // This ensures that when UTC functions will be implemented, locales will be compatible with them.
  // See an issue about UTC functions: https://github.com/date-fns/date-fns/issues/376


  var timezoneOffset = getTimezoneOffsetInMilliseconds(originalDate);
  var utcDate = subMilliseconds(originalDate, timezoneOffset);
  var formatterOptions = {
    firstWeekContainsDate: firstWeekContainsDate,
    weekStartsOn: weekStartsOn,
    locale: locale$1,
    _originalDate: originalDate
  };
  var result = formatStr.match(longFormattingTokensRegExp).map(function (substring) {
    var firstCharacter = substring[0];

    if (firstCharacter === 'p' || firstCharacter === 'P') {
      var longFormatter = longFormatters[firstCharacter];
      return longFormatter(substring, locale$1.formatLong, formatterOptions);
    }

    return substring;
  }).join('').match(formattingTokensRegExp).map(function (substring) {
    // Replace two single quote characters with one single quote character
    if (substring === "''") {
      return "'";
    }

    var firstCharacter = substring[0];

    if (firstCharacter === "'") {
      return cleanEscapedString(substring);
    }

    var formatter = formatters$1[firstCharacter];

    if (formatter) {
      if (!options.useAdditionalWeekYearTokens && isProtectedWeekYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      if (!options.useAdditionalDayOfYearTokens && isProtectedDayOfYearToken(substring)) {
        throwProtectedError(substring, dirtyFormatStr, dirtyDate);
      }

      return formatter(utcDate, substring, locale$1.localize, formatterOptions);
    }

    if (firstCharacter.match(unescapedLatinCharacterRegExp)) {
      throw new RangeError('Format string contains an unescaped latin alphabet character `' + firstCharacter + '`');
    }

    return substring;
  }).join('');
  return result;
}

function cleanEscapedString(input) {
  return input.match(escapedStringRegExp)[1].replace(doubleQuoteRegExp, "'");
}

function assign(target, dirtyObject) {
  if (target == null) {
    throw new TypeError('assign requires that input parameter not be null or undefined');
  }

  dirtyObject = dirtyObject || {};

  for (var property in dirtyObject) {
    if (dirtyObject.hasOwnProperty(property)) {
      target[property] = dirtyObject[property];
    }
  }

  return target;
}

function cloneObject(dirtyObject) {
  return assign({}, dirtyObject);
}

var MINUTES_IN_DAY = 1440;
var MINUTES_IN_ALMOST_TWO_DAYS = 2520;
var MINUTES_IN_MONTH = 43200;
var MINUTES_IN_TWO_MONTHS = 86400;
/**
 * @name formatDistance
 * @category Common Helpers
 * @summary Return the distance between the given dates in words.
 *
 * @description
 * Return the distance between the given dates in words.
 *
 * | Distance between dates                                            | Result              |
 * |-------------------------------------------------------------------|---------------------|
 * | 0 ... 30 secs                                                     | less than a minute  |
 * | 30 secs ... 1 min 30 secs                                         | 1 minute            |
 * | 1 min 30 secs ... 44 mins 30 secs                                 | [2..44] minutes     |
 * | 44 mins ... 30 secs ... 89 mins 30 secs                           | about 1 hour        |
 * | 89 mins 30 secs ... 23 hrs 59 mins 30 secs                        | about [2..24] hours |
 * | 23 hrs 59 mins 30 secs ... 41 hrs 59 mins 30 secs                 | 1 day               |
 * | 41 hrs 59 mins 30 secs ... 29 days 23 hrs 59 mins 30 secs         | [2..30] days        |
 * | 29 days 23 hrs 59 mins 30 secs ... 44 days 23 hrs 59 mins 30 secs | about 1 month       |
 * | 44 days 23 hrs 59 mins 30 secs ... 59 days 23 hrs 59 mins 30 secs | about 2 months      |
 * | 59 days 23 hrs 59 mins 30 secs ... 1 yr                           | [2..12] months      |
 * | 1 yr ... 1 yr 3 months                                            | about 1 year        |
 * | 1 yr 3 months ... 1 yr 9 month s                                  | over 1 year         |
 * | 1 yr 9 months ... 2 yrs                                           | almost 2 years      |
 * | N yrs ... N yrs 3 months                                          | about N years       |
 * | N yrs 3 months ... N yrs 9 months                                 | over N years        |
 * | N yrs 9 months ... N+1 yrs                                        | almost N+1 years    |
 *
 * With `options.includeSeconds == true`:
 * | Distance between dates | Result               |
 * |------------------------|----------------------|
 * | 0 secs ... 5 secs      | less than 5 seconds  |
 * | 5 secs ... 10 secs     | less than 10 seconds |
 * | 10 secs ... 20 secs    | less than 20 seconds |
 * | 20 secs ... 40 secs    | half a minute        |
 * | 40 secs ... 60 secs    | less than a minute   |
 * | 60 secs ... 90 secs    | 1 minute             |
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The function was renamed from `distanceInWords ` to `formatDistance`
 *   to make its name consistent with `format` and `formatRelative`.
 *
 * - The order of arguments is swapped to make the function
 *   consistent with `differenceIn...` functions.
 *
 *   ```javascript
 *   // Before v2.0.0
 *
 *   distanceInWords(
 *     new Date(1986, 3, 4, 10, 32, 0),
 *     new Date(1986, 3, 4, 11, 32, 0),
 *     { addSuffix: true }
 *   ) //=> 'in about 1 hour'
 *
 *   // v2.0.0 onward
 *
 *   formatDistance(
 *     new Date(1986, 3, 4, 11, 32, 0),
 *     new Date(1986, 3, 4, 10, 32, 0),
 *     { addSuffix: true }
 *   ) //=> 'in about 1 hour'
 *   ```
 *
 * @param {Date|Number} date - the date
 * @param {Date|Number} baseDate - the date to compare with
 * @param {Object} [options] - an object with options.
 * @param {Boolean} [options.includeSeconds=false] - distances less than a minute are more detailed
 * @param {Boolean} [options.addSuffix=false] - result indicates if the second date is earlier or later than the first
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @returns {String} the distance in words
 * @throws {TypeError} 2 arguments required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `baseDate` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `formatDistance` property
 *
 * @example
 * // What is the distance between 2 July 2014 and 1 January 2015?
 * const result = formatDistance(new Date(2014, 6, 2), new Date(2015, 0, 1))
 * //=> '6 months'
 *
 * @example
 * // What is the distance between 1 January 2015 00:00:15
 * // and 1 January 2015 00:00:00, including seconds?
 * const result = formatDistance(
 *   new Date(2015, 0, 1, 0, 0, 15),
 *   new Date(2015, 0, 1, 0, 0, 0),
 *   { includeSeconds: true }
 * )
 * //=> 'less than 20 seconds'
 *
 * @example
 * // What is the distance from 1 January 2016
 * // to 1 January 2015, with a suffix?
 * const result = formatDistance(new Date(2015, 0, 1), new Date(2016, 0, 1), {
 *   addSuffix: true
 * })
 * //=> 'about 1 year ago'
 *
 * @example
 * // What is the distance between 1 August 2016 and 1 January 2015 in Esperanto?
 * import { eoLocale } from 'date-fns/locale/eo'
 * const result = formatDistance(new Date(2016, 7, 1), new Date(2015, 0, 1), {
 *   locale: eoLocale
 * })
 * //=> 'pli ol 1 jaro'
 */

function formatDistance$1(dirtyDate, dirtyBaseDate) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  requiredArgs(2, arguments);
  var locale$1 = options.locale || locale;

  if (!locale$1.formatDistance) {
    throw new RangeError('locale must contain formatDistance property');
  }

  var comparison = compareAsc(dirtyDate, dirtyBaseDate);

  if (isNaN(comparison)) {
    throw new RangeError('Invalid time value');
  }

  var localizeOptions = cloneObject(options);
  localizeOptions.addSuffix = Boolean(options.addSuffix);
  localizeOptions.comparison = comparison;
  var dateLeft;
  var dateRight;

  if (comparison > 0) {
    dateLeft = toDate(dirtyBaseDate);
    dateRight = toDate(dirtyDate);
  } else {
    dateLeft = toDate(dirtyDate);
    dateRight = toDate(dirtyBaseDate);
  }

  var seconds = differenceInSeconds(dateRight, dateLeft);
  var offsetInSeconds = (getTimezoneOffsetInMilliseconds(dateRight) - getTimezoneOffsetInMilliseconds(dateLeft)) / 1000;
  var minutes = Math.round((seconds - offsetInSeconds) / 60);
  var months; // 0 up to 2 mins

  if (minutes < 2) {
    if (options.includeSeconds) {
      if (seconds < 5) {
        return locale$1.formatDistance('lessThanXSeconds', 5, localizeOptions);
      } else if (seconds < 10) {
        return locale$1.formatDistance('lessThanXSeconds', 10, localizeOptions);
      } else if (seconds < 20) {
        return locale$1.formatDistance('lessThanXSeconds', 20, localizeOptions);
      } else if (seconds < 40) {
        return locale$1.formatDistance('halfAMinute', null, localizeOptions);
      } else if (seconds < 60) {
        return locale$1.formatDistance('lessThanXMinutes', 1, localizeOptions);
      } else {
        return locale$1.formatDistance('xMinutes', 1, localizeOptions);
      }
    } else {
      if (minutes === 0) {
        return locale$1.formatDistance('lessThanXMinutes', 1, localizeOptions);
      } else {
        return locale$1.formatDistance('xMinutes', minutes, localizeOptions);
      }
    } // 2 mins up to 0.75 hrs

  } else if (minutes < 45) {
    return locale$1.formatDistance('xMinutes', minutes, localizeOptions); // 0.75 hrs up to 1.5 hrs
  } else if (minutes < 90) {
    return locale$1.formatDistance('aboutXHours', 1, localizeOptions); // 1.5 hrs up to 24 hrs
  } else if (minutes < MINUTES_IN_DAY) {
    var hours = Math.round(minutes / 60);
    return locale$1.formatDistance('aboutXHours', hours, localizeOptions); // 1 day up to 1.75 days
  } else if (minutes < MINUTES_IN_ALMOST_TWO_DAYS) {
    return locale$1.formatDistance('xDays', 1, localizeOptions); // 1.75 days up to 30 days
  } else if (minutes < MINUTES_IN_MONTH) {
    var days = Math.round(minutes / MINUTES_IN_DAY);
    return locale$1.formatDistance('xDays', days, localizeOptions); // 1 month up to 2 months
  } else if (minutes < MINUTES_IN_TWO_MONTHS) {
    months = Math.round(minutes / MINUTES_IN_MONTH);
    return locale$1.formatDistance('aboutXMonths', months, localizeOptions);
  }

  months = differenceInMonths(dateRight, dateLeft); // 2 months up to 12 months

  if (months < 12) {
    var nearestMonth = Math.round(minutes / MINUTES_IN_MONTH);
    return locale$1.formatDistance('xMonths', nearestMonth, localizeOptions); // 1 year up to max Date
  } else {
    var monthsSinceStartOfYear = months % 12;
    var years = Math.floor(months / 12); // N years up to 1 years 3 months

    if (monthsSinceStartOfYear < 3) {
      return locale$1.formatDistance('aboutXYears', years, localizeOptions); // N years 3 months up to N years 9 months
    } else if (monthsSinceStartOfYear < 9) {
      return locale$1.formatDistance('overXYears', years, localizeOptions); // N years 9 months up to N year 12 months
    } else {
      return locale$1.formatDistance('almostXYears', years + 1, localizeOptions);
    }
  }
}

/**
 * @name formatDistanceToNow
 * @category Common Helpers
 * @summary Return the distance between the given date and now in words.
 * @pure false
 *
 * @description
 * Return the distance between the given date and now in words.
 *
 * | Distance to now                                                   | Result              |
 * |-------------------------------------------------------------------|---------------------|
 * | 0 ... 30 secs                                                     | less than a minute  |
 * | 30 secs ... 1 min 30 secs                                         | 1 minute            |
 * | 1 min 30 secs ... 44 mins 30 secs                                 | [2..44] minutes     |
 * | 44 mins ... 30 secs ... 89 mins 30 secs                           | about 1 hour        |
 * | 89 mins 30 secs ... 23 hrs 59 mins 30 secs                        | about [2..24] hours |
 * | 23 hrs 59 mins 30 secs ... 41 hrs 59 mins 30 secs                 | 1 day               |
 * | 41 hrs 59 mins 30 secs ... 29 days 23 hrs 59 mins 30 secs         | [2..30] days        |
 * | 29 days 23 hrs 59 mins 30 secs ... 44 days 23 hrs 59 mins 30 secs | about 1 month       |
 * | 44 days 23 hrs 59 mins 30 secs ... 59 days 23 hrs 59 mins 30 secs | about 2 months      |
 * | 59 days 23 hrs 59 mins 30 secs ... 1 yr                           | [2..12] months      |
 * | 1 yr ... 1 yr 3 months                                            | about 1 year        |
 * | 1 yr 3 months ... 1 yr 9 month s                                  | over 1 year         |
 * | 1 yr 9 months ... 2 yrs                                           | almost 2 years      |
 * | N yrs ... N yrs 3 months                                          | about N years       |
 * | N yrs 3 months ... N yrs 9 months                                 | over N years        |
 * | N yrs 9 months ... N+1 yrs                                        | almost N+1 years    |
 *
 * With `options.includeSeconds == true`:
 * | Distance to now     | Result               |
 * |---------------------|----------------------|
 * | 0 secs ... 5 secs   | less than 5 seconds  |
 * | 5 secs ... 10 secs  | less than 10 seconds |
 * | 10 secs ... 20 secs | less than 20 seconds |
 * | 20 secs ... 40 secs | half a minute        |
 * | 40 secs ... 60 secs | less than a minute   |
 * | 60 secs ... 90 secs | 1 minute             |
 *
 * > ⚠️ Please note that this function is not present in the FP submodule as
 * > it uses `Date.now()` internally hence impure and can't be safely curried.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The function was renamed from `distanceInWordsToNow ` to `formatDistanceToNow`
 *   to make its name consistent with `format` and `formatRelative`.
 *
 *   ```javascript
 *   // Before v2.0.0
 *
 *   distanceInWordsToNow(new Date(2014, 6, 2), { addSuffix: true })
 *   //=> 'in 6 months'
 *
 *   // v2.0.0 onward
 *
 *   formatDistanceToNow(new Date(2014, 6, 2), { addSuffix: true })
 *   //=> 'in 6 months'
 *   ```
 *
 * @param {Date|Number} date - the given date
 * @param {Object} [options] - the object with options
 * @param {Boolean} [options.includeSeconds=false] - distances less than a minute are more detailed
 * @param {Boolean} [options.addSuffix=false] - result specifies if now is earlier or later than the passed date
 * @param {Locale} [options.locale=defaultLocale] - the locale object. See [Locale]{@link https://date-fns.org/docs/Locale}
 * @returns {String} the distance in words
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `date` must not be Invalid Date
 * @throws {RangeError} `options.locale` must contain `formatDistance` property
 *
 * @example
 * // If today is 1 January 2015, what is the distance to 2 July 2014?
 * var result = formatDistanceToNow(
 *   new Date(2014, 6, 2)
 * )
 * //=> '6 months'
 *
 * @example
 * // If now is 1 January 2015 00:00:00,
 * // what is the distance to 1 January 2015 00:00:15, including seconds?
 * var result = formatDistanceToNow(
 *   new Date(2015, 0, 1, 0, 0, 15),
 *   {includeSeconds: true}
 * )
 * //=> 'less than 20 seconds'
 *
 * @example
 * // If today is 1 January 2015,
 * // what is the distance to 1 January 2016, with a suffix?
 * var result = formatDistanceToNow(
 *   new Date(2016, 0, 1),
 *   {addSuffix: true}
 * )
 * //=> 'in about 1 year'
 *
 * @example
 * // If today is 1 January 2015,
 * // what is the distance to 1 August 2016 in Esperanto?
 * var eoLocale = require('date-fns/locale/eo')
 * var result = formatDistanceToNow(
 *   new Date(2016, 7, 1),
 *   {locale: eoLocale}
 * )
 * //=> 'pli ol 1 jaro'
 */

function formatDistanceToNow(dirtyDate, dirtyOptions) {
  requiredArgs(1, arguments);
  return formatDistance$1(dirtyDate, Date.now(), dirtyOptions);
}

var MILLISECONDS_IN_HOUR = 3600000;
var MILLISECONDS_IN_MINUTE = 60000;
var DEFAULT_ADDITIONAL_DIGITS = 2;
var patterns = {
  dateTimeDelimiter: /[T ]/,
  timeZoneDelimiter: /[Z ]/i,
  timezone: /([Z+-].*)$/
};
var dateRegex = /^-?(?:(\d{3})|(\d{2})(?:-?(\d{2}))?|W(\d{2})(?:-?(\d{1}))?|)$/;
var timeRegex = /^(\d{2}(?:[.,]\d*)?)(?::?(\d{2}(?:[.,]\d*)?))?(?::?(\d{2}(?:[.,]\d*)?))?$/;
var timezoneRegex = /^([+-])(\d{2})(?::?(\d{2}))?$/;
/**
 * @name parseISO
 * @category Common Helpers
 * @summary Parse ISO string
 *
 * @description
 * Parse the given string in ISO 8601 format and return an instance of Date.
 *
 * Function accepts complete ISO 8601 formats as well as partial implementations.
 * ISO 8601: http://en.wikipedia.org/wiki/ISO_8601
 *
 * If the argument isn't a string, the function cannot parse the string or
 * the values are invalid, it returns Invalid Date.
 *
 * ### v2.0.0 breaking changes:
 *
 * - [Changes that are common for the whole library](https://github.com/date-fns/date-fns/blob/master/docs/upgradeGuide.md#Common-Changes).
 *
 * - The previous `parse` implementation was renamed to `parseISO`.
 *
 *   ```javascript
 *   // Before v2.0.0
 *   parse('2016-01-01')
 *
 *   // v2.0.0 onward
 *   parseISO('2016-01-01')
 *   ```
 *
 * - `parseISO` now validates separate date and time values in ISO-8601 strings
 *   and returns `Invalid Date` if the date is invalid.
 *
 *   ```javascript
 *   parseISO('2018-13-32')
 *   //=> Invalid Date
 *   ```
 *
 * - `parseISO` now doesn't fall back to `new Date` constructor
 *   if it fails to parse a string argument. Instead, it returns `Invalid Date`.
 *
 * @param {String} argument - the value to convert
 * @param {Object} [options] - an object with options.
 * @param {0|1|2} [options.additionalDigits=2] - the additional number of digits in the extended year format
 * @returns {Date} the parsed date in the local time zone
 * @throws {TypeError} 1 argument required
 * @throws {RangeError} `options.additionalDigits` must be 0, 1 or 2
 *
 * @example
 * // Convert string '2014-02-11T11:30:30' to date:
 * var result = parseISO('2014-02-11T11:30:30')
 * //=> Tue Feb 11 2014 11:30:30
 *
 * @example
 * // Convert string '+02014101' to date,
 * // if the additional number of digits in the extended year format is 1:
 * var result = parseISO('+02014101', { additionalDigits: 1 })
 * //=> Fri Apr 11 2014 00:00:00
 */

function parseISO(argument, dirtyOptions) {
  requiredArgs(1, arguments);
  var options = dirtyOptions || {};
  var additionalDigits = options.additionalDigits == null ? DEFAULT_ADDITIONAL_DIGITS : toInteger(options.additionalDigits);

  if (additionalDigits !== 2 && additionalDigits !== 1 && additionalDigits !== 0) {
    throw new RangeError('additionalDigits must be 0, 1 or 2');
  }

  if (!(typeof argument === 'string' || Object.prototype.toString.call(argument) === '[object String]')) {
    return new Date(NaN);
  }

  var dateStrings = splitDateString(argument);
  var date;

  if (dateStrings.date) {
    var parseYearResult = parseYear(dateStrings.date, additionalDigits);
    date = parseDate(parseYearResult.restDateString, parseYearResult.year);
  }

  if (isNaN(date) || !date) {
    return new Date(NaN);
  }

  var timestamp = date.getTime();
  var time = 0;
  var offset;

  if (dateStrings.time) {
    time = parseTime(dateStrings.time);

    if (isNaN(time) || time === null) {
      return new Date(NaN);
    }
  }

  if (dateStrings.timezone) {
    offset = parseTimezone(dateStrings.timezone);

    if (isNaN(offset)) {
      return new Date(NaN);
    }
  } else {
    var dirtyDate = new Date(timestamp + time); // js parsed string assuming it's in UTC timezone
    // but we need it to be parsed in our timezone
    // so we use utc values to build date in our timezone.
    // Year values from 0 to 99 map to the years 1900 to 1999
    // so set year explicitly with setFullYear.

    var result = new Date(0);
    result.setFullYear(dirtyDate.getUTCFullYear(), dirtyDate.getUTCMonth(), dirtyDate.getUTCDate());
    result.setHours(dirtyDate.getUTCHours(), dirtyDate.getUTCMinutes(), dirtyDate.getUTCSeconds(), dirtyDate.getUTCMilliseconds());
    return result;
  }

  return new Date(timestamp + time + offset);
}

function splitDateString(dateString) {
  var dateStrings = {};
  var array = dateString.split(patterns.dateTimeDelimiter);
  var timeString; // The regex match should only return at maximum two array elements.
  // [date], [time], or [date, time].

  if (array.length > 2) {
    return dateStrings;
  }

  if (/:/.test(array[0])) {
    dateStrings.date = null;
    timeString = array[0];
  } else {
    dateStrings.date = array[0];
    timeString = array[1];

    if (patterns.timeZoneDelimiter.test(dateStrings.date)) {
      dateStrings.date = dateString.split(patterns.timeZoneDelimiter)[0];
      timeString = dateString.substr(dateStrings.date.length, dateString.length);
    }
  }

  if (timeString) {
    var token = patterns.timezone.exec(timeString);

    if (token) {
      dateStrings.time = timeString.replace(token[1], '');
      dateStrings.timezone = token[1];
    } else {
      dateStrings.time = timeString;
    }
  }

  return dateStrings;
}

function parseYear(dateString, additionalDigits) {
  var regex = new RegExp('^(?:(\\d{4}|[+-]\\d{' + (4 + additionalDigits) + '})|(\\d{2}|[+-]\\d{' + (2 + additionalDigits) + '})$)');
  var captures = dateString.match(regex); // Invalid ISO-formatted year

  if (!captures) return {
    year: null
  };
  var year = captures[1] && parseInt(captures[1]);
  var century = captures[2] && parseInt(captures[2]);
  return {
    year: century == null ? year : century * 100,
    restDateString: dateString.slice((captures[1] || captures[2]).length)
  };
}

function parseDate(dateString, year) {
  // Invalid ISO-formatted year
  if (year === null) return null;
  var captures = dateString.match(dateRegex); // Invalid ISO-formatted string

  if (!captures) return null;
  var isWeekDate = !!captures[4];
  var dayOfYear = parseDateUnit(captures[1]);
  var month = parseDateUnit(captures[2]) - 1;
  var day = parseDateUnit(captures[3]);
  var week = parseDateUnit(captures[4]);
  var dayOfWeek = parseDateUnit(captures[5]) - 1;

  if (isWeekDate) {
    if (!validateWeekDate(year, week, dayOfWeek)) {
      return new Date(NaN);
    }

    return dayOfISOWeekYear(year, week, dayOfWeek);
  } else {
    var date = new Date(0);

    if (!validateDate(year, month, day) || !validateDayOfYearDate(year, dayOfYear)) {
      return new Date(NaN);
    }

    date.setUTCFullYear(year, month, Math.max(dayOfYear, day));
    return date;
  }
}

function parseDateUnit(value) {
  return value ? parseInt(value) : 1;
}

function parseTime(timeString) {
  var captures = timeString.match(timeRegex);
  if (!captures) return null; // Invalid ISO-formatted time

  var hours = parseTimeUnit(captures[1]);
  var minutes = parseTimeUnit(captures[2]);
  var seconds = parseTimeUnit(captures[3]);

  if (!validateTime(hours, minutes, seconds)) {
    return NaN;
  }

  return hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE + seconds * 1000;
}

function parseTimeUnit(value) {
  return value && parseFloat(value.replace(',', '.')) || 0;
}

function parseTimezone(timezoneString) {
  if (timezoneString === 'Z') return 0;
  var captures = timezoneString.match(timezoneRegex);
  if (!captures) return 0;
  var sign = captures[1] === '+' ? -1 : 1;
  var hours = parseInt(captures[2]);
  var minutes = captures[3] && parseInt(captures[3]) || 0;

  if (!validateTimezone(hours, minutes)) {
    return NaN;
  }

  return sign * (hours * MILLISECONDS_IN_HOUR + minutes * MILLISECONDS_IN_MINUTE);
}

function dayOfISOWeekYear(isoWeekYear, week, day) {
  var date = new Date(0);
  date.setUTCFullYear(isoWeekYear, 0, 4);
  var fourthOfJanuaryDay = date.getUTCDay() || 7;
  var diff = (week - 1) * 7 + day + 1 - fourthOfJanuaryDay;
  date.setUTCDate(date.getUTCDate() + diff);
  return date;
} // Validation functions
// February is null to handle the leap year (using ||)


var daysInMonths = [31, null, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

function isLeapYearIndex(year) {
  return year % 400 === 0 || year % 4 === 0 && year % 100;
}

function validateDate(year, month, date) {
  return month >= 0 && month <= 11 && date >= 1 && date <= (daysInMonths[month] || (isLeapYearIndex(year) ? 29 : 28));
}

function validateDayOfYearDate(year, dayOfYear) {
  return dayOfYear >= 1 && dayOfYear <= (isLeapYearIndex(year) ? 366 : 365);
}

function validateWeekDate(_year, week, day) {
  return week >= 1 && week <= 53 && day >= 0 && day <= 6;
}

function validateTime(hours, minutes, seconds) {
  if (hours === 24) {
    return minutes === 0 && seconds === 0;
  }

  return seconds >= 0 && seconds < 60 && minutes >= 0 && minutes < 60 && hours >= 0 && hours < 25;
}

function validateTimezone(_hours, minutes) {
  return minutes >= 0 && minutes <= 59;
}

const defaultSettings = {
  dateFormat: 'relative',
  // https://github.com/date-fns/date-fns/blob/master/src/format/index.js
  enableFilters: false,
  defaultReviewsSortOrder: 'date desc',
  // 'date desc' | 'date asc' | 'has_media desc' | 'rating desc' | 'rating asc' | 'helpful desc' | 'unhelpful desc'
  enhancedRecorder: false
};
let widgetSettings = defaultSettings;
function setWidgetSettings(newWidgetSettings) {
  widgetSettings = { ...widgetSettings,
    ...newWidgetSettings
  };
}

var OkendoUtils = {
  getFormattedDate(date) {
    const parsedDate = parseISO(date);

    if (widgetSettings.dateFormat !== 'relative') {
      return format(parsedDate, widgetSettings.dateFormat);
    }

    return formatDistanceToNow(parsedDate, {
      addSuffix: true
    });
  },

  addParagraphsTags(text) {
    let encodedParagraphs = '<p>';
    encodedParagraphs += text.replace(/(?:\r\n|\r|\n)+/g, '</p><p>');
    encodedParagraphs += '</p>';
    return encodedParagraphs;
  },

  /**
   * Simplified version of https://davidwalsh.name/function-debounce. debounce() needs to be called directly.
   * @param {*} func Must be a regular function() instead of arrow function to access context
   * @param {*} wait Wait time in ms
   */
  debounce(func, wait) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;

      if (timer) {
        clearTimeout(timer);
      }

      timer = setTimeout(function () {
        func.apply(context, args);
      }, wait);
    };
  }

};

//
var script$6 = {
  props: {
    reply: {
      type: Object,
      required: true
    }
  },
  components: {
    Avatar
  },
  methods: {
    formatDate(date) {
      return OkendoUtils.getFormattedDate(date);
    }

  }
};

/* script */
const __vue_script__$6 = script$6;

/* template */
var __vue_render__$6 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _vm._m(1)};
var __vue_staticRenderFns__$6 = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('avatar',{attrs:{"avatarUrl":_vm.reply.avatarUrl,"displayName":_vm.reply.title,"width":"40px"}})},function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-reviewReply"},[(_vm.reply.avatarUrl)?_c('div',{staticClass:"okeReviews-reviewReply-avatar"},[_vm._m(0)],1):_vm._e(),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewReply-main"},[_c('div',{staticClass:"okeReviews-reviewReply-title"},[_vm._v("\n            "+_vm._s(_vm.reply.title)+"\n        ")]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewReply-date"},[_vm._v("\n            "+_vm._s(_vm.formatDate(_vm.reply.dateCreated))+"\n        ")]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewReply-body",domProps:{"innerHTML":_vm._s(_vm.reply.body)}})])])}];

  /* style */
  const __vue_inject_styles__$6 = function (inject) {
    if (!inject) return
    inject("data-v-06d91612_0", { source: ".okeReviews-reviewReply{font-size:12px;display:flex;border-top:2px solid #f5f5f5;padding-top:16px;margin-top:12px;margin-bottom:4px}.okeReviews-reviewReply-main{display:flex;justify-content:space-between;flex-flow:row wrap;flex-grow:1}.okeReviews-reviewReply-avatar{flex-grow:0}.okeReviews-reviewReply-date,.okeReviews-reviewReply-title{margin-bottom:4px}.okeReviews-reviewReply-title{font-weight:600}.okeReviews-reviewReply-body{width:100%}.okeReviews-reviewReply-date{color:#7c7c7c}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$6 = undefined;
  /* module identifier */
  const __vue_module_identifier__$6 = undefined;
  /* functional template */
  const __vue_is_functional_template__$6 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoReviewReply = normalizeComponent(
    { render: __vue_render__$6, staticRenderFns: __vue_staticRenderFns__$6 },
    __vue_inject_styles__$6,
    __vue_script__$6,
    __vue_scope_id__$6,
    __vue_is_functional_template__$6,
    __vue_module_identifier__$6,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$7 = {
  props: {
    attributes: {
      type: Array,
      required: true
    }
  },
  methods: {
    percentageValue(index) {
      const attribute = this.attributes[index];
      return attribute.type === 'range' ? this.scaleBetween(attribute.value, 0, 100, 1, 5) : this.scaleBetween(attribute.value, 0, 100, -1, 1);
    },

    scaledValue(index) {
      const attribute = this.attributes[index];
      return attribute.type === 'range' ? attribute.value : this.scaleBetween(attribute.value, -2, 2, -1, 1);
    },

    scaleBetween(unscaledNum, minAllowed, maxAllowed, unscaledNumMin, unscaledNumMax) {
      return (maxAllowed - minAllowed) * (unscaledNum - unscaledNumMin) / (unscaledNumMax - unscaledNumMin) + minAllowed;
    }

  }
};

/* script */
const __vue_script__$7 = script$7;

/* template */
var __vue_render__$7 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('tbody',_vm._l((_vm.attributes),function(attribute,index){return _c('tr',{key:attribute.title,staticClass:"okeReviews-attributeRatingsTable-row"},[_c('th',{staticClass:"okeReviews-attributeRatingsTable-row-label"},[_vm._v("\n            "+_vm._s(attribute.title)+"\n        ")]),_vm._v(" "),_c('td',{staticClass:"okeReviews-attributeRatingsTable-row-value"},[(attribute.type === 'range')?_c('div',{staticClass:"or-rg",class:{ 'has-lbl': attribute.minLabel || attribute.maxLabel }},[_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("Rated "+_vm._s(_vm.scaledValue(index))+" out of 5")]),_vm._v(" "),_c('div',{staticClass:"or-rg-bar"},[_c('div',{staticClass:"or-rg-bar-bg",style:({ width: _vm.percentageValue(index) + '%'})}),_vm._v(" "),_c('div',{staticClass:"or-rg-bar-seg"}),_vm._v(" "),_c('div',{staticClass:"or-rg-bar-seg"}),_vm._v(" "),_c('div',{staticClass:"or-rg-bar-mrk",style:({ left: _vm.percentageValue(index) + '%'})})]),_vm._v(" "),(attribute.minLabel || attribute.maxLabel)?_c('div',{staticClass:"or-rg-lbl"},[_c('div',{staticClass:"or-rg-lbl-itm",attrs:{"aria-hidden":"true"}},[_vm._v("\n                        "+_vm._s(attribute.minLabel)+"\n                    ")]),_vm._v(" "),_c('div',{staticClass:"or-rg-lbl-itm",attrs:{"aria-hidden":"true"}},[_vm._v("\n                        "+_vm._s(attribute.maxLabel)+"\n                    ")])]):_vm._e()]):_vm._e(),_vm._v(" "),(attribute.type === 'centered-range')?_c('div',{staticClass:"or-crg"},[_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("Rated "+_vm._s(_vm.scaledValue(index))+" on a scale of minus 2 to 2, where minus 2 is "+_vm._s(attribute.minLabel)+", 0 is "+_vm._s(attribute.midLabel)+" and 2 is "+_vm._s(attribute.maxLabel))]),_vm._v(" "),_c('div',{staticClass:"or-crg-bar"},[_c('span',{staticClass:"or-crg-bar-bg",style:(_vm.percentageValue(index) < 50 
                            ? { left: _vm.percentageValue(index) + '%', right: '50%' }
                            : { left: '50%', right: 100 - _vm.percentageValue(index) + '%' })}),_vm._v(" "),_c('div',{staticClass:"or-crg-bar-seg"}),_vm._v(" "),_c('div',{staticClass:"or-crg-bar-seg"}),_vm._v(" "),_c('div',{staticClass:"or-crg-bar-mid"}),_vm._v(" "),_c('div',{staticClass:"or-crg-bar-mrk",style:({ left: _vm.percentageValue(index) + '%' })})]),_vm._v(" "),_c('div',{staticClass:"or-crg-lbl"},[_c('div',{staticClass:"or-crg-lbl-itm",attrs:{"aria-hidden":"true"}},[_vm._v("\n                        "+_vm._s(attribute.minLabel)+"\n                    ")]),_vm._v(" "),_c('div',{staticClass:"or-crg-lbl-itm",attrs:{"aria-hidden":"true"}},[_vm._v("\n                        "+_vm._s(attribute.midLabel)+"\n                    ")]),_vm._v(" "),_c('div',{staticClass:"or-crg-lbl-itm",attrs:{"aria-hidden":"true"}},[_vm._v("\n                        "+_vm._s(attribute.maxLabel)+"\n                    ")])])]):_vm._e()])])}),0)};
var __vue_staticRenderFns__$7 = [];

  /* style */
  const __vue_inject_styles__$7 = undefined;
  /* scoped */
  const __vue_scope_id__$7 = undefined;
  /* module identifier */
  const __vue_module_identifier__$7 = undefined;
  /* functional template */
  const __vue_is_functional_template__$7 = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoBarAttributes = normalizeComponent(
    { render: __vue_render__$7, staticRenderFns: __vue_staticRenderFns__$7 },
    __vue_inject_styles__$7,
    __vue_script__$7,
    __vue_scope_id__$7,
    __vue_is_functional_template__$7,
    __vue_module_identifier__$7,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$8 = {
  props: {
    media: {
      type: Array,
      required: true
    },
    isOverflow: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  methods: {
    onMediaClick(event, clickedIndex) {
      event.preventDefault();
      this.$emit('mediaClick', {
        mediaItems: this.media,
        reviewId: this.media[clickedIndex].reviewId || undefined,
        //aggregate media has reviewId, review media does not
        initialIndex: clickedIndex
      });
    },

    findMediaUrl(mediaItem, urlType = 'thumbnailUrl') {
      if (mediaItem.type === 'image') {
        return mediaItem[urlType] || mediaItem.imageUrls[urlType];
      } else {
        return mediaItem[urlType] || mediaItem.videoUrls[urlType];
      }
    }

  }
};

/* script */
const __vue_script__$8 = script$8;

/* template */
var __vue_render__$8 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-mediaStrip",class:{ 'is-overflow': _vm.isOverflow }},[_vm._l((_vm.media),function(mediaItem,index){return [(mediaItem.type === 'image')?_c('a',{key:mediaItem.streamId,staticClass:"okeReviews-mediaStrip-item",attrs:{"href":_vm.findMediaUrl(mediaItem, 'largeUrl'),"role":"button"},on:{"click":function($event){return _vm.onMediaClick($event, index)}}},[_c('img',{staticClass:"okeReviews-mediaStrip-item-media",attrs:{"src":_vm.findMediaUrl(mediaItem, 'thumbnailUrl'),"alt":""}})]):_c('a',{key:mediaItem.streamId,staticClass:"okeReviews-mediaStrip-item",attrs:{"href":_vm.findMediaUrl(mediaItem, 'fullSizeUrl'),"role":"button"},on:{"click":function($event){return _vm.onMediaClick($event, index)}}},[_c('div',{staticClass:"okeReviews-mediaStrip-item-media okeReviews-mediaStrip-item-media--video",style:({ backgroundImage: ("url(" + (_vm.findMediaUrl(mediaItem, 'videoPosterUrl')) + ")") }),attrs:{"aria-label":""}})])]})],2)};
var __vue_staticRenderFns__$8 = [];

  /* style */
  const __vue_inject_styles__$8 = function (inject) {
    if (!inject) return
    inject("data-v-1ff0b884_0", { source: ".okeReviews .okeReviews-mediaStrip-item{margin-right:.25em}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$8 = undefined;
  /* module identifier */
  const __vue_module_identifier__$8 = undefined;
  /* functional template */
  const __vue_is_functional_template__$8 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoMediaStrip = normalizeComponent(
    { render: __vue_render__$8, staticRenderFns: __vue_staticRenderFns__$8 },
    __vue_inject_styles__$8,
    __vue_script__$8,
    __vue_scope_id__$8,
    __vue_is_functional_template__$8,
    __vue_module_identifier__$8,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$9 = {
  props: {
    itemId: {
      type: String,
      required: true
    },
    parentId: {
      type: String,
      required: false
    },
    helpfulCount: {
      type: Number,
      required: false,
      default: 0
    },
    unhelpfulCount: {
      type: Number,
      required: false,
      default: 0
    },
    contentType: {
      type: 'review' | 'question' | 'answer',
      required: false,
      default: 'review'
    }
  },

  data() {
    return {
      currentVote: undefined,
      isVoting: false
    };
  },

  computed: {
    helpfulCountDisplay() {
      return this.currentVote === 'upvote' ? this.helpfulCount + 1 : this.helpfulCount;
    },

    unhelpfulCountDisplay() {
      return this.currentVote === 'downvote' ? this.unhelpfulCount + 1 : this.unhelpfulCount;
    },

    helpfulLabelText() {
      return this.contentType === 'question' ? 'I had this question too' : 'Was this helpful?';
    }

  },
  methods: {
    onVote(newVote) {
      this.isVoting = true;
      const voteType = this.getVoteType(this.currentVote, newVote);
      const votePromise = this.getVotePromise(voteType);
      votePromise.then(() => {
        this.currentVote = newVote === this.currentVote ? '' : newVote;
        this.isVoting = false;
      }).catch(() => {
        this.isVoting = false;
      });
    },

    getVoteType(currentVote, newVote) {
      if (!currentVote) {
        return newVote;
      } else if (currentVote === newVote) {
        return `${newVote}-cancel`;
      } else {
        return `swap-to-${newVote}`;
      }
    },

    getVotePromise(voteType) {
      switch (this.contentType) {
        case 'review':
          return OkendoAPI.postHelpfulVoteReview(this.itemId, voteType);

        case 'question':
          return OkendoAPI.postHelpfulVoteQuestion(this.itemId, voteType);

        case 'answer':
          return OkendoAPI.postHelpfulVoteAnswer(this.parentId, this.itemId, voteType);
      }
    }

  }
};

/* script */
const __vue_script__$9 = script$9;

/* template */
var __vue_render__$9 = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-helpful"},[_c('span',{staticClass:"oke-helpful-label"},[_vm._v(_vm._s(_vm.helpfulLabelText))]),_vm._v(" "),_c('div',{staticClass:"oke-helpful-vote"},[_c('button',{staticClass:"oke-helpful-vote-button oke-helpful-vote-button--positive",class:{ 'oke-is-voted': _vm.currentVote === 'upvote', 'is-oke-disabled': _vm.isVoting },attrs:{"disabled":_vm.isVoting,"aria-describedby":("oke-helpful-vote-counter-positive-" + _vm.itemId),"type":"button","aria-label":"Yes"},on:{"click":function($event){return _vm.onVote('upvote')}}},[_c('span',{staticClass:"oke-helpful-vote-counter",attrs:{"id":("oke-helpful-vote-counter-positive-" + _vm.itemId)}},[_vm._v("\n                "+_vm._s(_vm.helpfulCountDisplay)+" "),_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v(_vm._s(_vm.helpfulCountDisplay === 1 ? 'person voted yes' : 'people voted yes'))])])]),_vm._v(" "),_c('button',{staticClass:"oke-helpful-vote-button oke-helpful-vote-button--negative",class:{ 'oke-is-voted': _vm.currentVote === 'downvote', 'is-oke-disabled': _vm.isVoting },attrs:{"disabled":_vm.isVoting,"aria-describedby":("oke-helpful-vote-counter-negative-" + _vm.itemId),"type":"button","aria-label":"No"},on:{"click":function($event){return _vm.onVote('downvote')}}},[_c('span',{staticClass:"oke-helpful-vote-counter",attrs:{"id":("oke-helpful-vote-counter-negative-" + _vm.itemId)}},[_vm._v("\n                "+_vm._s(_vm.unhelpfulCountDisplay)+" "),_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v(_vm._s(_vm.unhelpfulCountDisplay === 1 ? 'person voted no' : 'people voted no'))])])])])])};
var __vue_staticRenderFns__$9 = [];

  /* style */
  const __vue_inject_styles__$9 = function (inject) {
    if (!inject) return
    inject("data-v-5c599b40_0", { source: ".oke-helpful{color:#7c7c7c;display:block;font-size:12px}.oke-helpful-question{vertical-align:middle}.oke-helpful-vote{display:inline-block;margin-left:1em}.oke-helpful-vote-button{background:0 0;border:none;color:#7c7c7c;cursor:pointer;padding:0;transition:opacity .3s ease;vertical-align:middle;width:auto}.oke-helpful-vote-button::before{vertical-align:middle;margin-bottom:.3em}.oke-helpful-vote-button--positive::before{background-image:url(\"data:image/svg+xml,%3Csvg fill='%237c7c7c' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M23,10C23,8.89 22.1,8 21,8H14.68L15.64,3.43C15.66,3.33 15.67,3.22 15.67,3.11C15.67,2.7 15.5,2.32 15.23,2.05L14.17,1L7.59,7.58C7.22,7.95 7,8.45 7,9V19A2,2 0 0,0 9,21H18C18.83,21 19.54,20.5 19.84,19.78L22.86,12.73C22.95,12.5 23,12.26 23,12V10M1,21H5V9H1V21Z' /%3E%3C/svg%3E\");background-repeat:no-repeat;display:inline-block;content:\"\";width:1em;height:1em;background-size:1em 1em}.oke-helpful-vote-button--negative::before{background-image:url(\"data:image/svg+xml,%3Csvg fill='%237c7c7c' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M19,15H23V3H19M15,3H6C5.17,3 4.46,3.5 4.16,4.22L1.14,11.27C1.05,11.5 1,11.74 1,12V14A2,2 0 0,0 3,16H9.31L8.36,20.57C8.34,20.67 8.33,20.77 8.33,20.88C8.33,21.3 8.5,21.67 8.77,21.94L9.83,23L16.41,16.41C16.78,16.05 17,15.55 17,15V5C17,3.89 16.1,3 15,3Z' /%3E%3C/svg%3E\");background-repeat:no-repeat;display:inline-block;content:\"\";width:1em;height:1em;background-size:1em 1em;margin-left:1em}.oke-helpful-vote-button--negative::before,.oke-helpful-vote-button--positive::before{opacity:.5}.oke-helpful-vote-button--negative.oke-is-voted::before,.oke-helpful-vote-button--positive.oke-is-voted::before{color:inherit;opacity:1}.oke-helpful-vote-button.is-oke-disabled{opacity:.3}.oke-helpful-vote-counter{display:inline-block;margin-left:.25em;pointer-events:none}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$9 = undefined;
  /* module identifier */
  const __vue_module_identifier__$9 = undefined;
  /* functional template */
  const __vue_is_functional_template__$9 = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var HelpfulVoting = normalizeComponent(
    { render: __vue_render__$9, staticRenderFns: __vue_staticRenderFns__$9 },
    __vue_inject_styles__$9,
    __vue_script__$9,
    __vue_scope_id__$9,
    __vue_is_functional_template__$9,
    __vue_module_identifier__$9,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$a = {
  props: {
    productName: {
      type: String,
      required: true
    },
    productImageUrl: {
      type: String,
      required: false
    },
    productUrl: {
      type: String,
      required: false
    }
  }
};

/* script */
const __vue_script__$a = script$a;

/* template */
var __vue_render__$a = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-review-reviewer-product"},[(_vm.productImageUrl && _vm.productUrl)?_c('a',{staticClass:"okeReviews-review-reviewer-product-thumbnail",attrs:{"href":_vm.productUrl}},[_c('img',{staticClass:"okeReviews-review-reviewer-product-thumbnail-image",attrs:{"src":_vm.productImageUrl,"alt":_vm.productName}})]):_c('div',{staticClass:"okeReviews-review-reviewer-product-thumbnail"},[_c('img',{staticClass:"okeReviews-review-reviewer-product-thumbnail-image",attrs:{"src":_vm.productImageUrl,"alt":_vm.productName}})]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-reviewer-product-details"},[_c('div',{staticClass:"okeReviews-review-reviewer-product-details-heading"},[_vm._v("\n            Reviewing\n        ")]),_vm._v(" "),(_vm.productUrl)?_c('a',{staticClass:"okeReviews-review-reviewer-product-details-link",attrs:{"href":_vm.productUrl}},[_vm._v("\n            "+_vm._s(_vm.productName)+"\n        ")]):_c('div',[_vm._v("\n            "+_vm._s(_vm.productName)+"\n        ")])])])};
var __vue_staticRenderFns__$a = [];

  /* style */
  const __vue_inject_styles__$a = undefined;
  /* scoped */
  const __vue_scope_id__$a = undefined;
  /* module identifier */
  const __vue_module_identifier__$a = undefined;
  /* functional template */
  const __vue_is_functional_template__$a = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoProduct = normalizeComponent(
    { render: __vue_render__$a, staticRenderFns: __vue_staticRenderFns__$a },
    __vue_inject_styles__$a,
    __vue_script__$a,
    __vue_scope_id__$a,
    __vue_is_functional_template__$a,
    __vue_module_identifier__$a,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$b = {
  props: {
    review: {
      type: Object,
      required: true
    },
    showProductData: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    OkendoCustomerAttributes,
    OkendoProduct,
    OkendoProductAttributes,
    OkendoReviewerProfile,
    OkendoReviewReply,
    OkendoStars,
    OkendoBarAttributes,
    OkendoMediaStrip,
    HelpfulVoting
  },

  data() {
    return {
      formattedDate: '',
      reviewBodyParagraphs: ''
    };
  },

  mounted() {
    this.formattedDate = OkendoUtils.getFormattedDate(this.review.dateCreated);
    this.reviewBodyParagraphs = OkendoUtils.addParagraphsTags(this.review.body);
  },

  methods: {
    openMediaModal(mediaModalMessage) {
      this.$emit('mediaClick', { ...mediaModalMessage,
        reviewId: this.review.reviewId,
        source: 'review'
      });
    }

  }
};

/* script */
const __vue_script__$b = script$b;

/* template */
var __vue_render__$b = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('article',{staticClass:"okeReviews-review"},[_c('aside',{staticClass:"okeReviews-review-side"},[_c('div',{staticClass:"okeReviews-review-side-inner"},[_c('div',{staticClass:"okeReviews-review-reviewer"},[_c('okendo-reviewer-profile',{attrs:{"reviewer":_vm.review.reviewer}}),_vm._v(" "),(_vm.showProductData)?_c('okendo-product',{attrs:{"productName":_vm.review.productName,"productUrl":_vm.review.productUrl,"productImageUrl":_vm.review.productImageUrl}}):_vm._e(),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-recommendation",class:_vm.review.isRecommended ? 'okeReviews-review-recommendation--yes' : 'okeReviews-review-recommendation--no'},[_vm._v("\n                    "+_vm._s(_vm.review.isRecommended ? 'I recommend this product' : 'I don\'t recommend this product')+"\n                ")]),_vm._v(" "),(_vm.review.reviewer.attributes)?_c('okendo-customer-attributes',{attrs:{"attributes":_vm.review.reviewer.attributes}}):_vm._e()],1),_vm._v(" "),(_vm.review.productAttributes)?_c('okendo-product-attributes',{attrs:{"attributes":_vm.review.productAttributes}}):_vm._e()],1)]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-primary"},[_c('div',{staticClass:"okeReviews-review-meta"},[_c('div',{staticClass:"okeReviews-review-starRating"},[_c('okendo-stars',{attrs:{"rating":_vm.review.rating}})],1),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-date"},[_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("Review posted ")]),_c('span',[_vm._v(_vm._s(_vm.formattedDate))])])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-main"},[_c('h2',{staticClass:"okeReviews-review-main-heading"},[_vm._v(_vm._s(_vm.review.title))]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-main-content"},[_c('div',{staticClass:"okeReviews-review-main-content-body",domProps:{"innerHTML":_vm._s(_vm.reviewBodyParagraphs)}})])]),_vm._v(" "),(_vm.review.attributesWithRating)?_c('div',{staticClass:"okeReviews-review-attributeRatings"},[_c('table',{staticClass:"okeReviews-attributeRatingsTable",attrs:{"aria-label":"Product attributes ratings"}},[_c('okendo-bar-attributes',{attrs:{"attributes":_vm.review.attributesWithRating}})],1)]):_vm._e(),_vm._v(" "),(_vm.review.media && _vm.review.media.length)?_c('div',{staticClass:"okeReviews-review-media"},[_c('okendo-media-strip',{attrs:{"media":_vm.review.media},on:{"mediaClick":function($event){return _vm.openMediaModal($event)}}})],1):_vm._e(),_vm._v(" "),_c('helpful-voting',{attrs:{"itemId":_vm.review.reviewId,"helpfulCount":_vm.review.helpfulCount,"unhelpfulCount":_vm.review.unhelpfulCount,"contentType":"review"}}),_vm._v(" "),(_vm.review.reply)?_c('okendo-review-reply',{attrs:{"reply":_vm.review.reply}}):_vm._e()],1)])};
var __vue_staticRenderFns__$b = [];

  /* style */
  const __vue_inject_styles__$b = undefined;
  /* scoped */
  const __vue_scope_id__$b = undefined;
  /* module identifier */
  const __vue_module_identifier__$b = undefined;
  /* functional template */
  const __vue_is_functional_template__$b = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoReview = normalizeComponent(
    { render: __vue_render__$b, staticRenderFns: __vue_staticRenderFns__$b },
    __vue_inject_styles__$b,
    __vue_script__$b,
    __vue_scope_id__$b,
    __vue_is_functional_template__$b,
    __vue_module_identifier__$b,
    false,
    undefined,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
var script$c = {
  props: {
    url: {
      type: String,
      required: true
    }
  }
};

/* script */
const __vue_script__$c = script$c;

/* template */
var __vue_render__$c = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.url)?_c('a',{staticClass:"okeReviews-writeReviewButton okeReviews-button",attrs:{"href":_vm.url,"target":"_blank","rel":"nofollow"}},[_vm._v("\n    Write a Review\n")]):_vm._e()};
var __vue_staticRenderFns__$c = [];

  /* style */
  const __vue_inject_styles__$c = function (inject) {
    if (!inject) return
    inject("data-v-79e22abc_0", { source: ".okeReviews .okeReviews-writeReviewButton{visibility:visible;border:0;padding-left:24px;padding-right:24px;background-color:#f5f5f5;display:inline-block}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$c = undefined;
  /* module identifier */
  const __vue_module_identifier__$c = undefined;
  /* functional template */
  const __vue_is_functional_template__$c = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoWriteReviewButton = normalizeComponent(
    { render: __vue_render__$c, staticRenderFns: __vue_staticRenderFns__$c },
    __vue_inject_styles__$c,
    __vue_script__$c,
    __vue_scope_id__$c,
    __vue_is_functional_template__$c,
    __vue_module_identifier__$c,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$d = {
  props: {
    viewState: {
      type: String,
      required: true
    },
    writeReviewUrl: {
      type: String
    }
  },
  components: {
    OkendoWriteReviewButton
  }
};

/* script */
const __vue_script__$d = script$d;

/* template */
var __vue_render__$d = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-reviewsWidget-stateMessage"},[(_vm.viewState === 'error')?[_vm._v("Whoops, something went wrong 😣")]:_vm._e(),_vm._v(" "),(_vm.viewState === 'loading')?[_vm._v("Loading Reviews...")]:_vm._e(),_vm._v(" "),(_vm.viewState === 'empty')?[_vm._v("\n        No reviews yet"),(_vm.writeReviewUrl)?[_vm._v(", write one now?")]:_vm._e(),_vm._v(" "),(_vm.writeReviewUrl)?_c('div',{staticClass:"okeReviews-reviewsWidget-stateMessage-button"},[_c('okendo-write-review-button',{attrs:{"url":_vm.writeReviewUrl}})],1):_vm._e()]:_vm._e()],2)};
var __vue_staticRenderFns__$d = [];

  /* style */
  const __vue_inject_styles__$d = function (inject) {
    if (!inject) return
    inject("data-v-9780c4be_0", { source: ".okeReviews-reviewsWidget-stateMessage{font-family:inherit;border-top:2px solid #f5f5f5;border-bottom:2px solid #f5f5f5;padding:24px;text-align:center}.okeReviews-reviewsWidget-stateMessage-button{margin-top:8px}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$d = undefined;
  /* module identifier */
  const __vue_module_identifier__$d = undefined;
  /* functional template */
  const __vue_is_functional_template__$d = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoState = normalizeComponent(
    { render: __vue_render__$d, staticRenderFns: __vue_staticRenderFns__$d },
    __vue_inject_styles__$d,
    __vue_script__$d,
    __vue_scope_id__$d,
    __vue_is_functional_template__$d,
    __vue_module_identifier__$d,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$e = {
  props: {
    reviewCountByLevel: {
      type: Object,
      required: true
    },
    reviewCountTotal: {
      type: Number,
      requred: true
    }
  },

  data() {
    return {
      reviewCountData: undefined
    };
  },

  mounted() {
    this.reviewCountData = {
      level5Count: this.reviewCountByLevel.level5Count || 0,
      level4Count: this.reviewCountByLevel.level4Count || 0,
      level3Count: this.reviewCountByLevel.level3Count || 0,
      level2Count: this.reviewCountByLevel.level2Count || 0,
      level1Count: this.reviewCountByLevel.level1Count || 0
    };
  },

  methods: {
    reviewCountPercentage(count) {
      return count / this.reviewCountTotal * 100 + '%';
    },

    transformIndex(index) {
      return 5 - index;
    }

  }
};

/* script */
const __vue_script__$e = script$e;

/* template */
var __vue_render__$e = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-reviewsAggregate-ratingDistribution"},[_c('ul',{staticClass:"okeReviews-ratingDistribution",attrs:{"aria-label":"Rating distribution"}},_vm._l((_vm.reviewCountData),function(count,key,index){return _c('li',{key:key,staticClass:"okeReviews-ratingDistribution-item"},[_c('div',{staticClass:"okeReviews-ratingDistribution-item-label"},[_c('div',{staticClass:"okeReviews-ratingDistribution-item-label-inner"},[_vm._v(_vm._s(_vm.transformIndex(index))+" "+_vm._s(count === 1 ? "Star" : "Stars"))])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-ratingDistribution-item-value"},[_c('div',{staticClass:"okeReviews-ratingDistribution-item-value-inner"},[_c('div',{staticClass:"okeReviews-ratingDistribution-item-value-graph",attrs:{"role":"presentation"}},[_c('div',{staticClass:"okeReviews-barGraph"},[_c('div',{staticClass:"okeReviews-barGraph-layer okeReviews-barGraph-layer--background"}),_vm._v(" "),_c('div',{staticClass:"okeReviews-barGraph-layer okeReviews-barGraph-layer--foreground",style:({ width: _vm.reviewCountPercentage(count) })})])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-ratingDistribution-item-value-count"},[_vm._v("\n                        "+_vm._s(count)+" "),_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v(_vm._s(count === 1 ? "Review" : "Reviews"))])])])])])}),0)])};
var __vue_staticRenderFns__$e = [];

  /* style */
  const __vue_inject_styles__$e = undefined;
  /* scoped */
  const __vue_scope_id__$e = undefined;
  /* module identifier */
  const __vue_module_identifier__$e = undefined;
  /* functional template */
  const __vue_is_functional_template__$e = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoRatingBreakdown = normalizeComponent(
    { render: __vue_render__$e, staticRenderFns: __vue_staticRenderFns__$e },
    __vue_inject_styles__$e,
    __vue_script__$e,
    __vue_scope_id__$e,
    __vue_is_functional_template__$e,
    __vue_module_identifier__$e,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$f = {
  props: {
    aggregate: {
      type: Object,
      required: true
    },
    mediaAggregate: {
      type: Object
    }
  },
  components: {
    OkendoStars,
    OkendoRatingBreakdown,
    OkendoBarAttributes,
    OkendoMediaStrip
  },
  computed: {
    percentageRecommended() {
      return ((this.aggregate.recommendationCount || 0) / this.aggregate.reviewCount * 100).toFixed(0);
    },

    averageRating() {
      return (this.aggregate.ratingAndReviewValuesTotal / this.aggregate.ratingAndReviewCount).toFixed(1);
    },

    ratingsEnabled() {
      return !!this.aggregate.ratingCount;
    },

    hasMedia() {
      return this.mediaAggregate && this.mediaAggregate.media && this.mediaAggregate.media.length;
    }

  },
  methods: {
    openMediaModal(mediaModalMessage) {
      this.$emit('mediaClick', { ...mediaModalMessage,
        source: 'aggregate'
      });
    }

  }
};

/* script */
const __vue_script__$f = script$f;

/* template */
var __vue_render__$f = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _vm._m(0)};
var __vue_staticRenderFns__$f = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-reviewsWidget-reviewsAggregate"},[_c('div',{staticClass:"okeReviews-reviewsAggregate"},[_c('div',{staticClass:"okeReviews-reviewsAggregate-side"},[_c('div',{staticClass:"okeReviews-reviewsAggregate-side-inner"},[_c('div',{staticClass:"okeReviews-reviewsAggregate-summary"},[_c('div',{staticClass:"okeReviews-reviewsAggregate-summary-rating"},[_c('div',{staticClass:"okeReviews-reviewsAggregate-summary-rating-value"},[_c('div',{staticClass:"okeReviews-badge okeReviews-badge--large okeReviews-badge--rating"},[_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("Average Rating")]),_vm._v(" "),_c('span',[_vm._v(_vm._s(_vm.averageRating))]),_vm._v(" "),_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("out of 5")])])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewsAggregate-summary-rating-starRating",attrs:{"role":"presentation"}},[_c('okendo-stars',{attrs:{"rating":_vm.averageRating}})],1)]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewsAggregate-summary-total"},[(_vm.ratingsEnabled)?[_vm._v("\n                            Based on "+_vm._s(_vm.aggregate.ratingAndReviewCount || '0')+" "+_vm._s(_vm.aggregate.ratingAndReviewCount === 1 ? 'Rating' : 'Ratings')+"\n                        ")]:[_vm._v("\n                            Based on "+_vm._s(_vm.aggregate.ratingAndReviewCount || '0')+" "+_vm._s(_vm.aggregate.ratingAndReviewCount === 1 ? 'Review' : 'Reviews')+"\n                        ")]],2)]),_vm._v(" "),(_vm.aggregate.attributeRatings || _vm.hasMedia)?_c('okendo-rating-breakdown',{attrs:{"reviewCountTotal":_vm.aggregate.ratingAndReviewCount,"reviewCountByLevel":_vm.aggregate.ratingAndReviewCountByLevel}}):_vm._e()],1)]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewsAggregate-primary"},[(_vm.percentageRecommended)?_c('div',{staticClass:"okeReviews-reviewsAggregate-recommends"},[_c('span',{staticClass:"okeReviews-reviewsAggregate-recommends-badge okeReviews-badge okeReviews-badge--recommended"},[_vm._v(_vm._s(_vm.percentageRecommended)+"%")]),_vm._v(" "),_c('span',[_vm._v(_vm._s(_vm.aggregate.productName ? 'of reviewers would recommend this product to a friend' :  'of reviewers would recommend these products to a friend'))])]):_vm._e(),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewsAggregate-mod"},[(_vm.aggregate.attributeRatings)?_c('div',{staticClass:"okeReviews-reviewsAggregate-mod-itm okeReviews-reviewsAggregate-mod-itm--attRtgAv"},[_c('table',{staticClass:"okeReviews-attributeRatingsTable",attrs:{"aria-label":"Product attribute rating averages"}},[_c('okendo-bar-attributes',{attrs:{"attributes":_vm.aggregate.attributeRatings}})],1)]):_vm._e(),_vm._v(" "),(_vm.hasMedia)?_c('div',{staticClass:"okeReviews-reviewsAggregate-mod-itm okeReviews-reviewsAggregate-mod-itm--medAgg"},[_c('div',{staticClass:"okeReviews-mediaAggregate"},[_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("Customer photos and videos")]),_vm._v(" "),_c('okendo-media-strip',{attrs:{"media":_vm.mediaAggregate.media,"isOverflow":!!_vm.mediaAggregate.nextUrl},on:{"mediaClick":function($event){return _vm.openMediaModal($event)}}})],1)]):_vm._e()])])])])}];

  /* style */
  const __vue_inject_styles__$f = undefined;
  /* scoped */
  const __vue_scope_id__$f = undefined;
  /* module identifier */
  const __vue_module_identifier__$f = undefined;
  /* functional template */
  const __vue_is_functional_template__$f = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoAggregate = normalizeComponent(
    { render: __vue_render__$f, staticRenderFns: __vue_staticRenderFns__$f },
    __vue_inject_styles__$f,
    __vue_script__$f,
    __vue_scope_id__$f,
    __vue_is_functional_template__$f,
    __vue_module_identifier__$f,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$g = {
  props: {
    review: {
      type: Object,
      required: true
    },
    showProductData: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  components: {
    OkendoReviewerProfile,
    OkendoStars,
    HelpfulVoting,
    OkendoProduct
  },

  data() {
    return {
      formattedDate: '',
      reviewBodyParagraphs: ''
    };
  },

  mounted() {
    this.formattedDate = OkendoUtils.getFormattedDate(this.review.dateCreated);
    this.reviewBodyParagraphs = OkendoUtils.addParagraphsTags(this.review.body);
  }

};

/* script */
const __vue_script__$g = script$g;

/* template */
var __vue_render__$g = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-review okeReviews-review--simple"},[_c('okendo-reviewer-profile',{attrs:{"reviewer":_vm.review.reviewer}}),_vm._v(" "),(_vm.showProductData)?_c('okendo-product',{attrs:{"productName":_vm.review.productName,"productUrl":_vm.review.productUrl,"productImageUrl":_vm.review.productImageUrl}}):_vm._e(),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-meta"},[_c('div',{staticClass:"okeReviews-review-date"},[_c('span',{staticClass:"okeReviews-a11yText"},[_vm._v("Review posted ")]),_c('span',[_vm._v(_vm._s(_vm.formattedDate))])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-starRating"},[_c('okendo-stars',{attrs:{"rating":_vm.review.rating}})],1)]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-main"},[_c('h2',{staticClass:"okeReviews-review-main-heading"},[_vm._v(_vm._s(_vm.review.title))]),_vm._v(" "),_c('div',{staticClass:"okeReviews-review-main-content",domProps:{"innerHTML":_vm._s(_vm.reviewBodyParagraphs)}})]),_vm._v(" "),_c('helpful-voting',{attrs:{"itemId":_vm.review.reviewId,"helpfulCount":_vm.review.helpfulCount,"unhelpfulCount":_vm.review.unhelpfulCount,"contentType":"review"}})],1)};
var __vue_staticRenderFns__$g = [];

  /* style */
  const __vue_inject_styles__$g = undefined;
  /* scoped */
  const __vue_scope_id__$g = undefined;
  /* module identifier */
  const __vue_module_identifier__$g = undefined;
  /* functional template */
  const __vue_is_functional_template__$g = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoReviewSimple = normalizeComponent(
    { render: __vue_render__$g, staticRenderFns: __vue_staticRenderFns__$g },
    __vue_inject_styles__$g,
    __vue_script__$g,
    __vue_scope_id__$g,
    __vue_is_functional_template__$g,
    __vue_module_identifier__$g,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$h = {
  components: {
    OkendoReviewSimple
  },
  props: {
    shopifyProductId: {
      type: String,
      required: false
    },
    mediaItems: {
      type: Array,
      required: false
    },
    initialReviewId: {
      type: String,
      required: false
    },
    initialIndex: {
      type: Number,
      default: 0,
      required: false
    },
    mode: {
      type: String,
      required: true
    },
    isAllReviews: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data() {
    return {
      isMediaLoading: true,
      isLoadingMore: false,
      isError: false,
      media: [],
      nextUrl: '',
      imagesPerPage: 25,
      loadMoreThreshold: 8,
      review: undefined,
      reviewId: ''
    };
  },

  mounted() {
    this.loadMediaGalleryData();
  },

  methods: {
    loadMediaGalleryData() {
      this.reviewId = this.initialReviewId;
      this.loadReview();

      if (this.mode === 'aggregate') {
        const mediaAggregatePromise = this.isAllReviews ? OkendoAPI.getMediaAll(this.imagesPerPage) : OkendoAPI.getMediaProduct(this.shopifyProductId, this.imagesPerPage);
        mediaAggregatePromise.then(mediaResponse => {
          this.media = this.remapMedia(mediaResponse.data.media);
          this.nextUrl = mediaResponse.data.nextUrl;
          setTimeout(() => this.initFlickity(), 0);
          this.isMediaLoading = false;
        }).catch(() => {
          this.isMediaLoading = false;
          this.isError = true;
        });
      } else {
        this.media = this.remapMedia(this.mediaItems);
        this.isMediaLoading = false;
        setTimeout(() => this.initFlickity(), 0);
      }
    },

    loadReview() {
      OkendoAPI.getReview(this.reviewId).then(reviewResponse => {
        this.review = reviewResponse.data.review;
      });
    },

    remapMedia(media, append = false) {
      if (this.mode === 'aggregate') {
        return media.map(medium => ({
          url: medium.type === 'image' ? medium.imageUrls.largeUrl : medium.videoUrls.fullSizeUrl,
          type: medium.type,
          thumbnailUrl: medium.type === 'image' ? medium.imageUrls.thumbnailUrl : medium.videoUrls.thumbnailUrl,
          videoPosterURL: medium.type === 'image' ? undefined : medium.videoUrls.videoPosterUrl,
          streamId: medium.streamId,
          reviewId: medium.reviewId,
          append
        }));
      } else {
        return media.map(medium => ({
          url: medium.type === 'image' ? medium.largeUrl : medium.fullSizeUrl,
          type: medium.type,
          thumbnailUrl: medium.thumbnailUrl,
          videoPosterURL: medium.type === 'image' ? undefined : medium.thumbnailUrl,
          reviewId: this.reviewId,
          append
        }));
      }
    },

    initFlickity() {
      const mediaGallerySliderElement = this.$refs.slider;
      const mediaGallerySliderNavElement = this.$refs.sliderNav;
      const mediaGallerySlider = new Flickity(mediaGallerySliderElement, {
        initialIndex: this.initialIndex,
        accessibility: true,
        prevNextButtons: this.media.length > 1,
        pageDots: false,
        wrapAround: this.mode !== 'aggregate'
      });

      if (this.mode === 'aggregate') {
        const mediaGallerySliderNav = new Flickity(mediaGallerySliderNavElement, {
          asNavFor: mediaGallerySliderElement,
          accessibility: false,
          pageDots: false,
          prevNextButtons: this.media.length > 1
        });
        mediaGallerySlider.on('change', () => {
          const newReviewId = this.media[mediaGallerySliderNav.selectedIndex].reviewId;

          if (newReviewId !== this.reviewId) {
            this.review = undefined;
            this.reviewId = newReviewId;
            this.loadReview();
          }
        });
        mediaGallerySliderNav.on('settle', () => {
          if (this.media && this.nextUrl && mediaGallerySliderNav.cells.length - mediaGallerySliderNav.selectedIndex <= this.loadMoreThreshold && !this.isLoadingMore) {
            this.isLoadingMore = true;
            OkendoAPI.getMore(this.nextUrl).then(response => {
              this.media = this.media.concat(this.remapMedia(response.data.media, true));
              this.nextUrl = response.data.nextUrl;
              setTimeout(() => {
                mediaGallerySliderNav.append(this.$refs.navSlideToAppend);
                mediaGallerySlider.append(this.$refs.mainSlideToAppend);
                this.cleanseMediaOfAppends();
              }, 0);
              this.isLoadingMore = false;
            }).catch(() => this.isLoadingMore = false);
          }
        });
      }

      mediaGallerySliderElement.focus();
    },

    cleanseMediaOfAppends() {
      this.media.map(mediaItem => mediaItem.append = false);
    },

    onClose(event) {
      this.$emit('close');
    }

  }
};

/* script */
const __vue_script__$h = script$h;

/* template */
var __vue_render__$h = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews okeReviews--theme"},[_c('div',{staticClass:"okeReviews-modal"},[_c('div',{staticClass:"okeReviews-modal-overlay",attrs:{"tabindex":"-1"}}),_vm._v(" "),_c('div',{staticClass:"okeReviews-modal-inner",on:{"click":function($event){return _vm.onClose()}}},[_c('div',{staticClass:"okeReviews-modal-dialog",attrs:{"role":"dialog"}},[_c('div',{attrs:{"role":"document"}},[_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog",on:{"click":function($event){$event.stopPropagation();}}},[(_vm.isMediaLoading)?_c('span',{staticClass:"okeReviews-a11yText",attrs:{"role":"alertdialog","aria-busy":"true"}},[_vm._v("Loading...")]):_vm._e(),_vm._v(" "),_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-main"},[_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-slider"},[_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-slider-inner"},[_c('div',{ref:"slider",staticClass:"okeReviews-mediaSlider",class:{ 'is-loading': _vm.isMediaLoading }},_vm._l((_vm.media),function(mediaItem){return _c('div',{key:mediaItem.thumbnailUrl,ref:mediaItem.append ? 'mainSlideToAppend' : undefined,refInFor:true,staticClass:"okeReviews-mediaSlider-slide"},[(mediaItem.type === 'image')?_c('img',{staticClass:"okeReviews-mediaSlider-slide-media",attrs:{"src":mediaItem.url,"alt":"Customer {{ mediaItem.type }}"}}):_vm._e(),_vm._v(" "),(mediaItem.type === 'video')?_c('video',{staticClass:"okeReviews-mediaSlider-slide-media okeReviews-mediaSlider-slide-media--video",attrs:{"src":mediaItem.url,"poster":mediaItem.videoPosterURL,"width":"100%","controls":""}},[_c('source',{attrs:{"src":mediaItem.url,"type":"video/mp4"}}),_vm._v("\n                                                Your browser doesn't support HTML5 videos.\n                                            ")]):_vm._e()])}),0)])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-review"},[_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-review-inner",class:{ 'is-loading': !_vm.review }},[(_vm.review)?_c('okendo-review-simple',{attrs:{"review":_vm.review,"showProductData":_vm.isAllReviews}}):_vm._e()],1)])]),_vm._v(" "),(!_vm.isMediaLoading && _vm.mode === 'aggregate')?_c('div',{ref:"sliderNav",staticClass:"okeReviews-mediaGalleryReviewDialog-nav",class:{ 'is-loading': _vm.isLoading, 'is-loadingMore': _vm.isLoadingMore }},_vm._l((_vm.media),function(mediaItem){return _c('div',{key:mediaItem.thumbnailUrl,ref:mediaItem.append ? 'navSlideToAppend' : undefined,refInFor:true,staticClass:"okeReviews-mediaGalleryReviewDialog-nav-slide"},[(mediaItem.type === 'image')?_c('img',{staticClass:"okeReviews-mediaGalleryReviewDialog-nav-slide-media",attrs:{"src":mediaItem.thumbnailUrl}}):_vm._e(),_vm._v(" "),(mediaItem.type === 'video')?_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-nav-slide-media",style:({ 'background-image': ("url('" + (mediaItem.thumbnailUrl) + "')") })},[_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-nav-slide-media-playOverlay"})]):_vm._e()])}),0):_vm._e(),_vm._v(" "),(_vm.isError)?_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-errorOverlay"},[_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-errorOverlay-inner"},[_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-errorOverlay-img"}),_vm._v(" "),_c('div',{staticClass:"okeReviews-mediaGalleryReviewDialog-errorOverlay-msg"}),_vm._v(" "),_c('button',{staticClass:"okeReviews-button",on:{"click":function($event){return _vm.loadMediaGalleryData()}}},[_vm._v("Retry")])])]):_vm._e()])]),_vm._v(" "),_c('button',{staticClass:"okeReviews-modal-dialog-close",attrs:{"type":"button","aria-label":"close this modal window"},on:{"click":function($event){return _vm.onClose()}}})])])])])};
var __vue_staticRenderFns__$h = [];

  /* style */
  const __vue_inject_styles__$h = function (inject) {
    if (!inject) return
    inject("data-v-54f08ec4_0", { source: ".okeReviews .okeReviews-mediaGalleryReviewDialog-nav{position:relative;height:96px;overflow:hidden}.okeReviews .okeReviews-mediaGalleryReviewDialog-slider-inner{max-height:690px}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$h = undefined;
  /* module identifier */
  const __vue_module_identifier__$h = undefined;
  /* functional template */
  const __vue_is_functional_template__$h = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoMediaGallery = normalizeComponent(
    { render: __vue_render__$h, staticRenderFns: __vue_staticRenderFns__$h },
    __vue_inject_styles__$h,
    __vue_script__$h,
    __vue_scope_id__$h,
    __vue_is_functional_template__$h,
    __vue_module_identifier__$h,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$i = {
  props: {
    productId: {
      type: String,
      require: true
    },
    isOpen: {
      type: Boolean,
      require: true,
      default: false
    }
  },
  watch: {
    isOpen: function (isOpen) {
      this.updateFilterContentHeight(isOpen);
    }
  },

  data() {
    return {
      shopifyProductId: '',
      filterContentHeight: 0,
      filters: []
    };
  },

  mounted() {
    this.shopifyProductId = OkendoAPI.transformProductId(this.productId);

    if (this.shopifyProductId) {
      this.loadFilters();
    }
  },

  methods: {
    async loadFilters() {
      const filtersResponse = await OkendoAPI.getFiltersProduct(this.shopifyProductId);
      const {
        productAttributes
      } = filtersResponse.data;

      if (productAttributes && productAttributes.length) {
        this.filters = productAttributes.reduce((acc, filter) => {
          acc.push({ ...filter,
            options: filter.options.map(option => {
              return {
                name: option,
                isSelected: false
              };
            })
          });
          return acc;
        }, []);
      } // Wait for dom to catch up before setting the height


      this.$nextTick(() => this.updateFilterContentHeight(this.isOpen));
    },

    onFilterChange() {
      const filtersWithSelectedOptions = this.filters.reduce((acc, filter) => {
        const selectedFilterOptions = filter.options.reduce((acc, option) => {
          if (option.isSelected) {
            acc.push(option.name);
          }

          return acc;
        }, []);

        if (selectedFilterOptions.length) {
          acc.push({ ...filter,
            options: selectedFilterOptions
          });
        }

        return acc;
      }, []);
      this.$emit('change', filtersWithSelectedOptions);
    },

    onFilterClear(filter) {
      filter.options.forEach(option => {
        option.isSelected = false;
      });
      this.onFilterChange();
    },

    isFilterActive(filter) {
      return filter.options.some(option => option.isSelected);
    },

    updateFilterContentHeight(isOpen) {
      this.filterContentHeight = isOpen ? this.$refs.filtersContainer.scrollHeight : 0;
    }

  }
};

/* script */
const __vue_script__$i = script$i;

/* template */
var __vue_render__$i = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"filtersContainer",staticClass:"okeReviews-filters-content",style:({ height: (_vm.filterContentHeight + "px") }),attrs:{"aria-hidden":!_vm.isOpen,"id":"filters"}},_vm._l((_vm.filters),function(filter){return _c('div',{key:filter.title,staticClass:"okeReviews-filter"},[_c('div',{staticClass:"okeReviews-filter-title"},[_vm._v("\n            "+_vm._s(filter.title)+"\n            "),(_vm.isFilterActive(filter))?_c('button',{staticClass:"okeReviews-filter-clear",attrs:{"tabindex":_vm.isOpen ? 0 : -1},on:{"click":function($event){return _vm.onFilterClear(filter)}}},[_vm._v("\n                Clear\n            ")]):_vm._e()]),_vm._v(" "),_c('div',{staticClass:"okeReviews-filter-list"},_vm._l((filter.options),function(option){return _c('div',{key:option.name,staticClass:"okeReviews-filter-list-item"},[_c('legend',{staticClass:"okeReviews-a11yText"},[_vm._v(_vm._s(filter.title))]),_vm._v(" "),_c('input',{directives:[{name:"model",rawName:"v-model",value:(option.isSelected),expression:"option.isSelected"}],staticClass:"okeReviews-filter-list-item-checkbox okeReviews-a11yText",attrs:{"id":((filter.title) + "-" + (option.name)),"tabindex":_vm.isOpen ? 0 : -1,"type":"checkbox"},domProps:{"value":option.name,"checked":Array.isArray(option.isSelected)?_vm._i(option.isSelected,option.name)>-1:(option.isSelected)},on:{"change":[function($event){var $$a=option.isSelected,$$el=$event.target,$$c=$$el.checked?(true):(false);if(Array.isArray($$a)){var $$v=option.name,$$i=_vm._i($$a,$$v);if($$el.checked){$$i<0&&(_vm.$set(option, "isSelected", $$a.concat([$$v])));}else{$$i>-1&&(_vm.$set(option, "isSelected", $$a.slice(0,$$i).concat($$a.slice($$i+1))));}}else{_vm.$set(option, "isSelected", $$c);}},function($event){return _vm.onFilterChange()}]}}),_vm._v(" "),_c('label',{staticClass:"okeReviews-filter-list-item-label",attrs:{"for":((filter.title) + "-" + (option.name))}},[_vm._v("\n                    "+_vm._s(option.name)+"\n                ")])])}),0)])}),0)};
var __vue_staticRenderFns__$i = [];

  /* style */
  const __vue_inject_styles__$i = undefined;
  /* scoped */
  const __vue_scope_id__$i = undefined;
  /* module identifier */
  const __vue_module_identifier__$i = undefined;
  /* functional template */
  const __vue_is_functional_template__$i = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoFilters = normalizeComponent(
    { render: __vue_render__$i, staticRenderFns: __vue_staticRenderFns__$i },
    __vue_inject_styles__$i,
    __vue_script__$i,
    __vue_scope_id__$i,
    __vue_is_functional_template__$i,
    __vue_module_identifier__$i,
    false,
    undefined,
    undefined,
    undefined
  );

//
// Local dev/serve environment
// Local storage for helpful voting
// --- Skipping for now ---
// Template support (minimal template etc.)
// Foursixty integration
// GA
// Translation

var script$j = {
  props: {
    productId: {
      type: String,
      default: ''
    },
    sizeOverride: {
      type: String
    }
  },
  components: {
    OkendoReview,
    OkendoState,
    OkendoAggregate,
    OkendoMediaGallery,
    OkendoWriteReviewButton,
    OkendoFilters
  },

  data() {
    return {
      apiStoreBaseUrl: '',
      shopifyProductId: '',
      writeReviewUrl: '',
      nextUrl: '',
      reviews: [],
      aggregate: undefined,
      mediaAggregate: undefined,
      viewState: 'loading',
      isLoading: false,
      isLoadingMore: false,
      size: 'small',
      isMediaModalOpen: false,
      mediaModalConfig: {},
      sortOrder: '',
      filtersEnabled: false,
      areFiltersOpen: false,
      activeFilters: []
    };
  },

  mounted() {
    if (!this.sizeOverride) {
      this.addResizeListener();
    }

    this.isAllReviews = !this.productId;
    this.shopifyProductId = OkendoAPI.transformProductId(this.productId);
    this.writeReviewUrl = this.getWriteReviewUrl();
    this.filtersEnabled = widgetSettings.enableFilters && this.productId;
    this.sortOrder = widgetSettings.defaultReviewsSortOrder;
    this.loadReviews();
  },

  destroyed() {
    this.removeResizeListener();
  },

  computed: {
    widgetSize() {
      return this.sizeOverride || this.size;
    }

  },
  methods: {
    getReviewsPromise() {
      return this.isAllReviews ? OkendoAPI.getReviewsAll(undefined, this.sortOrder) : OkendoAPI.getReviewsProduct(this.shopifyProductId, undefined, this.sortOrder, this.activeFilters);
    },

    getAggregatePromise() {
      return this.isAllReviews ? OkendoAPI.getReviewAggregateAll() : OkendoAPI.getReviewAggregateProduct(this.shopifyProductId);
    },

    getMediaPromise() {
      return this.isAllReviews ? OkendoAPI.getMediaAll() : OkendoAPI.getMediaProduct(this.shopifyProductId);
    },

    getWriteReviewUrl() {
      return this.isAllReviews ? '' : OkendoAPI.writeReviewUrl(this.shopifyProductId, widgetSettings.enhancedRecorder);
    },

    async loadReviews() {
      const reviewsPromise = this.getReviewsPromise();
      const aggregatePromise = this.getAggregatePromise();
      const mediaAggregatePromise = this.getMediaPromise();
      Promise.all([reviewsPromise, aggregatePromise, mediaAggregatePromise]).then(([reviewsResponse, aggregateResponse, mediaResponse]) => {
        if (reviewsResponse && aggregateResponse && mediaResponse) {
          this.aggregate = aggregateResponse.data.reviewAggregate;
          this.mediaAggregate = mediaResponse.data;
          const {
            nextUrl,
            reviews,
            areReviewsGrouped
          } = reviewsResponse.data;
          this.isGroupedReviews = areReviewsGrouped;
          this.nextUrl = nextUrl;
          this.reviews = reviews;
          this.viewState = reviews.length ? 'content' : 'empty';
        } else {
          this.viewState = 'error';
        }
      }).catch(() => {
        this.viewState = 'error';
      });
    },

    loadFilteredReviews() {
      this.isLoading = true;
      this.getReviewsPromise().then(reviewsResponse => {
        if (reviewsResponse) {
          const {
            nextUrl,
            reviews
          } = reviewsResponse.data;
          this.nextUrl = nextUrl;
          this.reviews = reviews;
        }

        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
        this.viewState = 'error';
      });
    },

    loadMoreReviews() {
      if (!this.isLoadingMore) {
        this.isLoadingMore = true;
        OkendoAPI.getMore(this.nextUrl).then(response => {
          if (response && response.data) {
            const {
              nextUrl,
              reviews
            } = response.data;
            this.nextUrl = nextUrl;
            this.reviews.push(...reviews);
          }

          this.isLoadingMore = false;
        }).catch(() => {
          this.isLoadingMore = false;
        });
      }
    },

    onSortChange() {
      this.isLoading = true;
      this.getReviewsPromise().then(reviewsResponse => {
        const {
          nextUrl,
          reviews
        } = reviewsResponse.data;
        this.reviews = reviews;
        this.nextUrl = nextUrl;
        this.isLoading = false;
      }).catch(() => {
        this.isLoading = false;
        this.viewState = 'error';
      });
    },

    onFiltersToggle() {
      this.areFiltersOpen = !this.areFiltersOpen;
    },

    onFiltersChange: OkendoUtils.debounce(function (newFilters) {
      this.activeFilters = newFilters;
      this.loadFilteredReviews();
    }, 500),

    addResizeListener() {
      this.setWidgetSize();
      window.addEventListener('resize', this.setWidgetSize, {
        passive: true
      });
    },

    removeResizeListener() {
      window.removeEventListener('resize', this.setWidgetSize);
    },

    setWidgetSize() {
      const widgetWidth = this.$refs.widget && this.$refs.widget.clientWidth;

      if (widgetWidth > 960) {
        this.size = 'large';
      } else if (widgetWidth > 640) {
        this.size = 'medium';
      } else {
        this.size = 'small';
      }
    },

    openMediaModal(mediaModalMessage) {
      this.mediaModalConfig = mediaModalMessage;
      this.isMediaModalOpen = true;
    }

  }
};

/* script */
const __vue_script__$j = script$j;

/* template */
var __vue_render__$j = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews okeReviews--theme"},[_c('div',{ref:"widget",staticClass:"okeReviews-reviewsWidget js-okeReviews-reviewsWidget",class:_vm.widgetSize !== 'small' ? ("is-okeReviews-reviewsWidget-" + _vm.widgetSize) : ''},[(_vm.viewState === 'content' || _vm.activeFilters.length)?[_c('div',{staticClass:"okeReviews-reviewsWidget-header"},[_vm._m(0),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewsWidget-header-controls"},[_c('okendo-write-review-button',{attrs:{"url":_vm.writeReviewUrl}})],1)]),_vm._v(" "),_c('okendo-aggregate',{attrs:{"aggregate":_vm.aggregate,"mediaAggregate":_vm.mediaAggregate},on:{"mediaClick":function($event){return _vm.openMediaModal($event)}}}),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviewsWidget-reviews"},[_c('div',{staticClass:"okeReviews-reviews"},[_c('div',{staticClass:"okeReviews-reviews-controls"},[_c('div',{staticClass:"okeReviews-reviews-controls-filtersToggle"},[_c('button',{staticClass:"okeReviews-filterToggle okeReviews-button",class:{ 'is-okeReviews-hidden': !_vm.filtersEnabled },attrs:{"aria-expanded":_vm.areFiltersOpen,"id":"filter-toggle","type":"button"},on:{"click":function($event){return _vm.onFiltersToggle()}}},[_vm._v("\n                                Filters\n                                "),(!_vm.areFiltersOpen && _vm.activeFilters.length)?_c('span',{staticClass:"okeReviews-filterToggle-count"},[_vm._v(_vm._s(_vm.activeFilters.length))]):_vm._e()])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviews-controls-sort"},[_c('div',{staticClass:"okeReviews-reviews-controls-reviewCount"},[_vm._v("\n                                "+_vm._s(_vm.aggregate.reviewCount || '0')+" "+_vm._s(_vm.aggregate.reviewCount === 1 ? 'Review' : 'Reviews')+"\n                            ")]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviews-controls-select"},[_c('span',{staticClass:"okeReviews-select"},[_c('label',{staticClass:"okeReviews-a11yText",attrs:{"for":"okeReviews-reviews-controls-sort"}},[_vm._v("Sort By")]),_vm._v(" "),_c('select',{directives:[{name:"model",rawName:"v-model",value:(_vm.sortOrder),expression:"sortOrder"}],staticClass:"okeReviews-select-input",attrs:{"id":"okeReviews-reviews-controls-sort"},on:{"change":[function($event){var $$selectedVal = Array.prototype.filter.call($event.target.options,function(o){return o.selected}).map(function(o){var val = "_value" in o ? o._value : o.value;return val}); _vm.sortOrder=$event.target.multiple ? $$selectedVal : $$selectedVal[0];},function($event){return _vm.onSortChange()}]}},[_c('option',{attrs:{"value":"date desc"}},[_vm._v("Most Recent")]),_vm._v(" "),_c('option',{attrs:{"value":"date asc"}},[_vm._v("Oldest")]),_vm._v(" "),_c('option',{attrs:{"value":"has_media desc"}},[_vm._v("Photos & Videos")]),_vm._v(" "),_c('option',{attrs:{"value":"rating desc"}},[_vm._v("Highest Rating")]),_vm._v(" "),_c('option',{attrs:{"value":"rating asc"}},[_vm._v("Lowest Rating")]),_vm._v(" "),_c('option',{attrs:{"value":"helpful desc"}},[_vm._v("Most Helpful")]),_vm._v(" "),_c('option',{attrs:{"value":"unhelpful desc"}},[_vm._v("Least Helpful")])])])])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-reviews-controls-filters"},[_c('div',{staticClass:"okeReviews-filters",class:{ 'is-okeReviews-hidden': !_vm.filtersEnabled },attrs:{"aria-hidden":!_vm.filtersEnabled && !_vm.areFiltersOpen,"id":"filter-options","aria-labelledby":"filter-toggle"}},[(_vm.filtersEnabled)?_c('okendo-filters',{attrs:{"isOpen":_vm.areFiltersOpen,"productId":_vm.productId},on:{"change":function($event){return _vm.onFiltersChange($event)}}}):_vm._e()],1)])]),_vm._v(" "),_c('div',{staticClass:"okeReviews-alert okeReviews-alert--top",class:{ 'is-okeReviews-visible' : _vm.isLoading },attrs:{"aria-hidden":_vm.isLoading ? 'false' : 'true',"role":_vm.isLoading ? 'alert' : 'none'}},[_c('div',{staticClass:"okeReviews-reviews-alert-inner okeReviews-alert-inner"},[_vm._v("\n                            Loading Reviews...\n                        ")])]),_vm._v(" "),(_vm.reviews.length)?_c('div',{staticClass:"okeReviews-reviews-main"},_vm._l((_vm.reviews),function(review){return _c('div',{key:review.reviewId,staticClass:"okeReviews-reviews-review"},[_c('okendo-review',{attrs:{"review":review,"showProductData":_vm.isGroupedReviews || _vm.isAllReviews},on:{"mediaClick":function($event){return _vm.openMediaModal($event)}}})],1)}),0):(_vm.activeFilters.length)?_c('div',{staticClass:"okeReviews-reviewsWidget-stateMessage"},[_vm._v("\n                        No Reviews Found\n                    ")]):_vm._e()])]),_vm._v(" "),(_vm.nextUrl)?_c('div',{staticClass:"okeReviews-reviews-footer"},[_c('button',{staticClass:"okeReviews-showMore",class:{ 'is-okeReviews-loading': _vm.isLoadingMore },attrs:{"disabled":_vm.isLoadingMore},on:{"click":function($event){return _vm.loadMoreReviews()}}},[_vm._v("\n                    "+_vm._s(_vm.isLoadingMore ? 'Loading...' : 'Show more')+"\n                ")])]):_vm._e()]:_vm._e(),_vm._v(" "),(_vm.viewState !== 'content')?_c('okendo-state',{attrs:{"viewState":_vm.viewState,"writeReviewUrl":_vm.writeReviewUrl}}):_vm._e()],2),_vm._v(" "),(_vm.isMediaModalOpen)?_c('okendo-media-gallery',{attrs:{"initialIndex":_vm.mediaModalConfig.initialIndex,"mediaItems":_vm.mediaModalConfig.mediaItems,"initialReviewId":_vm.mediaModalConfig.reviewId,"shopifyProductId":_vm.shopifyProductId,"mode":_vm.mediaModalConfig.source,"isAllReviews":_vm.isAllReviews},on:{"close":function($event){_vm.isMediaModalOpen = false;}}}):_vm._e()],1)};
var __vue_staticRenderFns__$j = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews-reviewsWidget-header-poweredBy"},[_c('div',{staticClass:"okeReviews-reviewsWidget-poweredBy"},[_c('a',{staticClass:"okeReviews-poweredBy",attrs:{"href":"https://www.okendo.io","target":"_blank","aria-label":"Powered by Okendo"}})])])}];

  /* style */
  const __vue_inject_styles__$j = function (inject) {
    if (!inject) return
    inject("data-v-22c5bd85_0", { source: ".okeReviews .okeReviews-reviewsWidget{padding-top:0}.okeReviews .okeReviews-reviewsWidget-poweredBy{display:block}.okeReviews .okeReviews-reviewsWidget-header{display:flex;align-items:center}.okeReviews .modal-wrapper{border:0}.okeReviews .okeReviews-showMore{margin-top:-2px}.okeReviews .okeReviews-showMore::after{display:none}.okeReviews .okeReviews-showMore.is-okeReviews-loading::after{display:inline-block;animation:spin 1s linear infinite;border:2px solid;border-color:currentColor currentColor transparent transparent;border-radius:50%;height:10px;width:10px}.okeReviews .okeReviews-review-primary,.okeReviews .okeReviews-reviewsAggregate-primary{border:2px solid #f5f5f5!important}.okeReviews .okeReviews-review-side,.okeReviews .okeReviews-reviewsAggregate-side{border:0!important;background-color:#f5f5f5}.okeReviews .okeReviews-mediaStrip-item-media{border:2px solid #efefef}.okeReviews .okeReviews-showMore{border:0;padding-left:24px;padding-right:24px;background-color:#f5f5f5}.okeReviews .okeReviews-showMore{position:relative}.okeReviews .okeReviews-reviews-footer::before{height:2px;background:#f5f5f5;top:-2px}.okeReviews .okeReviews-review-reviewer-profile{border-bottom:2px solid #efefef}.okeReviews .okeReviews-review-reviewer-profile-details{vertical-align:middle;padding-top:0}.okeReviews .okeReviews-avatar{background-color:#fff;width:50px;height:50px}.okeReviews .okeReviews-avatar-placeholder{font-size:1em}.okeReviews .okeReviews-avatar::after{background-size:15px;width:15px;height:15px}.okeReviews .okeReviews-review--simple .okeReviews-avatar{background-color:#efefef}.okeReviews .okeReviews-helpful-vote-button,.okeReviews .okeReviews-review-recommendation,.okeReviews .okeReviews-review-reviewer-profile-status{margin-top:0;vertical-align:baseline}.okeReviews .okeReviews-helpful-vote-button::before,.okeReviews .okeReviews-review-recommendation::before,.okeReviews .okeReviews-review-reviewer-profile-status::before{margin-top:-.4em;margin-right:.1em;font-size:1em}.okeReviews .okeReviews-select,.okeReviews .okeReviews-select::before{border-color:#f5f5f5;border-width:2px}.okeReviews .okeReviews-reviewsWidget .oke-helpful{text-align:right}@keyframes spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$j = undefined;
  /* module identifier */
  const __vue_module_identifier__$j = undefined;
  /* functional template */
  const __vue_is_functional_template__$j = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var ReviewsWidget = normalizeComponent(
    { render: __vue_render__$j, staticRenderFns: __vue_staticRenderFns__$j },
    __vue_inject_styles__$j,
    __vue_script__$j,
    __vue_scope_id__$j,
    __vue_is_functional_template__$j,
    __vue_module_identifier__$j,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$k = {
  props: {
    question: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    }
  },
  components: {
    Avatar,
    HelpfulVoting
  },
  methods: {
    formatDate(date) {
      return OkendoUtils.getFormattedDate(date);
    }

  }
};

/* script */
const __vue_script__$k = script$k;

/* template */
var __vue_render__$k = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.question)?_c('div',[_c('div',{staticClass:"oke-qw-question"},[_c('div',{staticClass:"oke-qw-question-avatar"},[_vm._m(0)],1),_vm._v(" "),_c('div',{staticClass:"oke-qw-question-main"},[_vm._m(1),_vm._v(" "),_vm._m(2),_vm._v(" "),_vm._m(3),_vm._v(" "),_c('div',{staticClass:"oke-qw-question-main-helpful"},[_c('helpful-voting',{attrs:{"itemId":_vm.question.questionId,"helpfulCount":_vm.question.helpfulCount,"unhelpfulCount":_vm.question.unhelpfulCount,"contentType":"question"}})],1)])]),_vm._v(" "),_vm._l((_vm.question.answers),function(answer){return _c('div',{key:answer.answerId,staticClass:"oke-qw-answer"},[_c('div',{staticClass:"oke-qw-answer-avatar"},[_vm._o(_c('avatar',{attrs:{"avatarUrl":answer.author.avatarUrl,"displayName":answer.author.displayName,"width":"40px"}}),0,answer.answerId)],1),_vm._v(" "),_c('div',{staticClass:"oke-qw-answer-main"},[_vm._o(_c('div',{staticClass:"oke-qw-answer-main-author"},[_vm._v("\n                "+_vm._s(answer.author.displayName)+"\n            ")]),1,answer.answerId),_vm._v(" "),_vm._o(_c('div',{staticClass:"oke-qw-answer-main-date"},[_vm._v("\n                "+_vm._s(_vm.formatDate(answer.dateCreated))+"\n            ")]),2,answer.answerId),_vm._v(" "),_vm._o(_c('div',{staticClass:"oke-qw-answer-main-body",domProps:{"innerHTML":_vm._s(answer.body)}}),3,answer.answerId),_vm._v(" "),_c('div',{staticClass:"oke-qw-answer-main-helpful"},[_c('helpful-voting',{attrs:{"itemId":answer.answerId,"parentId":_vm.question.questionId,"helpfulCount":answer.helpfulCount,"unhelpfulCount":answer.unhelpfulCount,"contentType":"answer"}})],1)])])})],2):_vm._e()};
var __vue_staticRenderFns__$k = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('avatar',{attrs:{"avatarUrl":_vm.question.author.avatarUrl,"displayName":_vm.question.author.displayName,"width":"40px"}})},function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-qw-question-main-author"},[_vm._v("\n                "+_vm._s(_vm.question.author.displayName)+"\n            ")])},function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-qw-question-main-date"},[_vm._v("\n                "+_vm._s(_vm.formatDate(_vm.question.dateCreated))+"\n            ")])},function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-qw-question-main-body"},[_vm._v("\n                "+_vm._s(_vm.question.body)+"\n            ")])}];

  /* style */
  const __vue_inject_styles__$k = function (inject) {
    if (!inject) return
    inject("data-v-54522434_0", { source: ".oke-qw-answer,.oke-qw-question{display:flex;flex-flow:row nowrap;justify-content:stretch}.oke-qw-answer-main,.oke-qw-question-main{flex:1 1 0}.oke-qw-answer-main-author,.oke-qw-answer-main-date,.oke-qw-question-main-author,.oke-qw-question-main-date{margin-bottom:4px}.oke-qw-answer-main-author,.oke-qw-question-main-author{font-weight:700;font-size:12px;float:left}.oke-qw-answer-main-body,.oke-qw-question-main-body{clear:both;margin-bottom:4px}.oke-qw-answer-main-date,.oke-qw-question-main-date{font-size:12px;color:#7c7c7c;float:right}.oke-qw-question{margin-bottom:24px}.oke-qw-question-main{padding-right:20px}.oke-qw-question-main-body{font-weight:700}.oke-qw-answer{margin-left:56px;background-color:#f5f5f5;padding:20px;border-radius:8px;border-radius:12px}.oke-is-small .oke-qw-answer{margin-left:0}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$k = undefined;
  /* module identifier */
  const __vue_module_identifier__$k = undefined;
  /* functional template */
  const __vue_is_functional_template__$k = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var Question = normalizeComponent(
    { render: __vue_render__$k, staticRenderFns: __vue_staticRenderFns__$k },
    __vue_inject_styles__$k,
    __vue_script__$k,
    __vue_scope_id__$k,
    __vue_is_functional_template__$k,
    __vue_module_identifier__$k,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$l = {
  props: {
    questions: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    }
  },
  components: {
    Question
  }
};

/* script */
const __vue_script__$l = script$l;

/* template */
var __vue_render__$l = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.questions.length)?_c('ul',{staticClass:"oke-qw-questionList",class:{ 'oke-is-loading': _vm.isLoading }},_vm._l((_vm.questions),function(question,index){return _c('li',{key:question.questionId,staticClass:"oke-qw-questionList-item"},[_c('question',{attrs:{"question":question,"index":index}})],1)}),0):_vm._e()};
var __vue_staticRenderFns__$l = [];

  /* style */
  const __vue_inject_styles__$l = function (inject) {
    if (!inject) return
    inject("data-v-4fa71ddf_0", { source: ".oke-qw-questionList{transition:.2s opacity ease-out;position:relative}.oke-qw-questionList-item{list-style-type:none;margin-bottom:24px;padding-bottom:24px;border-bottom:2px solid #f5f5f5}.oke-qw-questionList-item:last-child{margin-bottom:0;border-bottom:0}.oke-qw-questionList.oke-is-loading{opacity:.3}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$l = undefined;
  /* module identifier */
  const __vue_module_identifier__$l = undefined;
  /* functional template */
  const __vue_is_functional_template__$l = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var QuestionsList = normalizeComponent(
    { render: __vue_render__$l, staticRenderFns: __vue_staticRenderFns__$l },
    __vue_inject_styles__$l,
    __vue_script__$l,
    __vue_scope_id__$l,
    __vue_is_functional_template__$l,
    __vue_module_identifier__$l,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$m = {
  props: {
    value: {
      type: String,
      required: false,
      default: 'date desc'
    },
    contentType: {
      type: 'reviews' | 'questions',
      required: false,
      default: 'reviews'
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  methods: {
    onSortChange(event) {
      this.$emit('input', event.target.value);
    }

  }
};

/* script */
const __vue_script__$m = script$m;

/* template */
var __vue_render__$m = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-sortSelect"},[_c('label',{staticClass:"oke-sortSelect-label",attrs:{"for":"oke-sortSelect-sort"}},[_vm._v("\n        Sort\n    ")]),_vm._v(" "),_c('select',{staticClass:"oke-sortSelect-sort oke-select",attrs:{"disabled":_vm.disabled,"id":"oke-sortSelect-sort"},domProps:{"value":_vm.value},on:{"change":function($event){return _vm.onSortChange($event)}}},[_c('option',{attrs:{"value":"date desc"}},[_vm._v(_vm._s('Most Recent'))]),_vm._v(" "),_c('option',{attrs:{"value":"date asc"}},[_vm._v(_vm._s('Oldest'))]),_vm._v(" "),_c('option',{attrs:{"value":"helpful desc"}},[_vm._v(_vm._s('Most Helpful'))]),_vm._v(" "),(_vm.contentType === 'reviews')?[_c('option',{attrs:{"value":"has_media desc"}},[_vm._v(_vm._s('Photos & Videos'))]),_vm._v(" "),_c('option',{attrs:{"value":"rating desc"}},[_vm._v(_vm._s('Highest Rating'))]),_vm._v(" "),_c('option',{attrs:{"value":"rating asc"}},[_vm._v(_vm._s('Lowest Rating'))]),_vm._v(" "),_c('option',{attrs:{"value":"unhelpful desc"}},[_vm._v(_vm._s('Least Helpful'))])]:_vm._e()],2)])};
var __vue_staticRenderFns__$m = [];

  /* style */
  const __vue_inject_styles__$m = function (inject) {
    if (!inject) return
    inject("data-v-5a66bf07_0", { source: ".oke-sortSelect-label{font-weight:600;margin-right:16px}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$m = undefined;
  /* module identifier */
  const __vue_module_identifier__$m = undefined;
  /* functional template */
  const __vue_is_functional_template__$m = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var SortSelect = normalizeComponent(
    { render: __vue_render__$m, staticRenderFns: __vue_staticRenderFns__$m },
    __vue_inject_styles__$m,
    __vue_script__$m,
    __vue_scope_id__$m,
    __vue_is_functional_template__$m,
    __vue_module_identifier__$m,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
var script$n = {
  props: {
    size: {
      type: 'small' | 'medium' | 'large',
      required: false,
      default: 'small'
    }
  }
};

/* script */
const __vue_script__$n = script$n;

/* template */
var __vue_render__$n = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('a',{staticClass:"okeReviews-poweredBy",attrs:{"href":"https://www.okendo.io","target":"_blank","aria-label":"Powered by Okendo"}})};
var __vue_staticRenderFns__$n = [];

  /* style */
  const __vue_inject_styles__$n = function (inject) {
    if (!inject) return
    inject("data-v-cdeb54e6_0", { source: ".oke-poweredBy{display:block;transition:.2s opacity ease-out;opacity:.4}.oke-poweredBy:focus,.oke-poweredBy:hover{outline:0;opacity:.6}.oke-poweredBy-image{display:block}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$n = undefined;
  /* module identifier */
  const __vue_module_identifier__$n = undefined;
  /* functional template */
  const __vue_is_functional_template__$n = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var PoweredBy = normalizeComponent(
    { render: __vue_render__$n, staticRenderFns: __vue_staticRenderFns__$n },
    __vue_inject_styles__$n,
    __vue_script__$n,
    __vue_scope_id__$n,
    __vue_is_functional_template__$n,
    __vue_module_identifier__$n,
    false,
    createInjector,
    undefined,
    undefined
  );

var script$o = {
    props: {
        productId: {
            type: String,
            required: false
        }
    },
    computed: {
        askQuestionUrl() {
            return OkendoAPI.askQuestionUrl(this.productId);
        }
    }
};

/* script */
const __vue_script__$o = script$o;

/* template */
var __vue_render__$o = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return (_vm.productId)?_vm._m(0):_vm._e()};
var __vue_staticRenderFns__$o = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('a',{staticClass:"oke-qw-askQuestion oke-button",attrs:{"href":_vm.askQuestionUrl,"target":"_blank","rel":"noopener noreferrer"}},[_vm._v("\n    Ask a Question\n")])}];

  /* style */
  const __vue_inject_styles__$o = undefined;
  /* scoped */
  const __vue_scope_id__$o = undefined;
  /* module identifier */
  const __vue_module_identifier__$o = undefined;
  /* functional template */
  const __vue_is_functional_template__$o = false;
  /* style inject */
  
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var AskQuestionButton = normalizeComponent(
    { render: __vue_render__$o, staticRenderFns: __vue_staticRenderFns__$o },
    __vue_inject_styles__$o,
    __vue_script__$o,
    __vue_scope_id__$o,
    __vue_is_functional_template__$o,
    __vue_module_identifier__$o,
    false,
    undefined,
    undefined,
    undefined
  );

//
var script$p = {
  props: {
    initialSortOption: {
      type: String,
      required: false,
      default: 'date desc'
    },
    widgetSize: {
      type: 'small' | 'medium' | 'large',
      default: 'small',
      required: false
    },
    sortDisabled: {
      type: Boolean,
      default: false,
      required: false
    },
    productId: {
      type: String,
      required: true
    }
  },
  components: {
    AskQuestionButton,
    PoweredBy,
    SortSelect
  },

  data() {
    return {
      selectedSortOption: 'date desc'
    };
  },

  mounted() {
    this.selectedSortOption = this.initialSortOption;
  },

  methods: {
    onSortChange(sortOption) {
      this.$emit('sortChange', sortOption);
    }

  }
};

/* script */
const __vue_script__$p = script$p;

/* template */
var __vue_render__$p = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-qw-header"},[_c('div',{staticClass:"oke-qw-header-poweredBy"},[_c('powered-by',{attrs:{"size":_vm.widgetSize}})],1),_vm._v(" "),_c('div',{staticClass:"oke-qw-header-sort"},[_c('sort-select',{attrs:{"disabled":_vm.sortDisabled,"contentType":"questions"},on:{"input":function($event){return _vm.onSortChange($event)}},model:{value:(_vm.selectedSortOption),callback:function ($$v) {_vm.selectedSortOption=$$v;},expression:"selectedSortOption"}})],1),_vm._v(" "),_c('div',{staticClass:"oke-qw-header-askQuestion"},[_c('ask-question-button',{attrs:{"productId":_vm.productId}})],1)])};
var __vue_staticRenderFns__$p = [];

  /* style */
  const __vue_inject_styles__$p = function (inject) {
    if (!inject) return
    inject("data-v-fcc8dbac_0", { source: ".oke-qw-header{display:flex;align-items:center;padding-bottom:12px}.oke-qw-header-sort{margin-left:auto;margin-right:24px}.oke-is-small .oke-qw-header{flex-flow:row wrap;justify-content:space-between}.oke-is-small .oke-qw-header-askQuestion{flex-basis:100%}.oke-is-small .oke-qw-header-askQuestion .oke-qw-askQuestion{width:100%;margin-top:8px}.oke-is-small .oke-qw-header-sort{margin:0}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$p = undefined;
  /* module identifier */
  const __vue_module_identifier__$p = undefined;
  /* functional template */
  const __vue_is_functional_template__$p = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var QuestionsHeader = normalizeComponent(
    { render: __vue_render__$p, staticRenderFns: __vue_staticRenderFns__$p },
    __vue_inject_styles__$p,
    __vue_script__$p,
    __vue_scope_id__$p,
    __vue_is_functional_template__$p,
    __vue_module_identifier__$p,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$q = {
  props: {
    isLoading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    onClick(event) {
      this.$emit('click', event.target.value);
    }

  }
};

/* script */
const __vue_script__$q = script$q;

/* template */
var __vue_render__$q = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-showMore oke-is-loading"},[_c('button',{staticClass:"oke-showMore-button oke-button",class:{ 'oke-is-loading': _vm.isLoading },attrs:{"title":_vm.isLoading ? 'Loading more' : '',"type":"button"},on:{"click":function($event){return _vm.onClick($event)}}},[_c('span',{staticClass:"oke-showMore-button-text"},[_vm._v("Show More")])])])};
var __vue_staticRenderFns__$q = [];

  /* style */
  const __vue_inject_styles__$q = function (inject) {
    if (!inject) return
    inject("data-v-49a8f226_0", { source: ".oke-showMore{text-align:center}.oke-showMore-button{border-top:0!important}.oke-showMore-button.oke-is-loading .oke-showMore-button-text{opacity:0}.oke-showMore-button-text{font-family:inherit}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$q = undefined;
  /* module identifier */
  const __vue_module_identifier__$q = undefined;
  /* functional template */
  const __vue_is_functional_template__$q = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var ShowMore = normalizeComponent(
    { render: __vue_render__$q, staticRenderFns: __vue_staticRenderFns__$q },
    __vue_inject_styles__$q,
    __vue_script__$q,
    __vue_scope_id__$q,
    __vue_is_functional_template__$q,
    __vue_module_identifier__$q,
    false,
    createInjector,
    undefined,
    undefined
  );

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$r = {
  props: {
    position: {
      type: 'top' | 'bottom',
      required: true
    }
  },

  data() {
    return {
      isVisible: false,
      message: 'Loading...'
    };
  },

  methods: {
    show(message, timeout = 0) {
      this.isVisible = true;
      this.message = message;

      if (timeout) {
        setTimeout(() => {
          this.isVisible = false;
        }, timeout);
      }
    },

    hide() {
      this.isVisible = false;
    }

  }
};

/* script */
const __vue_script__$r = script$r;

/* template */
var __vue_render__$r = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-alert",class:{
        'oke-is-visible': _vm.isVisible && _vm.message.length,
        'oke-alert--top': _vm.position === 'top',
        'oke-alert--bottom': _vm.position === 'bottom'
    },attrs:{"aria-hidden":!_vm.isVisible,"role":"alert","aria-live":"assertive","aria-atomic":"true"}},[_c('div',{staticClass:"oke-alert-inner"},[_vm._v("\n        "+_vm._s(_vm.message)+"\n    ")])])};
var __vue_staticRenderFns__$r = [];

  /* style */
  const __vue_inject_styles__$r = function (inject) {
    if (!inject) return
    inject("data-v-f5a3c448_0", { source: ".oke-alert{position:absolute;overflow:hidden;padding:12px 0;left:0;right:0;margin-left:auto;margin-right:auto;text-align:center}.oke-alert-inner{display:inline-block;transition:transform .2s ease-out;background-color:#fff;padding:8px 24px;box-shadow:0 0 5px 0 rgba(0,0,0,.15)}.oke-alert--top{top:0}.oke-alert--top .oke-alert-inner{top:12px;transform:translateY(-150%)}.oke-alert--bottom{bottom:0}.oke-alert--bottom .oke-alert-inner{bottom:12px;transform:translateY(150%)}.oke-alert.oke-is-visible.oke-alert--bottom .oke-alert-inner,.oke-alert.oke-is-visible.oke-alert--top .oke-alert-inner{top:auto;transform:translateY(0)}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$r = undefined;
  /* module identifier */
  const __vue_module_identifier__$r = undefined;
  /* functional template */
  const __vue_is_functional_template__$r = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var Alert = normalizeComponent(
    { render: __vue_render__$r, staticRenderFns: __vue_staticRenderFns__$r },
    __vue_inject_styles__$r,
    __vue_script__$r,
    __vue_scope_id__$r,
    __vue_is_functional_template__$r,
    __vue_module_identifier__$r,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$s = {
  props: {
    productId: {
      type: String,
      required: true
    },
    sizeOverride: {
      type: String
    }
  },
  components: {
    Alert,
    AskQuestionButton,
    QuestionsHeader,
    QuestionsList,
    ShowMore
  },

  data() {
    return {
      viewState: 'loading',
      size: 'small',
      questions: [],
      isLoadingMore: false,
      selectedSortOption: 'date desc',
      nextUrl: '',
      loadInitialLimit: 5,
      shopifyProductId: ''
    };
  },

  mounted() {
    this.shopifyProductId = OkendoAPI.transformProductId(this.productId);

    if (!this.sizeOverride) {
      this.addResizeListener();
    }

    this.loadQuestions(this.loadInitialLimit, this.selectedSortOption);
  },

  destroyed() {
    this.removeResizeListener();
  },

  computed: {
    widgetSize() {
      return this.sizeOverride || this.size;
    }

  },
  methods: {
    loadQuestions(limit, sort) {
      if (this.shopifyProductId) {
        const topAlert = this.$refs.topAlert;

        if (topAlert) {
          topAlert.show('Loading Questions...');
        }

        OkendoAPI.getQuestionsProduct(this.shopifyProductId, limit, sort).then(({
          data
        }) => {
          this.questions = data.questions;
          this.nextUrl = data.nextUrl;
          this.viewState = this.questions.length ? 'content' : 'empty';

          if (topAlert) {
            topAlert.hide();
          }
        }).catch(() => {
          this.viewState = 'error';

          if (topAlert) {
            topAlert.hide();
          }
        });
      }
    },

    showMoreQuestions() {
      if (this.nextUrl) {
        this.isLoadingMore = true;
        OkendoAPI.getMore(this.nextUrl).then(({
          data
        }) => {
          this.questions = [...this.questions, ...data.questions];
          this.nextUrl = data.nextUrl;
          this.isLoadingMore = false;
        }).catch(() => {
          this.$refs.bottomAlert.show('Unexpected error occurred, please try again', 2000);
          this.isLoadingMore = false;
        });
      }
    },

    onSortChange(newSort) {
      this.selectedSortOption = newSort;
      this.loadQuestions(this.loadInitialLimit, this.selectedSortOption);
    },

    addResizeListener() {
      this.setWidgetSize();
      window.addEventListener('resize', this.setWidgetSize, {
        passive: true
      });
    },

    removeResizeListener() {
      window.removeEventListener('resize', this.setWidgetSize);
    },

    setWidgetSize() {
      const widgetWidth = this.$refs.questionsWidget && this.$refs.questionsWidget.clientWidth;

      if (widgetWidth > 960) {
        this.size = 'large';
      } else if (widgetWidth > 640) {
        this.size = 'medium';
      } else {
        this.size = 'small';
      }
    }

  }
};

/* script */
const __vue_script__$s = script$s;

/* template */
var __vue_render__$s = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{ref:"questionsWidget",staticClass:"okeReviews",attrs:{"data-oke-reviews-version":""}},[_c('div',{staticClass:"okeReviews-qandaWidget oke-qw",class:{
            'oke-is-small is-okeReviews-qandaWidget-small': _vm.widgetSize === 'small',
            'oke-is-medium is-okeReviews-qandaWidget-medium': _vm.widgetSize === 'medium',
            'oke-is-large is-okeReviews-qandaWidget-large': _vm.widgetSize === 'large'
        }},[(_vm.viewState === 'loading' || _vm.viewState === 'content')?_c('questions-header',{attrs:{"sortDisabled":_vm.viewState !== 'content',"productId":_vm.shopifyProductId,"selectedSortOption":_vm.selectedSortOption,"widgetSize":_vm.widgetSize},on:{"sortChange":function($event){return _vm.onSortChange($event)}}}):_vm._e(),_vm._v(" "),_c('div',{staticClass:"oke-qw-main"},[(_vm.viewState === 'loading' || _vm.viewState === 'content')?_c('questions-list',{attrs:{"isLoading":_vm.viewState === 'loading',"questions":_vm.questions}}):_vm._e(),_vm._v(" "),(_vm.viewState === 'loading')?_c('div',{staticClass:"oke-qw-loading"},[_vm._m(0)]):_vm._e(),_vm._v(" "),(_vm.viewState === 'empty')?_c('div',{staticClass:"oke-qw-empty"},[_c('div',{staticClass:"oke-qw-empty-text"},[_vm._v("\n                    No questions yet, ask one now?\n                ")]),_vm._v(" "),_c('div',{staticClass:"oke-qw-empty-button"},[_c('ask-question-button',{attrs:{"productId":_vm.shopifyProductId}})],1)]):_vm._e(),_vm._v(" "),(_vm.viewState === 'error')?_c('div',{staticClass:"oke-qw-error"},[_c('div',{staticClass:"oke-qw-error-text"},[_vm._v("\n                    Error occurred while loading questions 😣\n                ")]),_vm._v(" "),_c('div',{staticClass:"oke-qw-error-button"},[_c('button',{staticClass:"oke-qw-error-button oke-button",attrs:{"type":"button"},on:{"click":function($event){return _vm.loadQuestions(_vm.loadInitialLimit, _vm.selectedSortOption)}}},[_vm._v("\n                        Retry\n                    ")])])]):_vm._e(),_vm._v(" "),(_vm.questions && _vm.questions.length)?_c('div',{staticClass:"oke-qw-main-alerts"},[_c('alert',{ref:"bottomAlert",attrs:{"position":"bottom"}}),_vm._v(" "),_c('alert',{ref:"topAlert",attrs:{"position":"top"}})],1):_vm._e()],1),_vm._v(" "),_c('div',{staticClass:"oke-qw-footer"},[(_vm.nextUrl && _vm.viewState === 'content')?_c('show-more',{attrs:{"isLoading":_vm.isLoadingMore},on:{"click":function($event){return _vm.showMoreQuestions()}}}):_vm._e()],1)],1)])};
var __vue_staticRenderFns__$s = [function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-qw-loading-text"},[_vm._v("\n                    Loading Questions "),_c('span',{staticClass:"oke-loader"})])}];

  /* style */
  const __vue_inject_styles__$s = function (inject) {
    if (!inject) return
    inject("data-v-ac8492de_0", { source: ".oke-qw{margin:auto;text-align:left}.oke-qw-questionList{padding:0;margin:0}.oke-qw-main{position:relative;padding-top:24px;border-top:2px solid #f5f5f5;border-bottom:2px solid #f5f5f5}.oke-qw-empty,.oke-qw-error,.oke-qw-loading{text-align:center;padding-bottom:24px}.oke-qw-empty-button,.oke-qw-error-button,.oke-qw-loading-button{margin-top:8px}.oke-qw-empty .oke-loader,.oke-qw-error .oke-loader,.oke-qw-loading .oke-loader{margin-left:8px}.oke-qw .oke-button{display:inline-block;transition:.2s background-color ease-out;background-color:#f7f7f8;padding:.5em 24px;margin:0;border:0;border-radius:0;color:inherit;text-align:center;position:relative}.oke-qw .oke-button.oke-is-loading .oke-button-text{opacity:0}.oke-qw .oke-button.oke-is-loading::before{background-image:url(\"data:image/svg+xml,%3Csvg fill='%237c7c7c' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M24 12h-2c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10v2C5.373 24 0 18.627 0 12S5.373 0 12 0s12 5.373 12 12z'/%3E%3C/svg%3E\");background-repeat:no-repeat;display:inline-block;content:\"\";width:12px;height:12px;background-size:12px 12px;animation:oke-spin 1s infinite linear;position:absolute;top:0;bottom:0;left:0;right:0;margin:auto}.oke-qw .oke-button:focus,.oke-qw .oke-button:hover{outline:0}.oke-qw .oke-button:not(.oke-is-loading){cursor:pointer}.oke-qw .oke-button:not(.oke-is-loading):focus,.oke-qw .oke-button:not(.oke-is-loading):hover{background-color:#e5e5eb}.oke-qw .oke-is-small .oke-button{padding-top:12px;padding-bottom:12px}.oke-qw .oke-select{cursor:pointer;background-repeat:no-repeat;background-position-x:100%;background-position-y:50%;border:none;padding:0 24px 0 0;border-radius:4px;appearance:none;color:inherit;background-color:transparent;-webkit-appearance:none;background-image:url(\"data:image/svg+xml,%3Csvg fill='%23000' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3C/svg%3E\");background-repeat:no-repeat}.oke-qw .oke-select:focus{outline:0;text-decoration:underline}.oke-qw .oke-select:disabled{background-color:transparent;background-image:url(\"data:image/svg+xml,%3Csvg fill='%237c7c7c' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M7 10l5 5 5-5z'/%3E%3Cpath d='M0 0h24v24H0z' fill='none'/%3E%3C/svg%3E\");background-repeat:no-repeat}.oke-qw .oke-loader{background-image:url(\"data:image/svg+xml,%3Csvg fill='%237c7c7c' xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M24 12h-2c0-5.523-4.477-10-10-10S2 6.477 2 12s4.477 10 10 10v2C5.373 24 0 18.627 0 12S5.373 0 12 0s12 5.373 12 12z'/%3E%3C/svg%3E\");background-repeat:no-repeat;display:inline-block;content:\"\";width:12px;height:12px;background-size:12px 12px;animation:oke-spin 1s infinite linear;vertical-align:middle;user-select:none}@keyframes oke-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$s = undefined;
  /* module identifier */
  const __vue_module_identifier__$s = undefined;
  /* functional template */
  const __vue_is_functional_template__$s = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var QuestionsWidget = normalizeComponent(
    { render: __vue_render__$s, staticRenderFns: __vue_staticRenderFns__$s },
    __vue_inject_styles__$s,
    __vue_script__$s,
    __vue_scope_id__$s,
    __vue_is_functional_template__$s,
    __vue_module_identifier__$s,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$t = {
  props: {
    activeTab: {
      type: String,
      required: false,
      default: 'reviews'
    },
    productId: {
      type: String,
      required: true
    },
    tabs: {
      type: Array,
      required: true
    }
  },

  data() {
    return {
      questionCount: 0,
      reviewCount: 0
    };
  },

  mounted() {
    const shopifyProductId = OkendoAPI.transformProductId(this.productId);

    if (shopifyProductId) {
      Promise.all([OkendoAPI.getReviewAggregateProduct(shopifyProductId), OkendoAPI.getQuestionAggregateProduct(shopifyProductId)]).then(([reviewsResponse, questionResponse]) => {
        this.reviewCount = reviewsResponse.data.reviewAggregate.reviewCount || 0;
        this.questionCount = questionResponse.data.questionAggregate.approvedQuestionCount || 0;
      });
    }
  },

  methods: {
    onActiveTabChange(newTab) {
      const newTabRefs = this.$refs[newTab];

      if (newTabRefs[0]) {
        newTabRefs[0].focus();
      }

      this.$emit('tabChange', newTab);
    },

    selectFirstTab() {
      const newTab = this.tabs[0];
      this.onActiveTabChange(newTab);
    },

    selectLastTab() {
      const newTab = this.tabs[this.tabs.length - 1];
      this.onActiveTabChange(newTab);
    },

    selectNextTab(index) {
      const nextTab = this.tabs[index + 1];
      const newTab = nextTab || this.tabs[0];
      this.onActiveTabChange(newTab);
    },

    selectPreviousTab(index) {
      const prevTab = this.tabs[index - 1];
      const newTab = prevTab || this.tabs[this.tabs.length - 1];
      this.onActiveTabChange(newTab);
    }

  }
};

/* script */
const __vue_script__$t = script$t;

/* template */
var __vue_render__$t = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"oke-rw-navBar",attrs:{"role":"tablist"}},_vm._l((_vm.tabs),function(tab,index){return _c('button',{key:tab,ref:tab,refInFor:true,staticClass:"oke-rw-navBar-item",attrs:{"aria-selected":_vm.activeTab === tab,"id":(tab + "-tab"),"aria-controls":(tab + "-panel"),"tabindex":_vm.activeTab === tab ? 0 : -1,"role":"tab","type":"button"},on:{"click":function($event){return _vm.onActiveTabChange(tab)},"keyup":[function($event){if(!$event.type.indexOf('key')&&_vm._k($event.keyCode,"right",39,$event.key,["Right","ArrowRight"])){ return null; }if('button' in $event && $event.button !== 2){ return null; }return _vm.selectNextTab(index)},function($event){if(!$event.type.indexOf('key')&&_vm._k($event.keyCode,"left",37,$event.key,["Left","ArrowLeft"])){ return null; }if('button' in $event && $event.button !== 0){ return null; }return _vm.selectPreviousTab(index)},function($event){if(!$event.type.indexOf('key')&&_vm._k($event.keyCode,"home",undefined,$event.key,undefined)){ return null; }return _vm.selectFirstTab()},function($event){if(!$event.type.indexOf('key')&&_vm._k($event.keyCode,"end",undefined,$event.key,undefined)){ return null; }return _vm.selectLastTab()}]}},[_vm._v("\n        "+_vm._s(tab)+"\n        "),(tab === 'reviews' && _vm.reviewCount)?_c('span',{staticClass:"oke-rw-navBar-item-count"},[_vm._v(_vm._s(_vm.reviewCount))]):_vm._e(),_vm._v(" "),(tab === 'questions' && _vm.questionCount)?_c('span',{staticClass:"oke-rw-navBar-item-count"},[_vm._v(_vm._s(_vm.questionCount))]):_vm._e()])}),0)};
var __vue_staticRenderFns__$t = [];

  /* style */
  const __vue_inject_styles__$t = function (inject) {
    if (!inject) return
    inject("data-v-94683dd0_0", { source: ".okeReviews .oke-rw-navBar{margin-bottom:16px;text-align:left}.okeReviews .oke-rw-navBar-item{outline:0;margin-right:24px;padding-bottom:4px;font-weight:600;background:0 0;border:0;color:inherit;cursor:pointer;display:inline-block;height:auto;padding:0;text-transform:capitalize}.okeReviews .oke-rw-navBar-item a{color:inherit}.okeReviews .oke-rw-navBar-item[aria-selected=true]{border-bottom:3px solid #dbdde4}.okeReviews .oke-rw-navBar-item-count{display:inline-block}.okeReviews .oke-rw-navBar-item-count::before{content:\"(\"}.okeReviews .oke-rw-navBar-item-count::after{content:\")\"}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$t = undefined;
  /* module identifier */
  const __vue_module_identifier__$t = undefined;
  /* functional template */
  const __vue_is_functional_template__$t = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var NavBar = normalizeComponent(
    { render: __vue_render__$t, staticRenderFns: __vue_staticRenderFns__$t },
    __vue_inject_styles__$t,
    __vue_script__$t,
    __vue_scope_id__$t,
    __vue_is_functional_template__$t,
    __vue_module_identifier__$t,
    false,
    createInjector,
    undefined,
    undefined
  );

//
var script$u = {
  props: {
    productId: {
      type: String,
      required: true
    }
  },
  components: {
    QuestionsWidget,
    ReviewsWidget,
    NavBar
  },

  data() {
    return {
      activeTab: 'reviews',
      questionsTabInitialised: false,
      tabs: ['reviews', 'questions'],
      widgetSize: 'small'
    };
  },

  mounted() {
    this.addResizeListener();
  },

  destroyed() {
    this.removeResizeListener();
  },

  methods: {
    onTabChange(newTab) {
      if (newTab === 'questions') {
        this.questionsTabInitialised = true;
      }

      this.activeTab = newTab;
    },

    addResizeListener() {
      this.setWidgetSize();
      window.addEventListener('resize', this.setWidgetSize, {
        passive: true
      });
    },

    removeResizeListener() {
      window.removeEventListener('resize', this.setWidgetSize);
    },

    setWidgetSize() {
      const widgetWidth = this.$refs.widget && this.$refs.widget.clientWidth;

      if (widgetWidth > 960) {
        this.widgetSize = 'large';
      } else if (widgetWidth > 640) {
        this.widgetSize = 'medium';
      } else {
        this.widgetSize = 'small';
      }
    }

  }
};

/* script */
const __vue_script__$u = script$u;

/* template */
var __vue_render__$u = function () {var _vm=this;var _h=_vm.$createElement;var _c=_vm._self._c||_h;return _c('div',{staticClass:"okeReviews",attrs:{"data-oke-reviews-version":""}},[_c('div',{ref:"widget",staticClass:"oke-cw"},[_c('nav-bar',{attrs:{"tabs":_vm.tabs,"activeTab":_vm.activeTab,"productId":_vm.productId},on:{"tabChange":function($event){return _vm.onTabChange($event)}}}),_vm._v(" "),_vm._l((_vm.tabs),function(tab){return _c('div',{key:tab},[_c('div',{staticClass:"oke-cw-panel",attrs:{"hidden":_vm.activeTab !== tab,"id":(tab + "-panel"),"aria-labelledby":(tab + "-tab"),"tabindex":"0","role":"tabpanel"}},[(tab === 'reviews')?_c('reviews-widget',{attrs:{"productId":_vm.productId,"sizeOverride":_vm.widgetSize}}):_vm._e(),_vm._v(" "),(_vm.questionsTabInitialised && tab === 'questions')?_c('questions-widget',{attrs:{"productId":_vm.productId,"sizeOverride":_vm.widgetSize}}):_vm._e()],1)])})],2)])};
var __vue_staticRenderFns__$u = [];

  /* style */
  const __vue_inject_styles__$u = function (inject) {
    if (!inject) return
    inject("data-v-3afe38aa_0", { source: ".oke-cw-panel{outline:0}.oke-cw-panel[hidden]{display:none}", map: undefined, media: undefined });

  };
  /* scoped */
  const __vue_scope_id__$u = undefined;
  /* module identifier */
  const __vue_module_identifier__$u = undefined;
  /* functional template */
  const __vue_is_functional_template__$u = false;
  /* style inject SSR */
  
  /* style inject shadow dom */
  

  
  var OkendoCombinedWidget = normalizeComponent(
    { render: __vue_render__$u, staticRenderFns: __vue_staticRenderFns__$u },
    __vue_inject_styles__$u,
    __vue_script__$u,
    __vue_scope_id__$u,
    __vue_is_functional_template__$u,
    __vue_module_identifier__$u,
    false,
    createInjector,
    undefined,
    undefined
  );



var components = /*#__PURE__*/Object.freeze({
    __proto__: null,
    OkendoStarRating: OkendoStarRating,
    OkendoReviewsWidget: ReviewsWidget,
    OkendoQuestionsWidget: QuestionsWidget,
    OkendoCombinedWidget: OkendoCombinedWidget
});

// Generated by: https://www.npmjs.com/package/vue-sfc-rollup

function install(Vue) {
  if (install.installed) return;
  install.installed = true;
  Object.keys(components).forEach(componentName => {
    Vue.component(componentName, components[componentName]);
  });
} // Create module definition for Vue.use()


const plugin = {
  install
}; // To auto-install when vue is found

/* global window global */

let GlobalVue = null;

if (typeof window !== 'undefined') {
  GlobalVue = window.Vue;
} else if (typeof global !== 'undefined') {
  GlobalVue = global.Vue;
}

if (GlobalVue) {
  GlobalVue.use(plugin);
} // Default export is library as a whole, registered via Vue.use()
function configureOkendo(config) {
  if (config.subscriberId) {
    OkendoAPI.setSubscriberId(config.subscriberId);
  }

  if (config.widgetSettings) {
    setWidgetSettings(config.widgetSettings);
  }
}

export default plugin;
export { OkendoCombinedWidget, QuestionsWidget as OkendoQuestionsWidget, ReviewsWidget as OkendoReviewsWidget, OkendoStarRating, configureOkendo };
